import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, AlertCircle, Search, FileText, Filter, DollarSign, CheckCircle, Download, Printer, Eye, Upload, Wrench, Film } from 'lucide-react';
import API, { getTagihan, getTagihanStats, getTagihanById, generateTagihanFromCM, createTagihan, updateTagihan, deleteTagihan, getCM, getCMById, getVehicles, getInventoriSpareparts, getJasaLayanan, getPublicSettings } from '../api';
import { useAuth } from '../AuthContext';
import MultiSelectFilter from '../components/MultiSelectFilter';

// Deteksi apakah URL berisi video (data:video base64 dari mobile-web, atau file .mp4 dll)
const isVideoURL = (u) => u && (u.startsWith('data:video') || /\.(mp4|webm|mov|m4v|avi)(\?|#|$)/i.test(u));

// Proses TTD jadi hitam pekat & tebal (sama persis seperti cetak CM) — jsPDF tidak dukung CSS filter,
// jadi proses via canvas lalu hasilnya dikonversi ke data URL PNG
const boldenSignature = (src) => new Promise((resolve) => {
  try {
    const img = new Image();
    img.onload = () => {
      try {
        const pad = 2; // padding biar efek tebal (drop-shadow) tidak terpotong di tepi
        const c = document.createElement('canvas');
        c.width = img.width + pad * 2;
        c.height = img.height + pad * 2;
        const ctx = c.getContext('2d');
        if (ctx && typeof ctx.filter === 'string') {
          ctx.filter = 'brightness(0) contrast(1.2) drop-shadow(0.6px 0 0 rgba(0,0,0,0.9)) drop-shadow(-0.6px 0 0 rgba(0,0,0,0.9)) drop-shadow(0 0.6px 0 rgba(0,0,0,0.9)) drop-shadow(0 -0.6px 0 rgba(0,0,0,0.9))';
          ctx.drawImage(img, pad, pad);
          resolve(c.toDataURL('image/png'));
          return;
        }
      } catch(e) { /* fallback ke gambar asli */ }
      resolve(src);
    };
    img.onerror = () => resolve(src);
    img.src = src;
  } catch(e) {
    resolve(src);
  }
});

const STATUS_OPTIONS = ['Pengajuan','Disetujui','Ditolak','Verifikasi','Selesai','Belum Dibayar','Sudah Ditagih','Lunas','Dibatalkan'];
const METODE_PEMBAYARAN = [
  { value: 'Tunai', label: 'Tunai', icon: '💵', description: 'Pembayaran secara langsung (cash)' },
  { value: 'Transfer', label: 'Transfer', icon: '🏦', description: 'Transfer bank (BCA, Mandiri, BRI, dll)' },
  { value: 'Debit', label: 'Debit', icon: '💳', description: 'Kartu debit / ATM' },
  { value: 'QRIS', label: 'QRIS', icon: '📱', description: 'Scan QRIS (GoPay, OVO, dll)' },
];
const STATUS_COLORS = {
  'Belum Dibayar': { bg: '#fef3c7', color: '#92400e' },
  'Sudah Ditagih': { bg: '#dbeafe', color: '#1e40af' },
  'Lunas': { bg: '#d1fae5', color: '#065f46' },
  'Dibatalkan': { bg: '#fee2e2', color: '#991b1b' },  'Pengajuan': { bg: '#fff5ee', color: '#c2410c' },'Disetujui': { bg: '#eef2ff', color: '#3730a3' },'Ditolak': { bg: '#fef2f2', color: '#991b1b' },'Verifikasi': { bg: '#f0fdf4', color: '#065f46' },'Selesai': { bg: '#d1fae5', color: '#065f46' },
};

const emptyForm = {
  cm_id: '',
  vehicle_id: '',
  no_body: '',
  no_polisi: '',
  nama_klien: '',
  jenis_pekerjaan: '',
  detail_pekerjaan: '',
  tipe_pekerjaan: '',
  jenis_kendaraan: '',
  sparepart_komponen: '',
  nominal_tagihan: '',
  biaya_lain: 0,
  ppn: 0,
  owner_name: '',
  owner_nik: '',
  owner_address: '',
  status: 'Pengajuan',
  tanggal_tagih: new Date().toISOString().slice(0, 10),
  tanggal_jatuh_tempo: new Date(Date.now() + 17 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  tanggal_bayar: '',
  catatan: '',
  invoice_no: '',
  no_kontrak: '',
  lokasi: '',
  ketua_koperasi: '',
  tanggal_pengerjaan: new Date().toISOString().slice(0, 10),
  signature_url: '',
};


// ====== Workflow Tabs (Pengajuan -> Verifikasi -> Selesai) ======
const WORKFLOW_TABS = [
  { key: 'semua', label: 'Semua Tagihan', icon: FileText, color: '#5e72e4' },
  { key: 'pengajuan', label: 'Pengajuan Tagihan', icon: FileText, color: '#fb6340' },
  { key: 'ditolak', label: 'Ditolak', icon: X, color: '#991b1b' },
  { key: 'verifikasi', label: 'Verifikasi Tagihan', icon: CheckCircle, color: '#11cdef' },
  { key: 'selesai', label: 'Tagihan Selesai', icon: CheckCircle, color: '#2dce89' },
];

export default function Tagihan() {
  const { user, hasMenuAction } = useAuth();

  // Helper functions untuk tanggal default (dideklarasikan SEBELUM digunakan)
  const getDefaultTglMulai = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
  const getDefaultTglSelesai = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); };

  const [data, setData] = useState([]);
  const [, setStats] = useState(null);
  const [cmList, setCmList] = useState([]);
  const [cmViewItem, setCmViewItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterNoPolisi, setFilterNoPolisi] = useState('');
  const [filterNoBody, setFilterNoBody] = useState('');

  const [filterTglMulai, setFilterTglMulai] = useState(getDefaultTglMulai());
  const [filterTglSelesai, setFilterTglSelesai] = useState(getDefaultTglSelesai());
  const [filterJenisKendaraan, setFilterJenisKendaraan] = useState('');
  const [filterNoKontrak, setFilterNoKontrak] = useState('');
  const [filterNamaKlien, setFilterNamaKlien] = useState([]);
  const [filterTeknisi, setFilterTeknisi] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [isPM, setIsPM] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedCM, setSelectedCM] = useState('');
  const [cmSearchText, setCmSearchText] = useState('');
  const [cmFilterBy, setCmFilterBy] = useState('all'); // 'all', 'cm_id', 'no_body', 'teknisi'
  const [cmTglMulai, setCmTglMulai] = useState(getDefaultTglMulai());
  const [cmTglSelesai, setCmTglSelesai] = useState(getDefaultTglSelesai());
  const [generateInfo, setGenerateInfo] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const findCMById = (cmId) => cmList.find(c => String(c.id) === String(cmId));
  // Fetch detail CM lengkap (termasuk foto_urls/video_urls yang TIDAK ada di listCM)
  // agar foto hasil upload mobile-web tetap muncul di modal "Lihat CM".
  const handleViewCM = useCallback(async (cmId) => {
    const fallback = findCMById(cmId);
    setCmViewItem(fallback || { id: cmId });
    try {
      const res = await getCMById(cmId);
      const full = res?.data;
      if (full) setCmViewItem(prev => prev && String(prev.id) === String(cmId) ? { ...prev, ...full } : prev);
    } catch (e) {
      console.warn('Gagal fetch detail CM di Tagihan:', e);
    }
  }, [cmList]);

  // Cetak CM Selesai langsung dari modal "Lihat CM" di menu Tagihan
  // (template cetak identik dengan menu CM: header perusahaan, semua section, TTD + stempel default)
  const handleCetakCM = async () => {
    const base = cmViewItem || {};
    let item = base;
    try {
      // Ambil detail lengkap agar data cetak selalu fresh & lengkap (foto, TTD, sparepart, dll)
      const res = await getCMById(base.id || base.cm_id);
      if (res?.data) item = { ...base, ...res.data };
    } catch (e) {
      console.warn('Gagal fetch detail CM untuk cetak:', e);
    }
    // Default TTD & stempel perusahaan dari app_settings (untuk bagian VERIFIKASI & PENETAPAN TAGIHAN)
    let printDefaults = { ttd: '', stempel: '' };
    try {
      const sRes = await getPublicSettings();
      printDefaults = {
        ttd: sRes?.data?.ttd_direktur || '',
        stempel: sRes?.data?.stempel_perusahaan || '',
      };
    } catch (e) { /* biarkan kosong */ }

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) { alert('Popup diblokir. Izinkan popup untuk mencetak.'); return; }

    const sekarang = new Date();
    const fmtDate2 = (d) => d ? new Date(d).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' }) : '-';
    const fmtDateTime2 = (d) => d ? new Date(d).toLocaleString('id-ID') : '-';
    const renderSignature = (val, fallback = '______________') => {
      if (!val) return fallback;
      if (typeof val === 'string' && val.startsWith('data:')) {
        return `<img src="${val}" style="max-height:35px;max-width:120px;vertical-align:middle;border:1px solid #ddd;border-radius:3px;padding:1px;background:#fff;" alt="TTD" />`;
      }
      return val;
    };

    const totalSP = (item.sparepart_digunakan || []).reduce((s, sp) => s + (sp.jumlah || 0) * (sp.harga || 0), 0);
    const totalJS = (item.jasa_digunakan || []).reduce((s, js) => s + (js.jumlah || 0) * (js.harga || 0), 0);
    const grandTotal = totalSP + totalJS;

    printWindow.document.write(`<!DOCTYPE html><html><head><title>Cetak CM - ${item.cm_id}</title>
<style>
  @page { margin: 1cm 0.8cm; }
  body { font-family: 'Times New Roman', Times, serif; font-size: 10pt; color: #000; line-height: 1.25; margin: 0; padding: 0; }
  .no-print { text-align: center; margin-bottom: 6px; }
  .no-print button { padding: 6px 16px; background: #1a1a2e; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 11px; font-family: sans-serif; }
  .header-table { width: 100%; border-collapse: collapse; margin-bottom: 2px; }
  .header-table td { vertical-align: top; padding: 0; }
  .header-left { text-align: left; width: 45%; }
  .header-left .logo-img { max-width: 120px; max-height: 38px; margin-bottom: 3px; }
  .header-left .title-print { font-size: 10pt; font-weight: 700; margin: 1px 0; text-transform: uppercase; }
  .header-left .subtitle-print { font-size: 7pt; color: #555; font-style: italic; margin: 0; }
  .header-left .cm-id-print { font-size: 8pt; font-weight: 600; margin: 2px 0 0; }
  .header-right { text-align: right; width: 55%; font-size: 6.5pt; line-height: 1.2; }
  .header-right .company-name { font-size: 7.5pt; font-weight: 700; margin: 0 0 2px; }
  .header-right .address-line { margin: 0; }
  .header-right .address-label { font-weight: 600; }
  .header-separator { border: none; border-top: 2px double #000; margin: 3px 0 4px; }
  .section-box { border: 1.5px solid #1a1a2e; margin-bottom: 4px; }
  .section-header { background: #1a1a2e; color: #fff; padding: 2px 8px; font-weight: 700; font-size: 8pt; letter-spacing: 0.5px; }
  .section-body { padding: 3px 8px; }
  table.info-grid { width: 100%; border-collapse: collapse; font-size: 8.5pt; }
  table.info-grid td { padding: 1px 4px; vertical-align: top; }
  table.info-grid td.label { font-weight: 700; width: 90px; }
  table.info-grid td.val { border-bottom: 1px solid #ccc; padding: 0 3px 1px; }
  .radio-item { font-size: 8.5pt; padding: 0; }
  .radio-checked { font-weight: 700; color: #1a1a2e; }
  .divider-dashed { border-top: 1px dashed #999; margin: 4px 0; }
  table.items { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 3px; }
  table.items th { border: 1px solid #000; padding: 2px 4px; text-align: center; font-weight: 700; background: #f0f0f0; font-size: 7.5pt; }
  table.items td { border: 1px solid #000; padding: 1px 3px; vertical-align: middle; }
  table.items td.price { text-align: right; }
  table.items td.center { text-align: center; }
  /* Foto dokumentasi — 2 kolom × 2 baris per halaman (1 foto ≈ 1/4 halaman).
     Section-nya dibiarkan boleh pecah antar-halaman supaya foto bisa besar. */
  .foto-print-section { page-break-inside: auto; }
  .foto-print-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; align-items: start; }
  .foto-print-item { text-align: center; page-break-inside: avoid; break-inside: avoid; }
  .foto-print-img { width: 100%; max-width: 100%; max-height: 118mm; object-fit: contain; border: 1px solid #ccc; border-radius: 3px; background: #fafafa; }
  .foto-print-caption { font-size: 8pt; color: #555; margin-top: 2px; }
  .statement { font-style: italic; font-size: 8pt; padding: 3px 8px; background: #fef9ef; border: 1px solid #fde68a; border-left: 3px solid #d97706; margin: 3px 0; }
  .ttd-section { display: flex; justify-content: space-between; margin-top: 8px; padding: 0 8px; }
  .ttd-box { text-align: center; width: 160px; }
  .ttd-box .ttd-line { margin-top: 20px; border-top: 1px solid #000; padding-top: 2px; font-size: 8pt; font-weight: 600; }
  .footer { text-align: center; font-size: 6.5pt; color: #888; margin-top: 6px; border-top: 1px solid #ccc; padding-top: 2px; }
  table.log-table { width: 100%; border-collapse: collapse; margin-top: 3px; font-size: 7.5pt; }
  table.log-table th { border: 1px solid #000; padding: 1px 3px; text-align: center; font-weight: 700; background: #f0f0f0; font-size: 7pt; }
  table.log-table td { border: 1px solid #000; padding: 1px 3px; vertical-align: middle; }
  .tagihan-box { text-align: right; font-size: 9pt; font-weight: 700; padding: 2px 6px; background: #f0fdf4; border: 1px solid #b8e6d0; margin: 3px 0; }
  @media print { .no-print { display: none !important; } }
</style></head><body>
<div class="no-print"><button onclick="window.print()">🖨 Cetak / Simpan PDF</button></div>

<table class="header-table">
  <tr>
    <td class="header-left">
      <img src="${window.location.origin}/logo-synexa.png" alt="Synexa Logo" class="logo-img" onerror="this.style.display='none'" />
      <div class="title-print">LAPORAN LAYANAN ARMADA</div>
      <div class="subtitle-print">Fleet Service Report</div>
      <div class="cm-id-print">No.CM : ${item.cm_id || '#' + item.id}</div>
    </td>
    <td class="header-right">
      <div class="company-name">PT. Synexa Global Tekno</div>
      <div class="address-line"><span class="address-label">Tangerang :</span> Ruko Silktown Avenue Blok RK/5A-05</div>
      <div class="address-line">Jl. Boulevard Silk Town, Kel. Pondok Jagung Timur, Kec. Serpong Utara,</div>
      <div class="address-line">Kota Tangerang Selatan - Banten 15320</div>
      <div class="address-line"><span class="address-label">Jakarta :</span> Office 8 SCBD Lt.17, Jl. Senopati - Jakarta Selatan</div>
      <div class="address-line">Website : www.synexa.co.id &nbsp;|&nbsp; Email : info@synexa.co.id</div>
    </td>
  </tr>
</table>
<hr class="header-separator" />

<!-- SECTION 1: INFORMASI UMUM -->
<div class="section-box">
  <div class="section-header">INFORMASI UMUM</div>
  <div class="section-body">
    <table class="info-grid">
      <tr><td class="label">Nama Klien</td><td class="val">${item.nama_klien || '______________________'}</td>
          <td class="label" style="width:110px">Ketua Koperasi</td><td class="val">${item.ketua_koperasi || '______________________'}</td></tr>
      <tr><td class="label">Rute</td><td class="val">${item.rute || '______________'}</td>
          <td class="label">Nama Teknisi</td><td class="val">${item.nama_teknisi || '______________'}</td></tr>
      <tr><td class="label">No. Body</td><td class="val">${item.no_body || '______________'}</td>
          <td class="label">Detail Pekerjaan</td><td class="val">${item.detail_pekerjaan || '______________'}</td></tr>
      <tr><td class="label">No. Polisi</td><td class="val">${item.no_polisi || '______________'}</td>
          <td class="label">Lokasi Pengerjaan</td><td class="val">${item.lokasi_pengerjaan || '______________'}</td></tr>
      <tr><td class="label">Pengawas / Pramudi</td><td class="val">${item.pengawas_pramudi || '______________'}</td>
          <td class="label">No. HP Pramudi</td><td class="val">${item.no_hp_pramudi || '______________'}</td></tr>
      <tr><td class="label">Odometer</td><td class="val">${item.odometer ? item.odometer + ' km' : '______________'}</td>
          <td class="label"></td><td class="val"></td></tr>
        
    </table>
  </div>
</div>

<!-- SECTION 2: INFORMASI LEMBAR PEMERIKSAAN -->
<div class="section-box">
  <div class="section-header">INFORMASI LEMBAR PEMERIKSAAN</div>
  <div class="section-body">
    <div style="font-size:8.5pt;margin-bottom:3px;">
      <strong>Detail / Uraian Pekerjaan:</strong><br/>
      <span style="font-size:8pt;">${(item.detail_pekerjaan || '-').replace(/\n/g, '<br/>')}</span>
    </div>
    <div class="divider-dashed"></div>
    <table style="width:100%;font-size:9.5pt;">
      <tr>
        <td style="width:50%;vertical-align:top;">
          <strong>Waktu Pemeriksaan:</strong><br/>
          <span>${fmtDateTime2(item.waktu_pemeriksaan)}</span>
        </td>
        <td style="width:50%;vertical-align:top;">
          <strong>Hasil Pemeriksaan Disetujui Oleh:</strong><br/>
          <span>${item.hasil_pemeriksaan || '-'}</span><br/>
          <div style="margin-top:4px;">
            <strong>TTD Pengawas / Pramudi:</strong><br/>
            ${renderSignature(item.tanda_tangan_pengawas)}
          </div>
        </td>
      </tr>
    </table>
  </div>
</div>

<!-- SECTION 3: INFORMASI LEMBAR HASIL PEKERJAAN -->
<div class="section-box">
  <div class="section-header">INFORMASI LEMBAR HASIL PEKERJAAN</div>
  <div class="section-body">
    ${(item.sparepart_digunakan || []).length + (item.jasa_digunakan || []).length > 0 ? `
    <table class="items">
      <thead><tr><th style="width:25px">No</th><th>Nama Barang / Jasa</th><th style="width:50px">Jumlah</th><th style="width:70px">Harga</th><th style="width:80px">Subtotal</th><th>Hasil Pengerjaan</th></tr></thead>
      <tbody>
        ${(item.sparepart_digunakan || []).map((sp, i) => `
          <tr><td class="center">${i+1}</td><td>${sp.nama || '-'}</td><td class="center">${sp.jumlah || 0}</td><td class="price">Rp ${(sp.harga || 0).toLocaleString('id-ID')}</td><td class="price">Rp ${((sp.jumlah||0)*(sp.harga||0)).toLocaleString('id-ID')}</td><td>${sp.hasil_pekerjaan || '-'}</td></tr>
        `).join('')}
        ${(item.jasa_digunakan || []).map((js, i) => {
          const idx = (item.sparepart_digunakan || []).length + i + 1;
          return `<tr><td class="center">${idx}</td><td>${js.nama || '-'} <em>(Jasa)</em></td><td class="center">${js.jumlah || 0}</td><td class="price">Rp ${(js.harga || 0).toLocaleString('id-ID')}</td><td class="price">Rp ${((js.jumlah||0)*(js.harga||0)).toLocaleString('id-ID')}</td><td>${js.hasil_pekerjaan || '-'}</td></tr>`;
        }).join('')}
      </tbody>
    </table>
    ` : '<div style="text-align:center;font-style:italic;color:#999;padding:8px;font-size:9pt">Belum ada data sparepart / jasa</div>'}
  </div>
</div>

<!-- SECTION FOTO: DOKUMENTASI FOTO -->
${(() => {
  const baseUrl = window.location.origin;
  const fotoList = item.foto_urls || (item.foto_url ? [item.foto_url] : []);
  const resolveUrl = (u) => {
    if (!u) return '';
    if (u.startsWith('data:')) return u;
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    return baseUrl + u;
  };
  return fotoList.length > 0 ? `
<div class="section-box foto-print-section">
  <div class="section-header">DOKUMENTASI FOTO</div>
  <div class="section-body">
    <div class="foto-print-grid">
      ${fotoList.map((url, i) => {
        const fullUrl = resolveUrl(url);
        const isVid = isVideoURL(fullUrl);
        return `
        <div class="foto-print-item">
          ${isVid
            ? `<video src="${fullUrl}" controls style="width:100%;max-height:118mm;border:1px solid #ccc;border-radius:3px;background:#1a1a2e"></video>`
            : `<img src="${fullUrl}" alt="Foto ${i+1}" class="foto-print-img" onerror="this.parentElement.innerHTML='<em style=font-size:6pt;color:#999>Foto ${i+1} (gagal dimuat)</em>'" />`}
          <div class="foto-print-caption">${isVid ? 'Video ' + (i+1) : 'Foto ' + (i+1)}</div>
        </div>`;
      }).join('')}
    </div>
  </div>
</div>
` : '';
})()}

<!-- SECTION 4: PENYELESAIAN & TTD -->
<div class="section-box">
  <div class="section-header">PENYELESAIAN PEKERJAAN</div>
  <div class="section-body">
    <table style="width:100%;font-size:9.5pt;">
      <tr>
        <td style="width:35%;vertical-align:top;">
          <strong>Waktu Pengerjaan:</strong><br/>
          <span style="font-size:8.5pt;">${fmtDateTime2(item.waktu_penyelesaian) || '______________'}</span>
        </td>
        <td style="width:65%;vertical-align:top;">
          <div style="font-style:italic;font-size:8pt;padding:2px 6px;background:#f9f9f9;border:1px solid #ddd;margin-bottom:3px;">
            Dengan ini dinyatakan bahwa pekerjaan telah selesai dikerjakan dan diterima dengan baik sesuai checklist catatan di atas.
          </div>
          <table style="width:100%;font-size:8pt;">
            <tr>
              <td style="width:50%;padding:1px 3px;"><strong>TTD Pengawas / Pramudi:</strong><br/>${renderSignature(item.tanda_tangan_pengawas)}</td>
              <td style="width:50%;padding:1px 3px;"><strong>TTD Teknisi:</strong><br/>${renderSignature(item.tanda_tangan_teknisi)}</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </div>
</div>

<!-- SECTION 5: VERIFIKASI & TAGIHAN -->
<div class="section-box">
  <div class="section-header">VERIFIKASI & PENETAPAN TAGIHAN</div>
  <div class="section-body">
    <div class="statement">
      <strong>Pernyataan Pemeriksaan Hasil Pekerjaan:</strong><br/>
      "Dengan ini dinyatakan bahwa laporan pekerjaan telah diperiksa dan diverifikasi kebenarannya berdasarkan hasil pemeriksaan tersebut, ditetapkan nilai tagihan sebagaimana tercantum di bawah ini."
    </div>
    <table style="width:100%;font-size:9.5pt;">
      <tr>
        <td style="width:35%;vertical-align:top;">
          <strong>Nominal Tagihan:</strong><br/>
          <span style="font-size:11pt;font-weight:700;color:#1aae6f;">Rp ${(item.nominal_tagihan || grandTotal).toLocaleString('id-ID')}</span>
        </td>
        <td style="width:65%;vertical-align:top;text-align:right;">
          <div style="display:inline-block;text-align:center;">
          <div style="font-weight:700;font-size:9pt;margin-bottom:6px;">Diperiksa &amp; Diterima oleh,</div>
          <div style="min-height:72px;min-width:200px;margin:2px auto;">${(() => {
            const v = item.verified_signature || printDefaults.ttd;
            const stempel = printDefaults.stempel;
            const ttdHtml = v
              ? (v.startsWith('data:')
                  ? `<img src="${v}" style="max-height:58px;max-width:200px;vertical-align:middle;position:relative;z-index:2;filter:brightness(0) contrast(1.2) drop-shadow(0.6px 0 0 #000) drop-shadow(-0.6px 0 0 #000) drop-shadow(0 0.6px 0 #000) drop-shadow(0 -0.6px 0 #000);" alt="TTD" />`
                  : `<span style="position:relative;z-index:2;">${v}</span>`)
              : '<span style="color:#aaa;font-size:7pt;font-style:italic;position:relative;z-index:2;">upload TTD</span>';
            // Stempel tampil sebagai watermark di BELAKANG TTD (overlay di area tanda tangan)
            if (!stempel) return `<span style="display:inline-block;vertical-align:middle;">${ttdHtml}</span>`;
            const stempelHtml = `<img src="${stempel}" style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);max-height:76px;max-width:170px;opacity:0.45;z-index:1;" alt="Stempel" />`;
            return `<span style="position:relative;display:inline-block;min-width:200px;min-height:72px;vertical-align:middle;">${stempelHtml}${ttdHtml}</span>`;
          })()}</div>
          <div style="font-size:9pt;font-weight:600;margin-top:2px;">Achmad Saudi</div>
          <div style="font-size:8pt;">Direktur Operasional</div>
          </div>
        </td>
      </tr>
    </table>
  </div>
</div>

<div class="footer">Dicetak dari sistem LayananSynexa FMS — ${fmtDate2(sekarang)} — Dokumen resmi</div>
</body></html>`);
    printWindow.document.close();
    setTimeout(() => { try { printWindow.print(); } catch(e) {} }, 500);
  };

  const [activeWorkflowTab, setActiveWorkflowTab] = useState('pengajuan');
  const [paymentItem, setPaymentItem] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [items, setItems] = useState([{ kode: '', nama: '', jumlah: 1, harga: 0, tipe: 'sparepart' }]);
  const [vehicleMasterList, setVehicleMasterList] = useState([]);
  const [sparepartMasterList, setSparepartMasterList] = useState([]);
  const [jasaMasterList, setJasaMasterList] = useState([]);
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState('');
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const [notifBanner, setNotifBanner] = useState('');
  const vehicleSearchRef = useRef(null);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [selectedItemIdx, setSelectedItemIdx] = useState(-1);
  const [itemDropdownPos, setItemDropdownPos] = useState({ top: 0, left: 0, width: 200, maxH: 220 });
  const itemSearchRef = useRef(null);
  const itemSearchTimerRef = useRef(null);

  // Server-side search for spareparts (debounced via itemSearchQuery)
  const searchSpareparts = useCallback((query) => {
    if (!query || query.length < 2) {
      // Reset to initial set
      getInventoriSpareparts({ limit: 200 }).then(spRes => {
        setSparepartMasterList(Array.isArray(spRes.data) ? spRes.data : []);
      }).catch(() => {});
      return;
    }
    getInventoriSpareparts({ search: query, limit: 50 }).then(spRes => {
      setSparepartMasterList(Array.isArray(spRes.data) ? spRes.data : []);
    }).catch(() => {});
  }, []);

  const handleItemSearchChange = useCallback((e) => {
    const val = e.target.value;
    setItemSearchQuery(val);
    // Debounce server-side sparepart search
    if (itemSearchTimerRef.current) clearTimeout(itemSearchTimerRef.current);
    itemSearchTimerRef.current = setTimeout(() => {
      // Only search spareparts (jasa is small, keep client-side)
      searchSpareparts(val);
    }, 400);
  }, [searchSpareparts]);

  const loadMasterData = () => {
    // Load initial data for dropdowns: vehicles (200), spareparts (200), jasa
    Promise.all([
      getVehicles({ limit: 1000 }),
      getInventoriSpareparts({ limit: 200 }),
      getJasaLayanan(),
    ])
      .then(([vehRes, spRes, jasaRes]) => {
        const vData = vehRes.data?.data || [];
        setVehicleMasterList(Array.isArray(vData) ? vData : (Array.isArray(vehRes.data) ? vehRes.data : []));
        setSparepartMasterList(Array.isArray(spRes.data) ? spRes.data : []);
        setJasaMasterList(Array.isArray(jasaRes.data) ? jasaRes.data : []);
      })
      .catch(err => console.error('Load master data error:', err));
  };

  // Vehicle search: instant client-side filter on pre-loaded 200 vehicles (like sparepart search)
  const handleVehicleSearchChange = useCallback((e) => {
    setVehicleSearchQuery(e.target.value);
    setShowVehicleDropdown(true);
  }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (filterStatus) params.status = filterStatus;
    if (filterNoPolisi) params.no_polisi = filterNoPolisi;
    if (filterNoBody) params.no_body = filterNoBody;

    if (filterTglMulai) params.tanggal_mulai = filterTglMulai;
    if (filterTglSelesai) params.tanggal_selesai = filterTglSelesai;

    // Load data independently so one error doesn't block others
    Promise.all([
      getTagihan(params).catch(err => { console.error('getTagihan error:', err); return { data: [] }; }),
      getTagihanStats().catch(err => { console.error('getTagihanStats error:', err); return { data: null }; }),
      getCM().catch(err => { console.error('getCM error:', err); return { data: [] }; }),
    ])
      .then(([res, statsRes, cmRes]) => {
        setData(res.data || []);
        setStats(statsRes.data || null);
        setCmList(cmRes.data?.data || cmRes.data || []);
      })
      .catch(err => console.error('Unexpected error:', err))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadMasterData(); }, []);

  // Cleanup item search timer on unmount
  useEffect(() => {
    return () => {
      if (itemSearchTimerRef.current) clearTimeout(itemSearchTimerRef.current);
    };
  }, []);

  // Close dropdowns on outside click only (tidak nutup pas scroll biar dropdown bisa di-scroll)
  useEffect(() => {
    const handler = (e) => {
      if (vehicleSearchRef.current && !vehicleSearchRef.current.contains(e.target)) {
        setShowVehicleDropdown(false);
      }
      if (itemSearchRef.current && !itemSearchRef.current.contains(e.target)) {
        setShowItemDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, []);

  // Check sessionStorage for notification from CM page
  useEffect(() => {
    const msg = sessionStorage.getItem('tagihan_notif');
    if (msg) {
      setNotifBanner(msg);
      sessionStorage.removeItem('tagihan_notif');
      const timer = setTimeout(() => setNotifBanner(''), 6000);
      return () => clearTimeout(timer);
    }
  }, []);

  // === Vehicle lookup map from master data ===
  const vehicleMap = React.useMemo(() => {
    const map = {};
    (vehicleMasterList || []).forEach(v => { map[v.id] = v; });
    return map;
  }, [vehicleMasterList]);

  // === Combined master list for unified item dropdown ===
  const combinedMasterList = React.useMemo(() => {
    const spareparts = (sparepartMasterList || []).map(sp => ({
      id: sp.id,
      nama: sp.name || '',
      harga: Number(sp.harga_jual || sp.price || 0),
      kode: sp.code || '',
      unit: sp.unit || '',
      tipe: 'sparepart',
    }));
    const jasas = (jasaMasterList || []).filter(j => j.is_active !== false).map(j => ({
      id: j.id,
      nama: j.nama || '',
      harga: Number(j.harga_jual || 0),
      kode: j.kode || '',
      unit: j.satuan || 'Jasa',
      tipe: 'jasa',
    }));
    return [...spareparts, ...jasas];
  }, [sparepartMasterList, jasaMasterList]);

  // === Filter options for dropdowns ===
  const jenisFilterOptions = React.useMemo(() => {
    const set = new Set();
    data.forEach(d => { if (d.jenis_kendaraan) set.add(d.jenis_kendaraan); });
    vehicleMasterList.forEach(v => { if (v.jenis_ac) set.add(v.jenis_ac); });
    return Array.from(set).sort();
  }, [data, vehicleMasterList]);

  const kontrakFilterOptions = React.useMemo(() => {
    const set = new Set();
    data.forEach(d => {
      const veh = vehicleMap[d.vehicle_id];
      if (veh && veh.contract_no) set.add(veh.contract_no);
    });
    return Array.from(set).sort();
  }, [data, vehicleMap]);

  const klienFilterOptions = React.useMemo(() => {
    const set = new Set();
    data.forEach(d => { if (d.nama_klien) set.add(d.nama_klien); });
    return Array.from(set).sort();
  }, [data]);

  const teknisiFilterOptions = React.useMemo(() => {
    const set = new Set();
    data.forEach(d => {
      const teknisi = d.nama_teknisi || '';
      if (teknisi) set.add(teknisi);
    });
    return Array.from(set).sort();
  }, [data, cmList]);

  // ====== COMPUTED STATS - dynamic based on all filters ======
  const computedStats = React.useMemo(() => {
    // Apply same filters as applyAdvancedFilters + filterByDateRange
    let filtered = data;

    // Advanced filters (jenis_kendaraan, no_kontrak, nama_klien, teknisi)
    if (filterJenisKendaraan || filterNoKontrak || filterNamaKlien.length > 0 || filterTeknisi) {
      filtered = filtered.filter(d => {
        if (filterJenisKendaraan && (d.jenis_kendaraan || '') !== filterJenisKendaraan) return false;
        if (filterNoKontrak) {
          const veh = vehicleMap[d.vehicle_id];
          const kontrak = veh ? (veh.contract_no || '') : '';
          if (kontrak !== filterNoKontrak) return false;
        }
        if (filterNamaKlien && filterNamaKlien.length > 0 && !filterNamaKlien.includes(d.nama_klien || '')) return false;
        if (filterTeknisi) {
          const teknisi = d.nama_teknisi || '';
          if (teknisi !== filterTeknisi) return false;
        }
        return true;
      });
    }

    // Date range filter
    if (filterTglMulai || filterTglSelesai) {
      filtered = filtered.filter(d => {
        const tgl = d.tanggal_pengerjaan || d.created_at || d.tanggal_tagih;
        if (!tgl) return true;
        const dateStr = tgl.slice(0, 10);
        if (filterTglMulai && dateStr < filterTglMulai) return false;
        if (filterTglSelesai && dateStr > filterTglSelesai) return false;
        return true;
      });
    }

    const total = filtered.length;
    const pengajuan = filtered.filter(d => d.status === 'Pengajuan').length;
    const disetujui = filtered.filter(d => d.status === 'Disetujui').length;
    const selesai = filtered.filter(d => d.status === 'Selesai').length;
    const lunas = filtered.filter(d => d.status === 'Lunas').length;
    const ditolak = filtered.filter(d => d.status === 'Ditolak').length;
    const total_nominal = filtered.reduce((sum, d) => sum + (Number(d.nominal_tagihan) || 0), 0);
    const nominal_belum = filtered
      .filter(d => ['Pengajuan', 'Disetujui', 'Belum Dibayar', 'Sudah Ditagih'].includes(d.status))
      .reduce((sum, d) => sum + (Number(d.nominal_tagihan) || 0), 0);

    return { total, pengajuan, disetujui, selesai, lunas, ditolak, total_nominal, nominal_belum };
  }, [data, filterJenisKendaraan, filterNoKontrak, filterNamaKlien, filterTeknisi, filterTglMulai, filterTglSelesai, vehicleMap, cmList]);

  // === Client-side filtered data for 'Semua Tagihan' tab (uses applyAdvancedFilters + filterByDateRange like other tabs) ===
  const filteredData = React.useMemo(() => {
    return filterByDateRange(applyAdvancedFilters(data));
  }, [data, filterJenisKendaraan, filterNoKontrak, filterNamaKlien, filterTeknisi, filterTglMulai, filterTglSelesai, vehicleMap, cmList]);

  const exportCSV = async () => {
    try {
      const params = { export: 'csv' };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterNoPolisi) params.no_polisi = filterNoPolisi;
    if (filterNoBody) params.no_body = filterNoBody;

      if (filterTglMulai) params.tanggal_mulai = filterTglMulai;
      if (filterTglSelesai) params.tanggal_selesai = filterTglSelesai;
      const res = await API.get('/tagihan', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      link.setAttribute('download', `tagihan_${now.toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export CSV gagal', err);
      alert('Gagal mengexport CSV. Silahkan coba lagi.');
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      tanggal_tagih: new Date().toISOString().slice(0, 10),
    });
    setItems([{ kode: '', nama: '', jumlah: 1, harga: 0, unit: '', tipe: 'sparepart' }]);
    setError('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      cm_id: item.cm_id ?? '',
      vehicle_id: item.vehicle_id ?? '',
      no_body: item.no_body || '',
      no_polisi: item.no_polisi || '',
      nama_klien: item.nama_klien || '',
      jenis_pekerjaan: item.jenis_pekerjaan || '',
      detail_pekerjaan: item.detail_pekerjaan || '',
      tipe_pekerjaan: item.tipe_pekerjaan || '',
      jenis_kendaraan: item.jenis_kendaraan || '',
      invoice_no: item.invoice_no || '',
      sparepart_komponen: item.sparepart_komponen || '',
      nominal_tagihan: item.nominal_tagihan ?? '',
      owner_name: item.owner_name || '',
      owner_nik: item.owner_nik || '',
      owner_address: item.owner_address || '',
      biaya_lain: item.biaya_lain ?? 0,
      ppn: item.ppn ?? 0,
      status: item.status || 'Pengajuan',
      tanggal_tagih: item.tanggal_tagih?.slice(0, 10) || new Date().toISOString().slice(0, 10),
      tanggal_jatuh_tempo: item.tanggal_jatuh_tempo?.slice(0, 10) || '',
      tanggal_bayar: item.tanggal_bayar?.slice(0, 10) || '',
      tanggal_pengerjaan: item.tanggal_pengerjaan?.slice(0, 10) || '',
      catatan: item.catatan || '',
      signature_url: item.signature_url || '',
    });
    // Fill no_kontrak and lokasi from vehicle lookup
    const veh = vehicleMap[item.vehicle_id];
    if (veh) {
      setForm(f => ({ ...f, no_kontrak: veh.contract_no || '', lokasi: veh.route_code || '', jenis_kendaraan: veh.jenis_ac || '' }));
    }
    // If tagihan berasal dari CM, ambil detail_pekerjaan dari data CM
    // Prioritas: cmList cache → API fallback → jenis_pekerjaan dari tagihan
    if (item.cm_id) {
      const loadCMDetail = async () => {
        let cmData = findCMById(item.cm_id);
        // Jika CM tidak ditemukan di cmList (limit 50), fetch dari API
        if (!cmData) {
          try {
            const res = await getCMById(item.cm_id);
            cmData = res?.data;
          } catch (e) { /* ignore */ }
        }
        if (cmData && cmData.detail_pekerjaan) {
          setForm(f => ({ ...f, detail_pekerjaan: cmData.detail_pekerjaan }));
        } else {
          // Fallback: gunakan jenis_pekerjaan dari tagihan (berisi detail dari CM)
          setForm(f => ({ ...f, detail_pekerjaan: f.jenis_pekerjaan || '' }));
        }
      };
      loadCMDetail();
    }
    const mergedItems = [
      ...(item.sparepart_digunakan && item.sparepart_digunakan.length > 0
        ? item.sparepart_digunakan.map(s => ({ kode: s.kode || '', nama: s.nama, jumlah: s.jumlah, harga: s.harga, unit: s.unit || '', tipe: 'sparepart' }))
        : [{ kode: '', nama: '', jumlah: 1, harga: 0, tipe: 'sparepart' }]),
      ...(item.jasa_digunakan && item.jasa_digunakan.length > 0
        ? item.jasa_digunakan.map(j => ({ kode: j.kode || '', nama: j.nama, jumlah: j.jumlah, harga: j.harga, unit: j.unit || '', tipe: 'jasa' }))
        : []),
    ];
    setItems(mergedItems);
    setError('');
    setIsPM(false);
    setShowModal(true);
  };

  const openCreatePM = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      tanggal_tagih: new Date().toISOString().slice(0, 10),
    });
    setItems([{ kode: '', nama: '', jumlah: 1, harga: 0, unit: '', tipe: 'sparepart' }]);
    setError('');
    setIsPM(true);
    setShowModal(true);
  };

  const handleGenerate = async () => {
    if (!selectedCM) { setError('Pilih data CM terlebih dahulu'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await generateTagihanFromCM({ cm_id: Number(selectedCM) });
      setGenerateInfo(res.data);
      setShowGenerateModal(false);
      setSelectedCM('');
      setCmSearchText('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal generate tagihan');
    } finally { setSaving(false); }
  };

  const totalSparepart = items.filter(i => i.tipe === 'sparepart').reduce((sum, item) => sum + (Number(item.jumlah) || 0) * (Number(item.harga) || 0), 0);
  const totalJasa = items.filter(i => i.tipe === 'jasa').reduce((sum, item) => sum + (Number(item.jumlah) || 0) * (Number(item.harga) || 0), 0);
  const subtotalSebelumPPN = totalSparepart + totalJasa + Number(form.biaya_lain || 0);
  const ppnPercent = Number(form.ppn || 0);
  const ppnAmount = subtotalSebelumPPN * (ppnPercent / 100);
  const nominalTagihan = subtotalSebelumPPN + ppnAmount;

  const selectVehicleFromMaster = (v) => {
    if (v) {
      setForm(f => ({
        ...f,
        vehicle_id: v.id,
        no_body: v.body_no || '',
        no_polisi: v.plate_no || '',
        nama_klien: v.operator || v.owner_name || '',
        no_kontrak: v.contract_no || '',
        lokasi: v.route_code || '',
        jenis_kendaraan: v.jenis_ac || '',
        ketua_koperasi: v.ketua_koperasi || '',
      }));
    }
    setShowVehicleDropdown(false);
    setVehicleSearchQuery('');
  };

  // Vehicle list for dropdown — client-side filter on pre-loaded 200 vehicles (instant)
  const filteredVehicles = vehicleMasterList.filter(v => {
    if (!vehicleSearchQuery) return true;
    const q = vehicleSearchQuery.toLowerCase();
    return (v.body_no || '').toLowerCase().includes(q) ||
      (v.plate_no || '').toLowerCase().includes(q) ||
      (v.operator || v.owner_name || '').toLowerCase().includes(q);
  }).slice(0, 200);

  // Helper: sanitize date fields before sending to backend (slice RFC3339 → YYYY-MM-DD)
  const sanitizeDates = (obj) => ({
    ...obj,
    tanggal_tagih: obj.tanggal_tagih?.slice(0, 10) || '',
    tanggal_jatuh_tempo: obj.tanggal_jatuh_tempo?.slice(0, 10) || '',
    tanggal_bayar: obj.tanggal_bayar?.slice(0, 10) || '',
    tanggal_pengerjaan: obj.tanggal_pengerjaan?.slice(0, 10) || '',
  });

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      const rawPayload = {
        ...form,
        cm_id: form.cm_id ? Number(form.cm_id) : null,
        vehicle_id: form.vehicle_id ? Number(form.vehicle_id) : null,
        nominal_tagihan: Number(nominalTagihan) || 0,
        jasa_digunakan: items.filter(i => i.tipe === 'jasa' && i.nama.trim() !== '').map(i => ({ kode: i.kode || '', nama: i.nama, jumlah: Number(i.jumlah) || 1, harga: Number(i.harga) || 0, unit: i.unit || '' })),
        sparepart_digunakan: items.filter(i => i.tipe === 'sparepart' && i.nama.trim() !== '').map(i => ({ kode: i.kode || '', nama: i.nama, jumlah: Number(i.jumlah) || 1, harga: Number(i.harga) || 0, unit: i.unit || '' })),
      };
      const payload = sanitizeDates(rawPayload);
      if (editing) {
        await updateTagihan(editing.id, payload);
      } else {
        await createTagihan(payload);
      }
      setShowModal(false);
      setIsPM(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Yakin ingin menghapus tagihan ${item.invoice_no}?`)) return;
    try { await deleteTagihan(item.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal menghapus'); }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await updateTagihan(item.id, sanitizeDates({
        ...item,
        cm_id: item.cm_id || null,
        vehicle_id: item.vehicle_id || null,
        nominal_tagihan: Number(item.nominal_tagihan) || 0,
        status: newStatus,
        tanggal_bayar: newStatus === 'Lunas' ? new Date().toISOString().slice(0, 10) : item.tanggal_bayar?.slice(0, 10) || '',
        metode_pembayaran: newStatus === 'Lunas' ? (selectedPaymentMethod || '') : '',
      }));
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal update status');
    }
  };

  const handleBayar = (item) => {
    setPaymentItem(item);
    setSelectedPaymentMethod('');
    setShowPaymentModal(true);
  };

  const handleKonfirmasiBayar = async () => {
    if (!selectedPaymentMethod) {
      alert('Pilih metode pembayaran terlebih dahulu');
      return;
    }
    try {
      await updateTagihan(paymentItem.id, sanitizeDates({
        ...paymentItem,
        cm_id: paymentItem.cm_id || null,
        vehicle_id: paymentItem.vehicle_id || null,
        nominal_tagihan: Number(paymentItem.nominal_tagihan) || 0,
        status: 'Selesai',
        tanggal_bayar: new Date().toISOString().slice(0, 10),
        metode_pembayaran: selectedPaymentMethod,
      }));
      setShowPaymentModal(false);
      setPaymentItem(null);
      setSelectedPaymentMethod('');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal memproses pembayaran');
    }
  };

  const filteredCM = cmList.filter(c => {
    // Only show CM with status 'Selesai Pengerjaan'
    if (c.status !== 'Selesai Pengerjaan') return false;
    // Exclude CM that already have a tagihan
    if (data.find(t => t.cm_id === c.id)) return false;
    // Date range filter — prefer tanggal_penyelesaian, fallback to created_at
    const tglCM = c.tanggal_penyelesaian ? c.tanggal_penyelesaian.slice(0, 10) : (c.created_at ? c.created_at.slice(0, 10) : '');
    if (cmTglMulai && tglCM < cmTglMulai) return false;
    if (cmTglSelesai && tglCM > cmTglSelesai) return false;
    // Search/filter
    if (!cmSearchText) return true;
    const q = cmSearchText.toLowerCase();
    if (cmFilterBy === 'cm_id') {
      return (c.cm_id || '').toLowerCase().includes(q);
    }
    if (cmFilterBy === 'no_body') {
      return (c.no_body || '').toLowerCase().includes(q) || (c.no_polisi || '').toLowerCase().includes(q);
    }
    if (cmFilterBy === 'teknisi') {
      return (c.nama_teknisi || '').toLowerCase().includes(q) || (c.teknisi_handle || '').toLowerCase().includes(q);
    }
    // 'all' — search across multiple fields
    return (c.cm_id || '').toLowerCase().includes(q) ||
      (c.no_body || '').toLowerCase().includes(q) ||
      (c.no_polisi || '').toLowerCase().includes(q) ||
      (c.nama_klien || '').toLowerCase().includes(q) ||
      (c.nama_teknisi || '').toLowerCase().includes(q) ||
      (c.jenis_pekerjaan || '').toLowerCase().includes(q) ||
      (c.detail_pekerjaan || '').toLowerCase().includes(q);
  });

    // Filtered data for workflow tabs (applying advanced filters)
  // NOTE: These are `function` (not const arrow) so they are hoisted and available to filteredData useMemo above
  function applyAdvancedFilters(items) {
    if (!filterJenisKendaraan && !filterNoKontrak && filterNamaKlien.length === 0 && !filterTeknisi) return items;
    return items.filter(d => {
      if (filterJenisKendaraan && (d.jenis_kendaraan || '') !== filterJenisKendaraan) return false;
      if (filterNoKontrak) {
        const veh = vehicleMap[d.vehicle_id];
        const kontrak = veh ? (veh.contract_no || '') : '';
        if (kontrak !== filterNoKontrak) return false;
      }
      if (filterNamaKlien && filterNamaKlien.length > 0 && !filterNamaKlien.includes(d.nama_klien || '')) return false;
      if (filterTeknisi) {
        const teknisi = d.nama_teknisi || '';
        if (teknisi !== filterTeknisi) return false;
      }
      return true;
    });
  }
  function filterByDateRange(items) {
    return items.filter(d => {
      const tgl = d.tanggal_pengerjaan || d.created_at || d.tanggal_tagih;
      if (!tgl) return true;
      const dateStr = tgl.slice(0, 10);
      if (filterTglMulai && dateStr < filterTglMulai) return false;
      if (filterTglSelesai && dateStr > filterTglSelesai) return false;
      return true;
    });
  }
  const pengajuanData = filterByDateRange(applyAdvancedFilters(data.filter(d => d.status === 'Pengajuan')));
  const disetujuiData = filterByDateRange(applyAdvancedFilters(data.filter(d => d.status === 'Disetujui')));
  const ditolakData = filterByDateRange(applyAdvancedFilters(data.filter(d => d.status === 'Ditolak')));
  const selesaiData = filterByDateRange(applyAdvancedFilters(data.filter(d => d.status === 'Selesai')));

const rupiah = (val) => {
    const n = Number(val);
    return isNaN(n) ? 'Rp 0' : `Rp ${n.toLocaleString('id-ID')}`;
  };

  // ===== PDF Export — redesigned to match sample invoice (PT Synexa style) =====
  const generatePDF = async (item) => {
    if (!item) return;
    // Fetch full tagihan detail (includes signature_url which is excluded from list for performance)
    try {
      const detailRes = await getTagihanById(item.id);
      if (detailRes.data && detailRes.data.signature_url) {
        item = { ...item, signature_url: detailRes.data.signature_url };
      }
    } catch(e) { /* signature not available, proceed without it */ }
    // TTD & stempel default dari pengaturan sistem (berlaku semua device)
    let ttdDefault = '';
    let stempelDefault = '';
    try {
      const sRes = await getPublicSettings();
      ttdDefault = sRes?.data?.ttd_direktur || '';
      stempelDefault = sRes?.data?.stempel_perusahaan || '';
    } catch(e) { /* settings not available, proceed without defaults */ }
    const { jsPDF, GState } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentW = pageW - 2 * margin;

    // Colors
    const dark = [30, 30, 30];
    const gray = [100, 100, 100];
    const headerBg = [13, 29, 54];
    const teal = [57, 143, 153];
    const tableHeadBg = [221, 240, 242];

    let y = margin;

    const drawSep = (yPos) => {
      doc.setDrawColor(180, 180, 180);
      doc.line(margin, yPos, pageW - margin, yPos);
    };

        // ===== 1. HEADER =====
    // Logo perusahaan (kiri atas)
const logoData = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAABwCAIAAADUo6jRAABWKElEQVR42p19d7wdZfH+zPvunnZbklvSk5teSCCQQkLvRXoREAUFG0VFBRSDgICAqIgIFkSK9BqqSDAoPaAJNRAgAdIL6becc3b3fWd+f2w5W8/N93c/l3Bzc87Z3fedd8ozz8xgtVoFAAT3Dwb3CwEAgZlDv0UAAGD/h6yvzBdg+CdmTnsZArB/Of/CXPsXQMTQdQAY/dtK+ziO30/ko4K7xeCvyADI7F6emTG0JOy+EMN3ie6jhB+cMx+69my1W0q+IfJmZAbvov6tMyfuP7gUsnsFTHx0bEndV/hr5v2Wwy9IbEr6IqZuPQIAitqLMbg4AHvrhZG3of9OjN2x947EtUL/ihD9SMT4IgY3FXszhrcm/LjsfaR/XYx9c5rgYsohca/tiwwieKKGtaXyro3MyMy+XLG/O5FDE9wo1p4Dg/9H9ogjixb5GWvbUHuZJ9jeYgWP44orc03GgqdnrC1N+KDG9p39F7C/0aGPiGww1+4cY3deEzZmgcGCs397CYFAAET3YKerIY5uafAonuJDQGT3f6F7wNB7MHT7gO7VauuBGD4iHFoRjLwkqqdC5xWBMThLriAhJN4YfiJXd/lS5amPYKc4tJwIzFFxQWYM7tdValhboWAda0IbVQTeinkf7t+vr1lDpymqtd0lc//0FRTHjlfwsDUN5m54sKTB9YGBMaEgop/EmPhXRG/VDH/pOe2A+3cSU2DMaZuBvonzDwfG7BEj1wwLIrD/h2d1mDFYJnYtD8cOky8O/jOz9/wRJZ9Uda6KQUyzVxwx0OFzyp47gBnGgUMLglk+gKf6EpYDff3v/+nvKUf0CoZOL4c/p/aByLVNcb0FV6ciMPpr6C1mZBPDRzVYa47cXk1YQvqPQyonus7e6WEGBBGzrBzSOu6VmH35TBNeDuvZuPXE6P9SnCD2lwr9/UP2rhk8JPonFMPXZ3/FfBsRMRapHpW3ZzXz4y6+b009Xc0RG4FRQeWwYCRXNn58a7fqCqn3JME58XY3uK/gkQNjGtgb71fMni7jhCPh6ycObWlYqlKclmD3Qq5QRHVgTI8gZ3rYsXUTqa9gjK4Xh9c5YmaCv6eIXfiGMOIrhtyu2lELn5DIvSJiTHdy2O2BhH70rUDUvWVPJQBjbWU5ZCTdC2PouThQwFFHNOVZIWSPEsvtO5hYk/zwKzH8KFG5xGCFau47JrUus6v6A7cG6lixpOMfmPnaZZP+WB9fIUcIAUCEdjy8CpDijXrWCpPeOkPcKc6S55jJwPg9hSxLxnLH5dY1IhhZMgjZLww77hiXSf9ha/5lekyXtTnp9xQ9eGHn0VcPsVgyagFCCiYIDQLdkx2RRMP3iCudImcY2ll3uRBrNgGxTtTPSactsGn+rojk8tV81MCD5tBaYKYrxn3BEBxeBcQ6d++6wAyp210Tp5oXwumhY5rtrX3izogRpn5gNACOhk1JPw45LBxJdyL0hPHgOQu5QEyiKsAJQ5F9NjALngkvVFr4HzNlMUllPwoUqc6TH71xXOdiTRlg4pYwHAyGtxkxFvH6VohjxivmNCBi2HQmwYbIj+hZcE6J8ULubehmMCEkKegMM9T1KmJATeyaISOMWTgfRt2MtOgpLoiBYYdU1VjPYCFkqDGOOW5RzwyjGEr4t+zZYi/ace9ZpD5nGAiMyAfH0SCse5Qjqi7bqnggDMZOMXuxIWISM4sIKIZXh2P3wxANTRIPnHT26z1QGtQSU0ScKoIhfzhdlNMx3sizhk1kDDVI/C0LXIx4OVEnMv2R6xwwZg6LQfhzRPg5M1fKi9I4Il6py9eX3eA64sVp++1KGGLSMwuhMph1wDmO0AAnIqqQykHO0EZ1xA4zcfYs8Cb+1zBeWjskfqDHfZn3YM8xId+Ydvi5juqNoKlxZ4vrWOfIKxkYRB3dnopW11c/Sc8j4UHHTSSk2c244WBOuQgHWLaHP2HUO4k7LmHDWgNmMaxUMIydQpZpi5wWThzI5A3XsIIooANJc8ahT/CB1vQjF5NurOsbYgrmzgloJq6+otYZ+/Jfwz6eyDqIafuDfZ4dyDYcYXcqBMRhSpgXB1IAMhRDSkqljmoM+84cEc8w1NnHycnyXLCvQDKCrYfwIU7Rfxxbruyz54KmyAkPKbbCHD8hScwCw/nM4A7DLkp4ZVI9wpDkiEz1mPorrrfqqVBCONWHSZWT1Ldcix/rZGeTegV2EilIYCKYjXDGDhLXSbRH5TseDyVdGczQhEkdyRzXCiHPFYEzEv+YImFZ+asQXpE0Eelbnha6ujlM158Q2VuCfexKEsOo7znVHM8gx4TJ6COcXkmGqxw6SVmwPidUSaqHF1bsjAmdmraobsaJk9vPcWcZ4yALJo5oqjOd8iyc4U2H1hUxxVRxql7IOJwcDt4j3j3G7X5mrOrein8FkW3LEtYoEtpHhToVzMRIqssDFBBDyLgfFIQzp2kxP9fwKk5f6DSICBOigeGDG0SdXjIxEqLW4I8oDoKc4st70T+mBEB+2pmTOo85Y5M4fs8YIy9FJQCjO9Ynmgh1jH7yfHI9T4ajIUgtBwcoMEFMiItINDJOgenYzbclNzt058Fa+Go8Qi/ZCawo5jNgmrHnOlEbhpL5oZxcqjOHYew+eso5w4Ih17G/tewxYqa3DXVinb6Me8p7o/Yk+q/IqV5KVNTCmhgT+G0shOJQWO16vYKje4VRVzQqlRhxHqP3kVRvHIPjmNNx8IwUYWpuKy7cCYnjVJkL4b0Yc9V3fqvSVEItqYrI3IdrWMs9R0FITtc/2dFPMkbuEwDDcCIufgi4Fl+72hfDW9JHYi12UW/LXT5WlDERJs5FYnXmpO/OIUgCU0BCrmeSk8o5TWVGqVWZqt61qBHPicPsmqhzmhWupuUhOAXxqp1QjjqznOSTcD3sNJIzC+dto9BdarDCMdC8hoHFPZbazmHchY2CqxjRScx1gp54BBJShhzADdBnxO7RZ9LDCuBM3I8zFAOm+j11Uit9KRWMeC5R3mC2WoVE/BWITtbKYDQrFwYa6kpt/E44CPKiz8vp2pTTca/oLgQPn6r+Y7SiOCTPYZWTifcGpjaOJ3MkIBCZ8XxaHg1iEHyq8WIOC2AfnkHCqmahAPELhWUxwvpI3k+EfIG1mCJDj7qfxnH6Vf2MQt8pIA5nCOLkOK6PiaCvCxCjXL2QzsA0HCuqCYKkJNd8zpT4IAtdiJC2fPwWwlR3rtFeRWyb4xk3TMD2YTgqyzKmYSecgbkzZIT69UUzjIH5LkI4YsK0hAbHEyiZAHIMNUhd8lTrH2wzJh3EkCZJ9SMxEleiF1F6lGQOQookpZWj1JOY7U0XdIxHfJz9pLUPZ+DM5FvIiYuldMJiXjsNmNjOIBiMOiXhbeNaoo9T/IPAteRMTysBCqegO9G0T40HmhI/h8NS5lSvFsJkPYwfNsiGlFKyk5yCCnJa1iTh+6aDCFE9xmGPkLPTEpDmSPkyGg0UM+genFahU28RfHUo0iKXuCRz1pmOJuQx3ROo5enCi+eVVmC2CYstUyyEjPHNEy4RxyDvrAAwidbFMi3BdbH2lZrijMccfuwd1vSxz46VJvXNVOOIR5hKwuZ4jiYN5A+fN87EZ2Ms3JQjkY4eAACgVa2mQIicXpKWpDvGYHNMYEic9pnh/a/nZCD6wTjWUlh9cHAYUjOS0SeqEzxjRiSYHSRirMQrLf2KqaK9k9BUxmcCZLMwIFUI6tYwZvEQMXF1Ti0yDRe/YLrzzqkZJ05LWwZRBoYwUY6hBmlJm+B1iMh1lxLD3lEfsWIYM2FOA/EgLQQLl9HV88cxngPlKBjPmUw9zooVoiAWZmUnMdsBxYQpRMgyjZC11LwTkB6nWd6sG06DG8KamuMBRWoCK1FDne3LZ4MiqX4078zhDgICrBUuAqckm1MThTFAClJT6aEyjlpInyZPGF0QrtUScd95liirlpMxRyorJO0MZJVZpta+xY9Khn6CUFAZxsnCTxpUToos4eYE9hoonrjNxvQgOY0CkE6rwrp4904cJU4F93kn1D6m5gD6YiZi4ujFSkHjmR+GeicEkzuNcd3DNb8tK6StkymKeeVJ+Utag1r1h3/RulWlvsz5nyiiD4CpmGE4gktUDtWsJGTTLFNsK8Q/DSGex42lBXeC6bVT9RFZygP7Ss8hxp3IgA0WA7IxYUCzPLx4TRTWhUfrxZRJpwpTNVAqTSiVRBouhcU0cYzrVK5VHQhMTWAnojkMSRdHU4kYV5XpwSqn5WQ4rmkx1YGLU+QgXT3UatJ3PjxOS+ZkqSvmvhzkhLfLySKFDM8pVg6WxgBNw1yzQUFOUqYwUcMdTlL5GXLMVgRZoG5wtALVLzga5cYAHo4Q8WpkhDqVUhjFrKN8j/TohEOXwz48qswanDDPOAx9YcxApzqbNf+grsZKFakQkI1pSGmmaoxCtZlgLGYmi9CDMGpUongdHkYkA7lmHGIUK+bMOrvUQIEzCdzeG4yIGAZslpi/wpwErsMng7KcG44QRaJ5JTCEAGZNLtJKwZuEDxalEjUB4p0FXM3KodQpeeY5IsNSiAiylVVYi7VMqiLyE1RezlMgCCEgLVgJmqoQh58yCrswCyEAURO5NdnB2RAxhIxT8vnhxiiayIMihRBB2ymuORXM8feGE5pak2ezhAguHFmwqCcat5jAaf0oakcBLcsKVXlwFtKTzLQDs2YGgGI+l1qq3+eXbdtCoGGYid2limWjSxZLA6Ky/BViYsBiPp+qKLRWlu24u5DE2yCtHVc+n0+5bcuK6WP0d1qgKORMEHVXg8myneQne7fniVctIE/29CJmwzAMw2/oQmTZthCCgcM8RMw4Pe4vghtgJsuyBWISCOQM0xR/gX+2a4cWAavVKkYBT+B6cFxwt0RcLBYA4PM16xZ98vmyjZu7Kw6gACl8ziQhIErBDMyEQiAKYCYiU8rWvDzv2EM+W7vh5ieeKzT111oJw2jM58YMaN536oTOYUO0UrZSEkV9B8nnXLDSVCoWAeCz1WsXfvL5kvVbtnR1a6J8vjCosbTb8IF7TxrT1jpAa2XZyhBYH2NkYFMaD7385vurNhj5PGstpFBKj+jfeNah+0YxC9aahcB8Pg/Aq9Z98d7nq9Zu6+rVQEqR0ijQPSJSGtpxDtlt/OTO4b955FlLaWBkppIpJ4wYvO+UCe0D+ntPLWodiGK97zRRsVjY0d3z9KtvfbRh08Cm0vF7zxg+qN2ybW/Z/eZsXuo6Khyuz1fIm/c//OSCf788dGD7Od/9+pAhg0OylQaD1wWTU8JtBLSq1aQ7lkDAIv4nMQshcrncK+9+eNM/Xnz+8/XduQZobIBcAYTwwgn31EkBiEAMiCCEZ9iJQUjYuvXne0+86pgDZ91076ItFhRzAAiaoNzbj9XpE0dcccLBHQP6VaqW7AszdG1fsVD44LOVv37q3098sqpLFmBAKzQ2gGEAEZTL0LVjaM48feKwnxy5b1vbgEq1KrEe4IqISHryFbcsl03QrxlsCwwDlNPRUlr+7RMKAoi9JlaaqFQsknIef3XRHS8vfu2Lrh1ogJmDYhEQQSkQAoQE0gAIjtPZnF968Zm/+seLVz6/CFr7AzOgBOLB2vr6xCEXnnBo24D+5WrVQIREtkMTFwr5JctXnPbbOz9UOejfCqo6uCCf+dZxu40e7ti2CEEDqdpBaV0qlX5x7e+uvOov0K8VureNmzz65afubG1r1VqJKEc8K5PDiQCl1m8xECy3VWS92DUqcURsGIYm+ultD930xocweiyMG2P27y/yOZB+lxsm95iG8onSj9kEMIPWwGC/9c6rR+85ccKocf96vyxNEOim/JzeCixbPr5ry2NnHDWlc2iWbNV0J3OxUPjzk/Mvnvfv3vFTYdKEXHMjGxKEMCQ6zMqywVFQ7oWlSzucyu0nHnL0lHEV25YZoL/H2VZqzr3z3xs+RjYWCUAKVEp1Clo0c0wJgQAQQBGXioXFHy+/+L5n/7OlAqNGw4ihZnMTGNIvckB0i2QZmMgwZeXz1WfnrdvPOPaABe+8WoVcztSArLWzZRt8tmKMU77rlAP3mTqhXKm6ajV4WGKWhrGjq3fWpTd/PnRsfsokQjZMWdm05bgSP3HS/hXbERhHE8M/E1GhUHj7nfdnHfoVHDJB5AtCyupny77+5QPu+uOvKpWKlJKDvi/IwP8fDo7Py3JzhSnRYywbgwAMxCyktC375Otvm7+pKvfeS7QPICl8Ig4zSkDfFHqeZIg/5noOBIwghdCWPXzpkmXfPOq+rdWzl6zLNRSUGx8KYeRMa/Wa4Z8tf+3sowe39leOEztMgWJRWpeKxevuf2Lugrfx0MOMIQO1d9a1ZHJsVZRw6sB+X+0cOJjssmV/tGnbq+988O09d50+akS1WpVCBE40JzTWzHv/9e7Q0UZDgYgEstJ6lKR3Zo8vIShmIi4VC7c9++IP5r1YnTjVnDwBSzkC8mIZYiTy3HDy8XlmYZjqg6UvHD5t/JjOcf94y8nlvSWWhpSGvW5j6cN3nz3ziP2njC9XKoaUQbBGzMV8/pRrb32kx8jtvaejFSIIQGbaLc+LDppqpfaIC/mOzGAa8qDjv/Hy+xuMjoHariKzEIK3rH/p4Zv2njOjXKkYGQ4iZ2fnUtWS4BpVvAbO1USNI4kwADCl/Pof7p6/1ckfdhi3tWpmVhqIJIBEaSKaQAaTgWAgSmaD2UA0BRgCDAYDhSHQQESlC6Zc1Tbw3GcXnjWy4/iBzXZFGV6fH3aqVm7o4NUjR3/rmVfds89RHNUVdleqHn/lv3NfeNs49jgxfIhGANRASjI5Sh/R1vjWjNF3Thi46eXXfnLj3791y0N/fPI/m7f1PvDaOxs2by3kc5ooi8QXivQJEAEkCIlCulwgV6p+/fA/vvPYy/bBh5t77KpzUmsHtEJmCWgIIYWQiBKFlMKQQgophZTA5vhx3375w2Ggfj15sO7qEYiAAjWpasUc2Fqeseepj728ev3GXC5HbuiHoLUuFgq3/uPFR1bsMKfvrpRyt00I1Ir2am9BQxCnERP95pta60Ihf9ud97/88jtGW7t2LK8MEFnLwk+u+5NSSgrBddP7qQy8lMZGDAJrPEBOUu3C79FEhULhL0+/MG/FtvwhhzimAUoxsQDQDLatbcu2bMdylGXblmVZtm1rbSmybGU5ZDtsO9q2HNtWjq0cpSoVC9ra71jTddtL/7tt6rCOgqFYuO49MKuqnRs25Hlt3vr6u8V8XhPFnpOYcrnc2o2bzn3wX+LAQ6CtPzm2i9MZAhylv9bW8OxuIwazs9flN50+75VnGwe9N3rim0PHzMv1/90G+0t3/XNTV08ul9PMmJrsY2ZNXomN104FXU+CmEvFwt3Pv/LTf71tHnE4tvXTygYmABAoCMDR2naUrcnWbGtyf3CIHQbL1g7gZ2yeev/8708YevCw/rbSkjUDCyFJqVxTaeOgYT989g1DeJZaayoUCks/X/WTJ1+XM2eREEAMmgSRY9tDi/JnE4YoRSKBhQUYFhHl8/nVq1Zfdt0tYshoJkL3cBBpRxn9Wl5/4/17Hnwyn89rrRmSRLwabBaDlDEGjPnAp8FpGH9yoRkgZ5pbt++4ZsFbYtoMJQWQApSCWNlO3pT7djQNzUvABEgnRK11AROC8BpzsAZiBNCtuy3fsa3NlH+aNOjkd9YIUwJ5vj85SnaOvHTxB4ePGTZiYJvtODJE9mcAQ+D3735q4/Cx5pBB2qogCmCQwHbF+lJ7411Thmuik2+8ayE0FY4/TBvSrf5DFGK0eGf5qq/ev+DZbx2DKCjRyNWHnQiI3O61rklHBk1k5nNLV6w5f95Lcr/9uF8TaOU5AMS2clCKqY354cVcQYgaNIhuk1sCAgDEwc306afrN2+/Y+aYKQuWlAlQMJMGAOWwOWTwvA8/euiN906dM623UjWl1Fp/92+Pdw0aYbQ0auWgkIiIrNm2bpvZOaQhX7GcmMuI4TalTFLKS666YXMXGCMatFP1PG0mAGBN2DzgihtvP/5LBzU2NWqtEbGvugNOIFvhSIONEChYD/XWRPl8/pn/vb9WNphtbdqxQQgpyFF6Zr/C36aN2LW5EEKzuGZGYlFFpE96AA7KctU+qaPx6+3Fv6/tzhVzityomYQ0trYPuuD5/z115pEQ6geutS4Vi7f/8z+Pr9pqHjVHa4UAjITMGmBEQ+FvYzuklM+88c6CHpE/eB9HMLB2L0ZMWqlc55AF73VdNO8/N512eLlalQEMGqowY2Yg/xsRmIAUAAjgnzyyoGfsLrmONmXbLrjJmonpW8P6nTO0/5SGfF6KaG+9MPhJgAL26CxbdskQ141r/957a3OlkgIJmoCZUYvO0Re/+enBE0c1NhRyudwd/3zlla3a3GecVhqFYK0NCbZSl0wecuSwAWXLMWJSFWLSKaVLpeIzz86//9H5RucUrWxABBeFRARAIpINpdUr11x/852/uuLHjuOGh2m11BhhJ8dpgIhB8lgE+4y+H8PZ5LAFH63EtjYUiASoWTlOq6BHZ3Tu2r+hV1HZUb2OKjuq7FDZUWVblZUuO+63Kju6rHTZ1mVblW33Zcp7WdUSCLbSN04eOqZgOFVHaGKlmYhs2+xoe7pH3/3fJcWCZxCJKJ/PrVi38SfPL5azZxMyKAdIC01CMznOn8Z1DC7lGeC5ZWuwczTkTCRCvzDKLUjQSuemTPjDyu1/e/F/pUJB+c5WhImsGYi9txCB0uzolmJh4YefPrN6ixzTqYlBSGQBSoNl3T5p0G1Th09vKgJARZH/7MEK+N+ay44qVyoSuKL0+eOHHjl0gF1RUjOAABSk2TCN1Wbjpc+9UTANZn5u6Qpj+AgUEgBBs0lsd5X3H1C8euqIqu3BBBGav18kSESGIbu6ui687DfYMhiYkBxgCjqsu24TOY5sbbv59keXLv2k4K5zEmT2UVvMajgYqpISft7HQ9IwSrENGTRBjv1Jj8P9W1lr1iSZ2VKHtxZHNBV7qo6BKBHdPyWCgcJANAD8v6L/De5fZfTbDd37581bpw4BxwFiIAI3OeKQGDr8olc+WLNxk+sSAaBgOOf2x7YOHCEGtLFmBGQiQdqpVL87tOWo9sZuWyHrdSy4f39QigmZEYiBCYCRGAhIszFhwvkvffDqh8sbikWlOV6pSwykQWt2fyBiRQLg3oVLYPgo2VAErYFYAmulLxjVdnZnW0/VrmjX2QL/6SD2sN6yeCkm0ES3TB02QArSIIhAExIr2zba+t/2yabn312GiLtMHKfYAE1Sa0narlQ7G3P3zBnnpnFEtgQQUS6X++Wvbvzko3XGgFatlXuqkLTwahYImJgZc2bZop9d+wc36dQ3KwTTCE7oJU+F37I2VlGOMc6aQHQcVZYmGDl2FBADIyjdypogUi8bCwVCBW4c5NtrLXJDuIuBWLadgwe3XDy2XZWVwYiaQQE5JI38ppaBP/znm4YUmrhYyP/5Hy/O3+SYE8YrpwqagEAy2jaNa8hdP26QrUgIAcxKGCBlmLeAzKD9CFwTGtIZP+n0p95Y88XmfM7UmiLrRgQErJkVgWZQbArZ3VN+acMOGDKElQIiJHIU9c/Ji0a3K0cbgDKtbhEz+DcCwXL06KbCr6cM0lUbmUFp1sTEwMzDRl70wns9vb3n7zPlsEmD7d6KY9lOpTK7ozT/8KnDG/OO0lKk5NQ5gOlLpUWL3rrpT/fKISO0bQGR+yJSjurazloHdl8rZbS2PvnsK/987t/FQkEpDf7gBU4b84GclgLxGQ4iQlXAzNJOZpZCCNOsoe+OApQLV3whmHOm4RBpIqVdJcNaK6210lop7wf3SymttAY/MkaO+MsS0bLUVZMGz2gy7bIyNICjURPZdm7wwMe+sO5/9a3GUnHJ8hU/e+5/cvpMEhIJgRi0BkWS6NapQ1tMqYhrORvNzIzMSAyaSbt6CJgYgElps7Fhdevg0+9foJUS0qDYRAHNqAiUBkWgKCfEuu1dq0lCIUdaA4MEwYp2LxodOaPqKGJWRJpIEwffiiK/VJq0pqASRQqs2OqbY9uPGdzo9NrSxTcIyCazWHi/Kn+3YFFbc+OTR+/+1MHj/rTXqH8evstLR+w6vqlQcbTw8Nd0ojoKoWz7+xdfaRv9sFBgJmBC7YDjlHLwiwu+Kt2FYPLCFGIwS5f88g9W1ZJShIoA3fat0AcbLFRvLfrSdV5MQwBGzhhUzKOjkAGU0kobprloc3Xuoy8IpoZisVQslkrFnBQoRKnUUEr9aiiVSqVcLqe1TlZwI4Bmzglx2/ShJVbkaNQKtAZNXKmK9vYfv/TB52vX/+iB+V0jJ4iWZlYaNYAiodjpqfy4c8CBHc1lR8tQfQNocmUCtGatJbFW2pUt0IzM5NiFgW2vqPwPHn4hnzMoRitQCpRGItAalJbAm3srvTInhQTtfgiBo8c05I1crrFULBULJXcpioXod+yXxXzO4NCIDU30hxkjWk1BDnsGUTNVHdkx8PpFK5esXJczjGM6O87dZdgRI9qBoaK0iMb80To81FoX8vmb//S3N157z+gYRLYNREBaoOBtW8869qArfvSto/eextu6JABqQiKtHaOl5b23P/rrHfe50EOY7OSNsMJ6xQG1mMFPQidCuFD4yAhac6lYuPK5//7igy25tv5aaY8jyFqvXr2bs33GwP7FUkPvtu2nHjD9kF3HXTz36u5yxTAMAEQUgIIBpBCtAwZMmTh67znThwwdykyVStWQMpZ1UgylgnnjB2t+/L9VuUJOuzCS1iil7u4esXLZGlkQk6ewb84loOM4u/XPv37IZOlbAWIo5o3j7lnwlG7I9WvURAisWA8qmWcNG3Dd0o2GaTD5swiYJQpr6cc3zxn9vUP27K1UTClBqZl/efq99uFmqUAACKxsPa2f+ethjYc99bYxcbzbf1cAa+bJBh3Uu9Xu7QXSwkXzmV1fm4kAAIVwoTEEYRhS9XSfeeismRNHuxgKAyjihoL592VffOPlT3M5Q7vWkFiicLbtOMTcMf/7J1ds5Z5ziZnjwzCI4nO5Tz/7fPo+x5YLHVwsubchEKlqD21vWPzsHQMG9Ptk+YqZR33HQoO9xBMJBq5a7SV495V5rW0DlFIiC3qIzaSJYlRGhCEYTE3xGRH+PBIQCKT1qbuNvn7pZtvRAABu8A7SGDHq3U2b3l2/HXrXdTSKm3ed8L2LLv/LzXdAYysQAUoQEgAAhfspoK32gW2nHXfYRT88e8SI4eVyRUoR5k5K5KrlXDBxyLOrty5Y35vLSUUMQrBmo1haNWKcUWoAQCQKzqdBzp/3GF+SouwoGW0M4lpJIA0CgXFHj/XjsR1Vy7nxo035Yk6Rx6bVrMwxo370nyUT2poPnTapXK3mBCIRKAblp6ccBsXa9X6UdrkixCSAP+iufrBmB1gVH2RDD7ti9lGYoHRbADD0Vo4BQ0pk23tsQ2DFUl8f1/H0yi2PfbotZxpKa2DQrM1+jQs+++LuV9/9xv7Ty5WKgelN1CFEFkcAIcWFc3/ZXZbGgAZSdkBD5+6tv77xwo6Otq6u7skTx37/rBOu/93dpoubABOQLBU2rl17zQ1/+cNvLrdtR0iMN3SOdeqOVm24gJ3HbkhlvoU5PYyoiUqF3MUvvP/bt9YXWhptb5wfM4MUIIXm3u4Pzjnqvw8/cPqZP85PmM7uCrq8Bqwh/IxCWTZsXDuoo/n2W6760uEHurIVzpMTQ84QK3uq0596r1sDGpIBmQgRUBhe52OBwCCBbMu6dPdhv9xjZLnqGL518DXWC09VzXxLg9YAiAq4KPH9wyeNaiodMP/9Vzb25guGIjdFzEIIXXFaV3/22neOGDeko7era68/PfVe20ijoegmGBzbmTYgd21n85eeeFuOG+Uz0F3EE91wwR1FheTz4JjQzZMQg0BglsC27ezV0bDgiF1ZU2zBDSE2VuwZj77zRZWEQAZmUgKAFbWt+/ytC0/oGNBPOQ5ml+oHFIYHH3r0K1/9oTFysvZfJQWqrduOOnLOM/fcVLEsBJBSdvf0Tjv4jLUbt2MhR6yBGRlRa6Oy440X7tt11ylWpSqDACGAurMZex79kOPNXqOc2KASmlkiWra6cp+J+w1vqnb1mgCoXVdAC+VUu3puPnKm8fnHZ513qRw8VtmOsm2ltFJauV+OpRxbaSZikTNzo8ZuoIZjvvKDJ556rlQqElGY8S4FWkqP7tfw2+kjdNUR2tUQAATsKFYExOyQUNqu2NNbS5dNHW5ZSiYbXAOAYtAMREwaiECT7SjB9OA+Y0eVpF2xBWnQhACsyCyYmwYMOunWp7bt2GGYJoEAEIIYSLPWoAmDxnHkervuZzJrUo7WjlaOoy2lbKWqjra1dlhZjrYcbStVddhWjq2Q6IYZo3LCE7/gWwDYWg9tLv5m1giqWMjMmpCQCaVpbJQNlzz6kiEF1a2MIGbTNDdv3nTx3Guw3yAGAE9ciG2rsaXwu1/8iPzZO47jDOjf76qLzqaeHQjsRcFEaAirqn72i98JBBQpfQAzO2JArbktZpf4JXKLxDkpHj92jy91tlg7epRNxCiZrO1dJ4wd+O0xrcec+UNLtGChyH4mxMMbiFzNZQgG0kzk2LZRKnL/wWdecPXSj5bl8vkg1EdfjitV55uTh5wyptXurRoAoIgVARFqAq1RK9K6IOjWOaNzAokZU7rKEjCzZtIEmlEzKCUFEvGQhvy9e4/Jac0KBAMoZs2qaueaS+9T6aw7/5nPmWahBIrYVuAQaAIib2yaJtauYAEQAgFoEJpAkyAQzMggGNGFzYiDplWO5ahy5cbZo2cObKnYWkbLiogBmLt7q1+dPOTE8W1Ob1X67Y60pcyOwfd8tHn+4qWlQkERx7j9wTcRmaZ5xS9/u2blNtmvP2kbiIGUEKi3b/vZBd8YP3aUy5XSWgNgtWqdccoxs+fsqrbtkCCBCFhrpYy2tueee+XxJ/5ZKBS11pkFCBh0bw719EIUgFynQDaK5qNAcJRuMY2njp9x6+GTZgwsFcCxLXtka+NdR+xxznk/fv9//4XGRlXuIbtK1aouV6i3W/d2U6WXyr26p8fZ3sV+Ak4rxyg1dFf44l/eLP1+S7HiT0fRH/YeM7TRtCu2weQ5TFqz0kKTrtpXTB85fWC/sq1SyVXoGkXNSIAaQDE4BAwCobvq7DV0wB/3HqMqDmoAx3XFQNtgjh795BfqJ/c9VyANlsVKs/JlSANpV8gAFIOLbxFrx1GWpR2lLEdZlrZtZdvKqmjb8iAW5SDQbm0NDx2+y/cmD6nYSkZZrIoonzNKxWJTQwmFvHHfMe0FoW2F2r0EsiZsG3zhU4t6yxUpBYcpoTV+sy6VSq+99vpfb71PDhpGlu1qa8GotnftMnXMBWefUrWqiFgsFkulkmsrhJS/+vkPkBVr5RUfELPWWChdctXve7p7DMOINbeNNXHhEBHeJUF4UWE6GzVapxCu7kXEfN5Ujvqsq7Kl4gxpKgzJy4WL380ZJjGwi6UzMJPfTBEZqFx1nv7Xq3/++xNs5iFnMAoEAQiwY9ubT/9lxh5TK9WqFCLMplUMpZyxYM2Wo558x3bYLJhu0kMKdGy91/CWF46eojUlaYmej3XXv57qMnJNDa461MRF1O+eNG1cS6miNDOUirkfvfTR7xevzTcWFQUjhlAIUJ9+nENwRo5DId0oWzl6j47C1WNbjnrkLWPMKDdnLBEcWx84tOGyWWNs7QL8HCmu8Uv1WvPG5H6lnCErlpIYiaoYoJDPr1q38ZV3lvb2lqeN75y12+RbF392zrMfmQ15Iq9xjyGFvXLVFfuM+MUpB0e4U0HLSWZE3PvAoxa9t1a2D9LK8TUZUs/25x/78yEH7VMul0ul0sJF7773wbIhg9uP2G8Wocjn82ec+/N7733a6N+slQNMwCBNQ61bf/U1P/z5JReUy2XDMPogY4UCQyNzUh5E2qxFYkkhgLlctSWK8f0aoD+QJpt4v71n98kAO2z/PaeOGXrOJb+X+VbNzMCGkE7V/se/Xpmxx1Qm4mhBnSGgYqtDhrW+durs7z3//purt4IwQApHCFDqsl0nmlI4SkvE1NopJAaHQDOTj5GKWm5BCKxazm/3Hrd0c+/8FV35kum4GQXSDGQM7XRcs8t+qY4m1JQzTTBMUBrchJpAVjwoZx4wZAAoB0R2fSqDrXTZ8jgagVQRczGf++09T177xOvboAACpf3S1ace8LPTj/jbW2sWbeg184YmAmLtKNne8esXl54ya+LEEYMt2w5/lKuubrr5L4sWfmh0TtTaQUBgElKoTRvOOPOEQw7ap1yplEqluTfccd1DL0JTP3Cqh89866Ervp3L56+48FtPPjW/t2qh9JoVk6NES8uvb7j1tJOPGTVqpG3bUspwH6s4dzk0BVckq6o529UP4lkEMBABuKp0xdY2sQCoVCqVSqVcrpTLFe/nSqXs/s/7p3KlWv3OWafuvutYtWOHYAJSrG0wjPc//ixWsMq1UmO2NLVsXv3YwaP+ffaB399nwoEjW2YNajx18qA9B/dzHA8nTPQ6Y0Bgz2wxakZNrAg0hVu8uXVidx++y5jmnFVxpCJQCoiBBLMQwnCtFCqF2k0vQltjIUeOdj+HgAlA6w07erV2eh0/xW6pSpBrt1XZVhVbVRztZhcAI1VcxULhqr8+dPHt/9w2fqY5a9/crP1o5v5zX1j62ap1Nxw0DioV359j1iyErBRbf/TQy8jkFaqhWy1A+Xz+888+/8XVN8r2oeTlNwkRyLI6hg+57rIfWZZdLOSfmv/idQ++KPc7PLfvwflDj5u/nq5/cD6yHju28+ILvkHbt0shXd+USIu82b2l++dX/ka6mTEOZsKmNBgPKyARp2FxqIIUE+WXiSZYAkD64imEkEIYUhhSuF9SCOn+TwghpTQkEzFgZ3sDVMrIjEQuFrZpWw8Ao0BMRKha67xp3n7vQ7Nm7PvBg/eeX9o+b//OhV+e/uDhk4WyNRGm1ViHWPoulKVBESoCR4WLNIUAW+mOhvx9R04qaoccQmLUDO63YnQ0agYNoDQobVWqw/s3D2sw2VJIzFqTrQDF++u2b+2u5E0p3EyzQBGknL3fCIHR6bKIWlOpWHz+9beueHKRud+RsqlZKaVsyzAEDO1c2q32GzP4uKlDVG9FardQgLXjmM3Nzy/beucL/ysU8ppqCR0p5UVzr9q+1cZiA5MDpJm0YKDtW6+Ze/7QoYMty0IUz7zxgRg3Rba2O0JqYjly5CvbNBDbtv3Dc88cPXGE6uoWfhpbO0q2tj/8wJP/+te/i6WiC62l5kAjc+c4NK8wZdhpKBhmZop81f6q/W8O/ZWSr9ZaKy2kZK0+WrERCo1M7OV3lVPKy1i/dwzPfQdobB6wbp39/Z/+buJeJ4yZefSkmUeM2uWgU7/+A9OQgVwlk56syZcq7f9MwZTrIPndW3X2HNb6p0PG696KJDf3TKCJtQ7kkpUGpR3LaW1p3HvUQKxYQhMqzUqbwJu2Ve5d/KlhmI7Syam+EOpWV4sBicyc+cWWbd/6zT04ehcqFElrt4ecdnRbS3Ha0AGk9dzZnSZpchRqBu1SdxzROnju80vXbdmeNw1iVkoVi8V5Tzw975H5xqBhAZ3VMKTavnW/g2ad9dUTK9WqW4qYH9BG/drcyhVpCCqU+g0bDAIs22lqarrmsh+wVUYUwFRDy43iTy+73qpWpZ8mCSvdZLsJDtDYlHgwlIHURCiwUCyGvgvBz8FX7K/F6D+5YUghn//dTX/68L1PZEMDaw1MyIS2M2HMSLd4MgWI8pSoxFJTYehwbB2+tSI/Wdu1YtXWNVvKIuTARkMVdFMbYGsPLHDtoKbIFGr2mKjlivWN3Uf9aOZQe1uPBGCtmAhdqfIkUgGREBIAvjJlOPd0kw86kGLZ1HLl8+8v/GB5U0NJCEnMxKyJ/ePG7m/I+41XpWwaxvd/c9dqHGB0DCTb8ihyQpLl/GzPzqEtxbJlz+psP2xsqy470k1cEpAGaRobt6vb/v22NAylyczltm/fduHFv8CGAQwaWHusc6XyReN31/xUSMlEKJC0Pu3APaCYryrgnGkxMzjf3KOTAQ1DVqvVU046+oDD9lFbtkq3ngpAay1b+r/95jt/ue1uN4Ho17gjZPXKQjCi/hOGh/uxj4sUi0Xbtpcu/dBxdLi1INcc/PjwvXDlCyK6aY2urp77Hnjkz3c8KvoPY2UzuIYcGdTBe8+I0XVqKaZQjYciAGGKhryUQot8rljkRK/HcA7ZTcKB0kwUDEyp9WfEWo2TEFi17N8cOuWDjV3Pf7oj11jwmBr+kAAUEthBot6qfcj4IXOGNC7cWjFLeddAIcCOXNORv5t3zZHTvnrwnv36N4XcjGQxM/T2lhsaSn+9d97Dry41DzxGEyMI0CQYnN7ywRNaL5gzzrK1EEgMVxw06R9LFtgW502piRnAIHIMY4tDRJoBTNP8wQ/nrvh0ozF0pFYOIDJrKQy1edNPfn7u9N136+3tNQwDgC3b3ntS531fE79YuGJdxenoV7p09qjjJwyu2koiKk2I+Otf/Gjvl/9LbhMAYGBirUVT2zXX3/LlE4/uGNjhJRBjzYUjvRrA4NROJr7DRcyFQuGNN/77/e//9N2lyzV5Y3PcygIvAvLyYm5IIPz0NbmbwSzQq3IhUgxKYPtQkAZrzcBSAPV0j5/UedD+ezqOI106L6ewLbA2vx2YiACIovwITplbwS44rjx+PTL4USGHm2S4qpuYGfCek2bsddsrn+6wDFPqWpDKQAyK2FGKuKGQv/HYGfve9QYoFkREzFob0tjRPup7j7/7q3++M7mtsYgMTCBM0ooFoTCQmZUjWNvVyl/mfnvz1h0X3vas3G0fEgiaAVkgs6XaiuK2Y3Z3b9FAtGxn5sjWx799wNfueqV3azfkCgBcsR0oGqfP2UUI2dRQuviii+/5+4PGwPHKdlwrJoVQO7rGTx3zsx9/17IsIYQ/rRbLlnP6rsOPHj9oY6/d0ZhvKearlnJ9KilFpVKZOWPat791yp9uvMtob1NKASBpMgqFTWvXXnntjbf+8TeO47AQ6FVdc2xENAYaK72qmpmZTcNYu3bt8SeftXHtFuzXxiAgaH6HAnyqYbT1H3iFqf6AGWYGIAApDENIqV0LwYxAAg3d3XXFjy9raGgol8uGlMkeZX48QQCETB51nQGIMDGeJU6wd2kDym8cyV4JUJJ65pIZbaU6Gov3n7T7Abe+bHNeSOkR7tw6CsVSmHlDVKr2niM7rj144sVPvptvKjExAxKxkSuI8ZPXdHev2bIdlAJGAAWkAciVXJASuqtfP2TKiCEd+575s55BE8yBg5VVdS2zQLQr1Vu+vOeo1uZyxZISmThnGOs2bDY/WfT69w64d8mmBR+v7Slb49qa5h6+6+yRbcs++eSSuVfPe+xZo3WE1hoEMinBgIxg99x8/SVNTU09vb1SCGZyVbUQ2FNVeSnHDWhQBDX4w+/I4jjO5T8577F5z23a0ivyJmkG1tq2REv7HXc9cvaZp86aNT0oyYwhiAFJxqcmR7tfou9dSsN4/ImnN65dl+sYDsJA4dbACZQmSgOFQIEoJEgTDRPdFwjp/SAlCokChBAoTGEYjEJrBkbQhKRNYThrVp/xjWNP//LR1UrNMYwEEAiRgkv2PWt2syW6ryZkCBo8NhWRS+5Lb17NTMwGYm/VmjVy4B+P2033lCUTaMVag9ZALrhAzCwllivWRXuN/sFeI6ztPQKldPUfkbZso1Awhw7NdY7KjRpljhhhdI4yR43LjRydHzXGGDhs4qzJd517/K9vn/fqesqNHqsrZWRA4hyg3VM5b7+xp+4+2pUq14+xHWfwoI5P12w8+4TTdt+46E+zWx47fsIfDhhS/fB/Z3/r/D32PHDeY8/JthEaJTCjJuEmm79YdcUVPzrs0IN6e3sNn0Pruy5oSMEAFUWKOFZljkI4tjNw0MAr5p5HPTsEIJAGBgYUUqiyfd1vbslMz4SqxIzUVtUc2tDechnBcF3P0G4Tej2TBQMhhRvWeDXWTMpvH8jAgjUFI6GEaWjN9rrVJ37liNtuusqybMDI+LWw545BcYBbJOPqHgIgYqL4CGQM9+9hr3w7PKqGUjrqsD8miAFMKcuV6ll7jl+8evMfX1ye69+siEEgMgvlNMqcaUiltECsOnTT8TNbGktXz/8QhJHLSWYmRGYgpQFUCDjRHuxkVx8480tLPvj40jvnGzMOUCBQKxQsDWFt7zlgl4E3HrOHZdlC1Jw/KYRl2T84/zsrV288/ayzwRzY3K+5p3s7VR0AAxoHyNai1gpRohAS0bGrtGP7j396/i/m/tCtmveHEXhNH2NjFpIYjZTCsqxvfv20v9758DuLPjKam5RSwESkhZn7eNmnyrE9wn64nxS7dsv7Q2Q2NQNAIZj5sEMPhmLR6e01JUqBUgpDoiFASpBCSAESWQIbPmBjiKBiQghE6RUOgJQggZE12bbastl0eudecf7Df/8DomBmESQlskaJMBluhYJAKYQEMAL2RWow63tmhkBDCANRAhgoEL2KYYx1aQxJmRBYtawbj5t5wIQ2e0fFlNIgFkRUqZ6+2zDDMFwKDABULHXVoVOf+vqek/tLu7vXqTqaABElsrsUUnglJDlDqu7eK4+aMm1Q01cu+wuPniJLJUkOCKFsx9q8fZ8JbQ9/bS8BzMxuBjfU4BQrlcoNv7rsL3c83Ng8oGvTZjIHmO2dhYHDzFKDEIYhJAKRZTmbN7fk6OY/XX3Dry4vVyp+Os3tjYRuwgcz56wGHeGEJsrl8zde/3MApSzLNIUhwTQkOd0zZ+xu5vLKjw2j7w+aPqARbT/np8oQ2V/f3aft9rc/X3/RRdds27Il2k8tOnYIRKRSIOg0LfxLsgAgKBaGDR92xIFHnvfNU3ffbRe3O5eIhpSYPupSKfsLtaUIqgzMYOSgskPZI1AIZoWJFjzuc1QsS/VUlCHBsUBIIFRFNKRwyZ+hko7ANXQrTEAzI+KDX9//mL+99L9Pt4JEADhjnzHn7T3JsmwpMFjJcsU6ZmrngeMG3//WigfeXb14Q0932QJH+Y04NKAAKaG7e/+JbXMPmvyVC65ZsqYKs1r1th1gSDDkyAEN3zx0/IUHTcpLaStdy6aHgWghqtXqd8/68n6zp/z+ljufnP/axtWrHAXAAtgBFJDPDR7SfuLZJ17wvbPGjR1drlSkQIyN+OTYiPGgyyEEg0nd1xlCVCqVA/ab85c/X33hhVf2btkBAAqcybtNueqyC5VSQohUpC5ohhzpj5VqEN3AcMWqNW++sai3UkVA9iK+FF5EGLlw650RRfBQxUK+c8SwiRPGDBjQDwA8fl+Q5I72sMdQx0BDyjVr1/77P2+yNJlsRIFCOlZlyi4TZs/a3S2wTLafNIRY9Nm69zdXpGlopaSUjtYDm4tHjh+cOjwxjMS47Pu8aZRt9fh7K9d1VXcd0v/IiUNsrYl9dR8q1zCFMHM5ILVya8/Hm3vX7+i1NbuglUAppXRs6/hdRzaAvvupf+eHdmKuyAi5nDm8pbDHsAH9GkqObSuioEIw2aUEmF0GHwCsX79h0eJ3P/ls9YYvNiM7Q4cMnTR+zO67TW7vaAfgcrliSAnpg8whWUSb1fBDExeLhY8+Xv744//YsPmLieMmnHrKsf37tbhhpi9JQftA91i681QwIVhhfCLIbhIVCnn8/2rbl/xSStm2g+hGAvEOcZA2ZsOFakRaIxSrWkWByQkXXku+nOFRgUOmzracPhumoNezCaRAM+fWJnG1avsTtVPKejWxQCyYEmRylgwDCO3YmiGXy8UqD5SjbEU1Fg1GuvrGjj2x14hBShmvVlfKsm2BKLJ7e0QI6KmdoaKN/Nw+b8FdaKUcV10lJtoHTf28TwlGnmDd3DMRu+15MFu/cbKXawIaRESRZubD73CxXU+TMQspBSIBk194D6EBNlKIMP8EEv3viCPDLxDACG1hLImU7CnFzJq9AsDgIFC4xCgylJfR42GniIWbK3RxVyGEy5kQ6IVRmJbKzVomclNsNdgI0F9Z6HO4SVpNBEf7H9SaazMwubghAIIUInvvAq8VARirluV/QjjCjw+iqd+Amus2gIv1oYz1FKzZfETSWkjpn+layFatVqVwW7ehYUi3AQsHgs4ppQSuvXezY8pxEu0r0utb4vuKYJo5JnKUCr8ytTdpTYlaVspQDH8/pJSIqJTiaOvYeh0V67Cg0v4R0x62j16P4UZoYTeV/w83Ewo52Yifudrws9AHYby9G0fHVdSTqtA8i9SaEg9wRdRaF4tFy7Iefvjhl19+edWqVf3795s5a9Zxxxw7fMSISqUiEA3D+M53vvv222/ffPPNc+bM8WC6cIWIL1VCCMuqnnLKl9euXXfvvfeOHz++Wq26HYIhRGLGEEUu3GKaifKFwupVq0497bTmpqb773+gpV+LUgoADMN48T8vrl6zWkpJRFopQJRSCiG0VtOm7T5p0iQv6RFaDdI6Xyi8+eab559//uRJk+64805ODoMNQcwcGh+UHF/DiSVNApW14VGJAUxYZ74SxAfZhU8+cLwbdIpsMQOCURtAH7t2uCk3x0r12W+WXXvCWMcHTKeuhjisUHsIRC8j+f7773/3u99ZuPCN4N13333PVVdedcMNvz3jjDMr5TIzL168+J133tm6datrK13T6bJyiMhRylXXiEiaXnvt9c2bN1cqFXLp6ohKKSFQoE83Z3YbjRiGQUSkNYSCiUq1+sYbbzQ0NCitvMsxG4Zx0cUXLV68OPXU/mzu3GuvuaZSLhumKaVkIq21lNJtu7J129a33nqru6vLjfxJa2ICQCklAhJpYhYCmVhIgShcAQ07l5rIvQfX44GgB0QCpoSo4xLrVxU7WpG0CYamEjOkGq4U3eH1afKr2SK3hB5rmRGSrZi5RqDDoKl5yBGMQ0Kx7ErQAKc2kds/i0Rk5nKfffbZYYcdtmHDhim7TDnvvHPHjhu3bdu2+++//8knnzzzzK83NDSeeOKJjmOXSiUhhCGNQGrdQKlSLucLhXw+z8yVSsXNjbU0N2/dutUVOy0lM7svrlaqQgo3dV8sFgGgWq0WCvnIgHAGZjZNo6m5KdBzQkit9AUXXLBs+bJSsbRhw4Y//vGPOdM897xzW1vbLMs64ID9Hcdpam4GAEc5uVwBAGzbJkcDgBRSCNHU3IRCKMfJ5/Ou0Lh1TIViMbzetu14d1utCoFutbR7t7ZtA3DRffBKxVPbmGixEKrTwkS3/aBJX+bII47/EpNjiGpJN464aFa1Gvm2rKzfVEN/Zv0m9l2tWlXLCv7zPs2Kv6tSLmutjz32WAD40pFHbt+2nUNf3zv/fCHE9OnTq9UqEc2ePRsAnv3Hs8xc7u3Vmu68886DDz54xIgR06ZNu+SSS1atWsXM1Uply5Ytw4cNk1IuXLjw0ksvHTdu3MSJE88995zPPvuMmcvlcqVSYeYFC/51/PHHjxs7duaMGQ88cP8jjzxy8skn33777US0dOlSKWV7W/uGDRuUUpVKpVqtViuV4N5WrlyJiI2NjZs2bQp+SUR333334YcfPmbM6L3mzP7972+slMvVapWZ58+fDwC77bqre+n33nvvjDPOOPa4Y+fPn6+UuuGG35500kkLFiy4++6/T58+fcqUKWeeeeZHH31ERBX/bl966aVvfOMbEydMGDdu7Ne+9tXXXn2Vmd3Pr1ar7toGu1aNb0cf+2X1+YKoeCSFwdtoy4LqTlzJ8m86fqPezcevXa29oHY996ZqT+7fZblcdpdYStnY2Pjp8k+Zecf27Y7jBFu1fv36LVu2OI6jtZozZzYA/OMfzzCzVbW+dsbX3GPT0d7u/jB8+LBF//sfab1127bhw4ebpjli5EgAaGludl8wZMiQJUuW2LbNzH+/+243zDEMw/WK2tvbAeCcc85h5iVLlggh2ttdwXIqlbL7wOXe3q6uLsdxFi9ejABNjY3Lli2zLGvHjh2WZX3zm99yLzRyxAj3hwMPPHDLli3M/OyzzwLAbrvtqrVeu2bNpEmTAODkk07u6upi5mOOORoARowYAQCm339l5MgRq1evceXyrrvuQiEAIJ/LuarLMORf//pX95zUEw7vVMeEIC4l1bSfd0YE/ddbgWCJ1GROsnwbA3pnKF2Nfhl+dgY4PH6D4yqWaxNc3n77ba31brvt1jmqs1wulxoaVqxcMW/eY489+ui8efPeeuutl156acWKzwPGNGkGgD/c/Id777l3xIjhzz777LLly5cvX/6lo760evWac849VxMBsxTCcRyrWp0/f/7yTz9dvHjxXnP2Wrdu3cUXXWya5rJly88/7zxmvuCCC1asWLF23brLL79806ZNhmH4cV9sAqZnaoSU7mwI0zRc4rthGFLK5ubm++677/bb/zZz5qyVK1euWLly8+bNxx1//H/+85/f/PrXfikfSGlUq9XTvnLa0qVLTzrppEcefSSfywFAQ0OjlHLHju2PPfbYunXrnn7m6cGDB69cuerBBx7I5/NLP1x67rnnMtHcn81duWrV6tWrL7/8cqX09773vY+WfpTP57WbY49EZ5BK204ZMRbCgOLdQxHq5P0iU4DR7/bHLBL9bNMAD68c3yupj4HU8etFfMbaQCms5U1SxuSVy2XXprj+tWEYjz78yEknnXzyl7980kknHXXUUSeeeOLChW8IIdzI35318OCDDwghLr/siiOPPNI0zTFjxtz+t9s7OjoWLVq0ePHipsZG965uuOGGww47rFQq7bHHHr///Y2mab7w7xc2b97y8ssv9fT0zJ49+/e///3QoUM72tuvvPLKY445WiklUEBtzFPmkOYgQieX76fUPXffLYSYveesDz/88J677/7vf/87Z/ZsRLz3vvu01q4eYuazz/7mK6+8esjBB999992WVbUdx72c1vrb3/7OiSeeWCqVjj7q6K985SuI+NHHSwHg4UcerlQqXzryyGuuvaa9vb1fv35XXnnlEUccbtv2Y489KoQgTVlygwyQMU6PQyXOiGkSE53IGIdFguiNOVyNY9R2NzpEmWPgFsSHVCWnf0emqaT0RcJwa+bY/Xd2djLzxx991NtbzuVylmUdfsQRzS0thmForS+//LItW7YOHjw4eH3ONIlow8aNRLTrrlMdxwGA3p6eQYMGjR83btOmTStXrpwxfbrWChBmzpzpOA4C2JY1srOzX7+WTZs2b9u2ddOmTYg4c8YMIurt7QWAYrG4xx7Tn376GW8SU3guJWT1uPDcfGkY5XJ5/Yb1RHTzLbfcfEuEXtLd3d3T3e02eliy5P23337b/aVXNhz6Gjx4sNbacWyt821tbczsFkWuW7cOEWfOmqW1rlTKzFAqlWbOnDV//vOrVq+G1MA8MVWktrlhGCjcBjfegi+OQQQzZiKNQEKcXa+YIgZCc6jAC7KHaqYjpTVTl8gYZANrQkql1KxZs4YPH75i5cqbb/5DoVCQUu6+++7nnXfed77znSFDh2zf0TVi+PAZM2YSkbvljuMgYntbmxDis88/N02TiBoaGzdu3LD80+XM3NnZScwuaWTphx+apulm7NetW7d9+45CPt+/f//m5mZm/uCDD4QQhUIhn88bhvHBBx+4XLRURZ7ZBl0IZs7lci0t/YQQN9100+o1q997772PP/545cqVK1eueOutxU3NzS6C7TjqlFNO2Xfffd/8739/8P0f5HK5cCDPzC76ECRt3Jtpb29z3T4ppWnmcrmclHLJkiXMPLCjA9KHM6YjBDGwKjRJm+sN54r1lkmdueTLqqjBrBjr7Liza5qsLKuXnMKUXuS2ZQ0YMODnP/85AMydO/eSSy5Zv359uVz+4osvbrvttq999WvKcS686KJ+/VqUcqQ0hBCaCBFPOOFEIrr00ktfffVV0zTXrV/33e+es2HDxmnTpu2xxx6WZUlpIuKFF/548eLFQuAnn3xy4YUXOo6z9977tLW1zZkzRwjx4ksvXXPNLy3LItI333zz44/PE0Jo8mghbukaZPejcrEM14oZhnHCCScQ0QMPPGBVralTp44dN/aRRx754Q9/NO+xeW4aRwjR2dl53333/fa3v83n83fcecfj8+Y1NTUFnyZCXYoQQQjholbHHnMsIj791FO3uLoQ4ZZbbn7mmWcE4vEnnOB+MsZ75kHcuEXTXzF8MfYnZ/ncUcSBY+kF12iFAofMmMIPZb3IopoaZ2Z+W7EPj32sG8C7saErWwBQyOfGjxs3YMAA96/nnHOO0rpcLhPR9OnTAeCJJ55g5q6urkMPPdR9zbixYxsbGgCgtbX19ddf11pv3rTJ/YSW5iYAGD16tOvi9O/f/6233rZtm4guu+wy9+2DBg0aOHAgAHR0tAPAOd/1okIAaGxoCMMN7s27N/zOO++4dN7ly5ZprauVSnd39/777+9+5h577D5kyBD35wULFjDz0089BQDjxo3r7elh5ssvvxwA+rU0v/fee8x83HHHAcB1113HzNu3b2fmq666CgDOPOMMZibSV199dRBvjh7V6f78q1/9yo0KwyuaFtRb6dBDpRLZi+hXaPczcYfI5byXWfKyn/+c06biYs3zjg9LxowR5/XZAkl9Fh5S7frshx122H777lupVrZs2bpu3bpcLrfXXntde+21l1xyieNPCVy4cCEinvLlU4YNHwYAp556SmNj4/r161etWtXS0nLcccfdcfvtrroiooULFw4ZMuSOO+/cvGXzkiXvNzc3H3HEEXfccce0adNsyyLmQw89dNCgQWvXrvli4xcNDQ3XXnvtIYcctnr1qoMOPHD2nDldXTtee/XVcePGnXraaYV8nolC/gVIKbu6ul5//fXRY8Z85fTTGxsblVL5fP6kE0+UhrFx48Zly5aZpnnCCSc89NBDs2bNYuYtmzcvXrx4l112Oenkk4lon332+eSTj6tVq1wuH3rooe+//353d/fhhx++6667Orady+WWL1+2YsXKffbdd7/99iuXK4cccsjo0aM3btz4+coV5d7ynrNnX3/99eecc45lVV1HrTZaG4M2MOn7VcvCJbLK8VnMIeJatKV7nfHm6E3/qk/JqvPFkEnwgKiLB3UtKfpNyV14pqurq6urq1AotLW1AUC1UkEh3JhUCIHC7bvu0s+laZrVanXHju2FQrGlpcUDo6VkIsM0gwTIF5u+kEK2trbWXsDMzIVCwbKs7du3Nzc1F0tFtzmqWw3oJkxQCK11FoFbmgZCLakcvqVt27aVSiXvlsplISX6+RnS2n27mctprRxHSSGEYbgsabclkxtf++kpHawPEW3avBmYOzo6ENFbnNCM2ez8R4r5q5+GT3WpOW1cOUYuifWIfn1KGKcJUn2+RyyBHQwxC0IXV1xypikNg4hcGDNwY6OxLvrdSbWLKhGRY9u19Bl6nER3EI6ZMwHYtuxwfs1NNRrSMExDa+04jlcKj7EeTpjSWzpa3IwRypQ2DNPwH6FG74l0FgbyOwmCzxHKpiX5rC+tpRCmmQME15obhsG1gUj1SA1cN7YN+cNxAWX0mzNn0BwiiAT7fCyIslkSxMJavjmLMxTjh4RfiVk1jVFaXzx171Y3RJlbGCa2RxQ4stc3FpJsofAkM7dDNca4G740QFSgEg1b41SirLMbi8gwZD0xs8FdFh4JqROsgzQ51qEwxSas1u65HvclS+bi41sTsEX85d68wjRacCodDLPUVYJUWe/+Q1OEIU2wsshS6Qy4xOmpR+rIYIBA9J/qa+iUOUHZyf8gLVtnsmQmGSvDYGXtFGNk4G+6xajLA6yzvztpzVyZFpyFVMUOQwwdrWsWMa1gJv31GCNFIDBnPVVqg7jYjnJGFgFijQbCnMnkYLRo61gvT4Je8qDWgyfWnjkL8klbMswukoFEW8OgQiVS/Ylpjg7HSbBZEyWyBnKFmypihoOV3O6YVHmdomPYVR1QNGtdEPtINXIqlBquSA7b50gpEtZxISM7GhTN1eaZhVeWU3R7YoEwJHXJCYY+lTC9/iL+4rSAOunwht2SNEse3CpmeLVpTnTs89M+NFU+MNsEQRobNglMoq8tBEbxLk4TBc7WjQgpHRMgcfIwgyAdadPAwMlNSh1HC6nOFCexXYxnPHdKB2OonrMm7FxjwKXnG2pnAlMXgesezlqdLdaayNWy9UlyOEdsb7R2PLr9nJ04zvCrMLWsJ/SBmPbhwVg59ApWfQ3LsfZZgQgk2vwlUV2AnfoNx5AIDGvoCOkxOeMuggiHFFX0Shj2JaLby+D3E0iPtMOzi/1qjRq5w+ugg+HV4Pit+k8RnsMUZdhiJvYT+azYeao9acKT4yz6AXOyP2OMlMlpnNJ0PzXaRddjpsSc4xr2ySJuUELqnWsnAzBeuwKceBJMaHiMJpdS7Gxts0Oi4FfCoq8pYuRUjKcXcGfWOaD6QDD1NC5ZoUYBgbEO1zb5sJzHtIVa6ZHbyMWbBuWtGadkVzB+ABgi5rV28Dhdl6SIQlrfa05fjDTdiv831ZAZQjLXusDHTGGfcFkdaCTWYotrSoM5YblSXS5fhjhEKfJ0RdjLwRqIFXTgjMCwIaOBEcwsedGo842YusiMPmQW9fqjsDNH4J+Qmx/L8iaURsyaRINcTrfdUV3IwS1m0ntiqiupxDAhc5wUucQQ19jkwEitW2rgkMS+oM7wllAnmJjPi5zpJsc/MIjFOGbyMiNKDvhDnOHt+65AbA4Ch/CwGKu71nUurBYxNF4Nw/1v2I9hGTAaFXIyN1YXaPCr+zktwMSUgvMssIvToS9Odf9CMofxgBrjQskRSKyutDDWRvd6e8PZ2H8ihZRenVe7Lkb4SpjQGSG3A9nvYIwQNEXBCNUsjpRyxN2LFhmlUnoiDKTUEqjAUQ4RxzgAGwDDMhaEav74sCAyxega9o25oI/wYgZnhDPCWgjzRUOdjBE9/wHrXDJCeIj4vfETyNG1itVTRH1ir60NA1arFkRnuMdlBSHL3tfJTKWWwmFdbC3kMEbgR66LRwfvYY5H2pkIbRJDz3hl7HE4Lf6N0twQ0xD5vlP02Utbp1QTsv2T1EpPTmRxoS9gFrMg0Oz78UwhpjUiw+yoo8/14hB2x6kxeQacgYgcnesTC/uTreECJzc4VRHwJlFYB9HO/VFErW9kGYP+hYlzF6NZRm4Yse9EPCbwSX9jcOekMu1u4xIQq+XnxItTmViZeGTUdsd8NrEzN1ynbVVycTH5XoRUrzMKhiG4IVUEPuA+bLkvOlk4ZCjlG29AkhjSjKnxFyfx+iQslHUDIanFOlmNtHRQRnoIs08pc7haMMNR4RDumGV5U8HSlNtPlNJwKMoUvHPnIEDIUrmqcVUZNFINFYlzHw5HPKjz1wnr4L8Yc8Aj/Ug5BeCIkaejr6+Xs8KkS4sc1oKYjr1Fobs4CMQhbCMlPcopDiNnnAQGhLrYhL/zWOu8mQ2vJ3kQ9V8f8fIRALiOxmJMph046vYlFU8YR07gwllQBUOEuBU2ppAghCQPdaTjY7bTkGY2Yg2lM5MzsaJuDs6rq2xCsBMmkdpQ2MvRUxqIBqYlDdOCO85KR2J2di9kbTiJKQBmAraRppIYgcc45l1gDIeMjxTKHKQTs4kY7QzXZ7spqJvTwLSsHKcluTgBH8QkqU5BXKZ94ZTeQZjWyS0VAAsJaBIwjoMFGMbKMhPmwCnpMkwG2slsJvcpXkk7zvWcHIDYmBbAtMNcg2X8fxBZR5ozkn2cEc3Vk6QYepT2gnRySGLwMMb6fWUhK17HzbizWV/sw70H4gQyjgbOzJw0oDGBSpma7HaoTIGnOMNpi3UB4rpxHNTNRWI8FECoewhjpygLVsSw4xHkSTADII3pgCxHB+rUxcaWGKPYVogxna4DfP2U6j/FaDkImX1QknYkE+CNKnuIkj6SyCFCRhou3QaloI7A2dKA6Wq2xniJ1B9jLUDJaIvGUBtSkhypG0YfUqg7UfAvGQBhLC8GDLFZOpzJ4GFMA0hDfI7MgCXiGkeLazG5h2F0AKOJrDRCdxzbxDjW3Ec4GMpFIGZSbxHSVQtGbdT/Abiqcbg5XZXGLofZqZEg9ZnwazOXK8WUZ7A86sdzoYg7sDDsD+sTdeQJMB3RwT7FCKAPWDKJI0SjNvazxRkWIupH1wxlwq/HujcZm5gC9agmgUqID7XgNBFPveUAM4ydq6idTQvsI0kR7suy8848b1/OMWZ7KXEXKIwL+riB+D8423Wlnut2aa9/Bd45Iiwzxp36BLwZagSOKbuejbBDco1SrAnXvObUffVDP0yisq4bXkNeIEk7gSTzk2uNtDGqcutY//o04pgX0YfrmR2VYxY9FbEeQJqi2zHqAcRsk18ygAmwJ6LewmbeQ9Yx245gKryXnDaWIJtBCkFgJy1FX8mNtH6KGJaheNN5L2XU1/Yn1jPdLDKnGHiAOA6ULQ1Z2RhMU1dxoYzHSEnvn90AS9RJXGW2bOY0VRyOjwJ74dfHxVVCWNOkdQTkFJOWsQ0JxZmVFagf22dh0JCWFUn3ICHCSeI0ij3svCuWlg2slyKLTMvJNGHc1+dg/aIxTmQ+omhteG5EPPpMBfXTyMuxdqxxlAVCO1ofsU3pMhDNZHkNdjHuWiUR81Q2LSazJlHqEtdF1yIhWIYl5f+jFky3JuEOEQltEWQ+4vk7TIgLpuRbIJFDi4FBKVyPxDFNZbVw2BT4hlpEbC1HMEZOGIKMwuYIg4UTHkOy7y9GgM1Eko7T9jUsP2lEifgGe5vAqQaFOaVjagxuTVMiWRk83Ek9xGlNZlPRS4Q49MTxrlAYponGxDAjGotsI0JaXt+/E06rheS+wsagw71IBREgA6qBGq0l0zzFoCmsQQcZKjfGRcG4l5raHYmT3nFogHSWko+C+4lKhNRTHmJJYFYqos/iuWyYiqPT2GIKIxPuQkj1jSABQEZ0c7jkkCMIvg9xJYtDU9I7QejHkJk7FpwKjSS2P1LJhOHhFknpDWkJzLAgHCfmctIuMu8EXJ4IFLIjat45jZLu64RVPaZzNBIykU0U5sxHiOM44RRkooIA+4zgQz4fZJMAMOKjRTR+uAgxSD9gqFYti2MnsC/0PBy+xbQ2c9zkc5RbFGJhpjXBxDRvw//kmAjGLhQCEzDSDrquAUruqb9UyFHnI5OEFAYk6wtrLAjFBEEAE+cxXvmCMQ8B62fGojO9E2mMjAL3eMDk56c5Ewrx+WJcZxFEpNwgpIFTomOOqm6IzFOMpfdDrLHoIBUI8T04srsYfXFEXiNVyH4DVQzNSouuIKSWYgYGHKPeX0woQ8hNNE+aisCkeCgcTUkxxFm4CcUeEqOoSxzL+WbFVfHse3RYSUxfYSxdgynBE8fAgegUiLD5Tz3NDPD/ANXH294RyZkFAAAAAElFTkSuQmCC';
    try {
      doc.addImage(logoData, 'PNG', margin, 3, 40, 22);
    } catch(e) { /* ignore logo error */ }

    // INVOICE text to the right
    doc.setTextColor(...headerBg);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', pageW - margin, 18, { align: 'right' });

    // Subtitle below header
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...headerBg);
    

    // Accent line
    doc.setDrawColor(...teal);
    doc.setLineWidth(1);
    doc.line(margin, 20, pageW - margin, 20);
    doc.setLineWidth(0.3);
    doc.setDrawColor(180, 180, 180);

    y = 26;

    // ===== 2. ALAMAT PT (kiri) + DATA INVOICE (kanan) =====
    const col2X = margin + (contentW / 2) + 6;

    // Left: Alamat Perusahaan (split jadi baris pendek biar muat)
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    const addrLines = [
      'PT. SYNEXA GLOBAL TEKNO',
      'Graha Raya: Ruko Silktown Avenue Blok RK/5A-05',
      'Jl. Boulevard Silk Town, Kel. Pondok Jagung Timur',
      'Kec. Serpong Utara, Kota Tangerang Selatan 15320',
      'Jakarta: Office 8 SCBD Lt.17, Jl. Senopati',
      'www.synexa.co.id | info@synexa.co.id',
    ];
    addrLines.forEach((line, i) => {
      doc.text(line, margin, y + i * 3.5);
    });

    // Right: DATA INVOICE
    const invFields = [
      ['INVOICE NO.', item.invoice_no || '-'],
      ['TANGGAL', item.tanggal_tagih ? new Date(item.tanggal_tagih).toLocaleDateString('id-ID') : '-'],
      ['NO BODY', item.no_body || '-'],
      ['ROUTE', (vehicleMap[item.vehicle_id]?.route_code || item.lokasi || '-')],
      ['NO POLISI', item.no_polisi || '-'],
      ['KATEGORI', item.jenis_kendaraan || '-'],
    ];

    const labelW = 18;
    const rowH = 4.5;

    doc.setFontSize(7);
    for (let i = 0; i < 3; i++) {
      const ry = y + i * rowH;
      const f1 = invFields[i];
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...gray);
      doc.text(f1[0] + ':', col2X, ry);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...dark);
      doc.text(f1[1], col2X + labelW + 2, ry);

      const f2 = invFields[i + 3];
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...gray);
      doc.text(f2[0] + ':', col2X, ry + 3 * rowH + 1);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...dark);
      doc.text(f2[1], col2X + labelW + 2, ry + 3 * rowH + 1);
    }

    y += Math.max(addrLines.length * 3.5 + 2, 3 * rowH + 2 + 3 * rowH + 1) - 3;
    drawSep(y);
    y += 4;

    // ===== 3. PIHAK PENERIMA (kiri) + INFORMASI REKENING (kanan) — tanpa judul header =====
    const innerColW = (contentW - 10) / 2;
    const baseY = y;

    // Left: TO / Up / Di Tempat
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...dark);
    let penerimaOffset = 0;
    doc.text(`TO : ${item.nama_klien || '-'}`, margin, baseY + penerimaOffset);
    penerimaOffset += 4.5;
    if (item.ketua_koperasi) {
      doc.text(item.ketua_koperasi, margin, baseY + penerimaOffset);
      penerimaOffset += 4.5;
    }
    if (item.owner_name) {
      doc.text(`Up. ${item.owner_name}`, margin, baseY + penerimaOffset);
      penerimaOffset += 4.5;
    }
    doc.text('Di Tempat', margin, baseY + penerimaOffset);

    // Right: No. Rekening / Bank / Cabang / No. Rek
    const rekeningX = margin + innerColW + 10;
    doc.setFontSize(7);
    doc.text('No. Rekening : PT. Synexa Global Tekno', rekeningX, baseY);
    doc.text('Bank         : Bank Central Asia (BCA)', rekeningX, baseY + 4.5);
    doc.text('Cabang       : Sudirman', rekeningX, baseY + 9);
    doc.text('No. Rek      : 0353319555', rekeningX, baseY + 13.5);

    y = baseY + 19;
    drawSep(y);
    y += 4;

    // ===== 4. KETERANGAN PEKERJAAN & JATUH TEMPO =====
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...dark);
    doc.text('KETERANGAN PEKERJAAN & JATUH TEMPO', margin, y);
    y += 2.5;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['JENIS PEKERJAAN', 'JATUH TEMPO PEMBAYARAN']],
      body: [[item.jenis_pekerjaan || '-', item.tanggal_jatuh_tempo ? new Date(item.tanggal_jatuh_tempo).toLocaleDateString('id-ID') : '-']],
      styles: { fontSize: 7, cellPadding: 2, textColor: [50, 50, 50], lineColor: [180, 180, 180], lineWidth: 0.3 },
      headStyles: { fillColor: [...tableHeadBg], textColor: [50, 50, 50], fontStyle: 'bold', fontSize: 7, halign: 'left' },
      columnStyles: {
        0: { cellWidth: contentW * 0.55 },
        1: { cellWidth: contentW * 0.45 },
      },
    });
    y = doc.lastAutoTable.finalY + 6;

    // ===== 5. RINCIAN BARANG / JASA (items only — no summary rows) =====
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...dark);
    doc.text('RINCIAN BARANG / JASA', margin, y);
    y += 2.5;

    const spItems = (item.sparepart_digunakan || []).map(s => ({ nama: s.nama, jumlah: `${s.jumlah || 1}`, harga: s.harga || 0 }));
    const jsItems = (item.jasa_digunakan || []).map(j => ({ nama: j.nama, jumlah: `${j.jumlah || 1}`, harga: j.harga || 0 }));
    const allItems = [...spItems, ...jsItems];

    // Hitung total di luar if block agar bisa dipakai di Terbilang
    const totalAll = allItems.reduce((s, i) => s + (i.harga || 0) * Number(i.jumlah || 1), 0);
    const biayaLain = Number(item.biaya_lain || 0);
    const ppnPersen = Number(item.ppn || 0);
    const ppnAmount = (totalAll + biayaLain) * (ppnPersen / 100);
    const grandTotal = totalAll + biayaLain + ppnAmount;

    if (allItems.length > 0) {
      const uraianW = contentW - 8 - 25 - 16 - 12 - 30 - 34; // = 55mm
      const colDefs = {
        0: { cellWidth: 8, halign: 'center' },
        1: { cellWidth: 25 },
        2: { cellWidth: uraianW },
        3: { cellWidth: 16, halign: 'center' },
        4: { cellWidth: 12, halign: 'center' },
        5: { cellWidth: 30, halign: 'right' },
        6: { cellWidth: 34, halign: 'right' },
      };

      // Build lookup from combinedMasterList for fallback
      const kodeLookup = {};
      const unitLookup = {};
      (combinedMasterList || []).forEach(m => {
        if (m.nama) {
          if (m.kode) kodeLookup[m.nama] = m.kode;
          if (m.unit) unitLookup[m.nama] = m.unit;
        }
      });

      const bodyRows = allItems.map((i, idx) => [
        (idx + 1).toString(),
        i.kode || kodeLookup[i.nama] || '-',
        i.nama || '-',
        i.jumlah,
        i.unit || unitLookup[i.nama] || '-',
        `Rp ${(i.harga || 0).toLocaleString('id-ID')}`,
        `Rp ${((i.harga || 0) * Number(i.jumlah || 1)).toLocaleString('id-ID')}`,
      ]);

      // Summary rows — SUB TOTAL muncul hanya jika ada PPN atau Biaya Lain
      if (ppnPersen > 0 || biayaLain > 0) {
        bodyRows.push(['', '', 'SUB TOTAL', '', '', '', `Rp ${totalAll.toLocaleString('id-ID')}`]);
      }
      if (biayaLain > 0) {
        bodyRows.push(['', '', 'Biaya Lain-lain', '', '', '', `Rp ${biayaLain.toLocaleString('id-ID')}`]);
      }
      if (ppnPersen > 0) {
        bodyRows.push(['', '', `PPN ${ppnPersen}%`, '', '', '', `Rp ${ppnAmount.toLocaleString('id-ID')}`]);
      }
      // GRAND TOTAL ditampilkan terpisah di bawah tabel (2 kolom saja)

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['No', 'KODE BARANG', 'URAIAN', 'QTY', 'UNIT', 'HARGA SATUAN', 'JUMLAH']],
        body: bodyRows,
        styles: { fontSize: 6.5, cellPadding: 2.5, textColor: [50, 50, 50], lineColor: [200, 200, 200], lineWidth: 0.2 },
        headStyles: { fillColor: [...tableHeadBg], textColor: [50, 50, 50], fontStyle: 'bold', fontSize: 6.5, halign: 'center' },
        columnStyles: colDefs,
        alternateRowStyles: { fillColor: [245, 250, 252] },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 2) {
            const txt = String(data.cell.raw || '');
            if (txt === 'SUB TOTAL' || txt === 'Biaya Lain-lain' || txt.startsWith('PPN')) {
              data.cell.styles.fontStyle = 'bold';
            }
          }
          if (data.section === 'body' && data.column.index === 6) {
            const txt = String(data.cell.raw || '');
            if (txt.startsWith('Rp') && txt !== `Rp ${((allItems[0]?.harga||0) * Number(allItems[0]?.jumlah||1)).toLocaleString('id-ID')}`) {
              // Approximate: bold last 3 rows in JUMLAH column (SUB TOTAL / Biaya Lain / PPN)
              if (data.row.index >= bodyRows.length - 3) {
                data.cell.styles.fontStyle = 'bold';
              }
            }
          }
        },
      });
      y = doc.lastAutoTable.finalY + 2;

      // ===== GRAND TOTAL — 2 kolom saja (label + nilai) =====
      const gtLabelW = contentW * 0.65;
      const gtValueW = contentW * 0.35;
      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        body: [[
          { content: 'GRAND TOTAL', styles: { fontStyle: 'bold', fontSize: 7, halign: 'left', fillColor: [221, 240, 242], textColor: [30, 30, 30] } },
          { content: `Rp ${grandTotal.toLocaleString('id-ID')}`, styles: { fontStyle: 'bold', fontSize: 7, halign: 'right', fillColor: [221, 240, 242], textColor: [30, 30, 30] } },
        ]],
        theme: 'grid',
        styles: { cellPadding: 3, lineColor: [180, 180, 200], lineWidth: 0.3 },
        columnStyles: {
          0: { cellWidth: gtLabelW },
          1: { cellWidth: gtValueW },
        },
      });
      y = doc.lastAutoTable.finalY + 6;
    }

    // ===== 7. TERBILANG — dengan background box =====
    const terbilangBoxH = 12;
    const boxPad = 6;
    doc.setFillColor(...tableHeadBg);
    doc.roundedRect(margin, y, contentW, terbilangBoxH, 2, 2, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...dark);
    const terbilangText = grandTotal > 0 ? `Terbilang : ${numberToWords(grandTotal).trim()} Rupiah` : 'Terbilang : Nol Rupiah';
    const terbilangLines = doc.splitTextToSize(terbilangText, contentW - 2 * boxPad);
    doc.text(terbilangLines, margin + boxPad, y + 7);
    y += terbilangBoxH + 3;

    // ===== 8. CATATAN (if any) =====
    if (item.catatan) {
      doc.setTextColor(...gray);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      doc.text('CATATAN:', margin, y);
      y += 3;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...dark);
      doc.setFontSize(7);
      const catatanLines = doc.splitTextToSize(item.catatan, contentW);
      doc.text(catatanLines, margin, y);
      y += catatanLines.length * 3.5 + 4;
    }

    // ===== 10. SIGNATURE SECTION =====
    const footerStartY = Math.max(y + 1, pageH - 36);

    const sigW = 60;
    const sigGap = 18;
    const totalSigW = 2 * sigW + sigGap;
    const sigStartX = (pageW - totalSigW) / 2;
    const sigRightX = sigStartX + sigW + sigGap;
    const sigLineY = footerStartY + 6;

    doc.setTextColor(...dark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.text('Terimakasih', sigRightX + sigW / 2, sigLineY - 38, { align: 'center' });

    // TTD image di bawah Reynatania — hitam pekat & tebal (sama seperti cetak CM)
    if (item.signature_url) {
      try {
        const processed = await boldenSignature(item.signature_url);
        doc.addImage(processed, 'PNG', sigRightX + 4, sigLineY - 32, sigW - 8, 22);
      } catch(e) { console.error('TTD render error:', e); }
    } else {
      doc.setTextColor(...gray);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.text('( ........................... )', sigRightX + sigW / 2, sigLineY - 5, { align: 'center' });
    }

    doc.line(sigRightX, sigLineY, sigRightX + sigW, sigLineY);
    doc.setTextColor(...gray);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Reynatania Meydiana S.', sigRightX + sigW / 2, sigLineY + 5, { align: 'center' });

    // ===== KOLOM KIRI: MENGETAHUI (Direktur Operasional) + TTD & stempel default =====
    // Auto-detect format for default TTD & stempel
    const defFmt = (u) => u && u.startsWith('data:image/png') ? 'PNG' :
                    (u && (u.startsWith('data:image/jpeg') || u.startsWith('data:image/jpg'))) ? 'JPEG' : 'JPEG';

    doc.setTextColor(...dark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.text('Mengetahui,', sigStartX + sigW / 2, sigLineY - 38, { align: 'center' });

    // Stempel sebagai watermark di BELAKANG TTD (opacity rendah)
    if (stempelDefault) {
      try {
        doc.setGState(new GState({ opacity: 0.45 }));
        doc.addImage(stempelDefault, defFmt(stempelDefault), sigStartX + 4, sigLineY - 34, sigW - 8, 28);
        doc.setGState(new GState({ opacity: 1 }));
      } catch(e) { console.error('Stempel default render error:', e); }
    }

    if (ttdDefault) {
      try {
        const processed = await boldenSignature(ttdDefault);
        doc.addImage(processed, 'PNG', sigStartX + 4, sigLineY - 32, sigW - 8, 22);
      } catch(e) { console.error('TTD default render error:', e); }
    } else {
      doc.setTextColor(...gray);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.text('( ........................... )', sigStartX + sigW / 2, sigLineY - 5, { align: 'center' });
    }

    doc.line(sigStartX, sigLineY, sigStartX + sigW, sigLineY);
    doc.setTextColor(...gray);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Achmad Saudi', sigStartX + sigW / 2, sigLineY + 5, { align: 'center' });
    doc.text('Direktur Operasional', sigStartX + sigW / 2, sigLineY + 9, { align: 'center' });

    // ===== 9. THANK YOU — di paling bawah, center, background box =====
    const thanksY = Math.max(footerStartY + 16, y + 5);
    const thanksBoxH = 16;
    if (thanksY + thanksBoxH < pageH - 8) {
      doc.setFillColor(...tableHeadBg);
      doc.roundedRect(margin, thanksY, contentW, thanksBoxH, 2, 2, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...gray);
      doc.text('Make all checks payable to PT. SYNEXA GLOBAL TEKNO.', pageW / 2, thanksY + 7, { align: 'center' });
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...teal);
      doc.text('THANK YOU FOR YOUR BUSINESS!', pageW / 2, thanksY + 15, { align: 'center' });
    }

    // ===== 11. DARK FOOTER BAR =====
    doc.setFillColor(...headerBg);
    doc.rect(0, pageH - 8, pageW, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(5.5);
    doc.setFont('helvetica', 'italic');
    const nowDate = new Date();
    const dateStr = nowDate.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    doc.text(`Dicetak: ${dateStr}  |  Invoice: ${item.invoice_no || '-'}`, margin, pageH - 3);
    doc.text(`Page 1 / 1`, pageW - margin, pageH - 3, { align: 'right' });

    doc.save(`Invoice_${item.invoice_no || 'tagihan'}.pdf`);
  };

  // Helper: convert number to Indonesian words for invoice
  function numberToWords(n) {
    if (n === 0) return '';
    const angka = ['', 'Satu', 'Dua', 'Tiga', 'Empat', 'Lima', 'Enam', 'Tujuh', 'Delapan', 'Sembilan', 'Sepuluh', 'Sebelas'];
    if (n < 12) return angka[n];
    if (n < 20) return numberToWords(n - 10) + ' Belas';
    if (n < 100) return numberToWords(Math.floor(n / 10)) + ' Puluh ' + numberToWords(n % 10);
    if (n < 200) return 'Seratus ' + numberToWords(n - 100);
    if (n < 1000) return numberToWords(Math.floor(n / 100)) + ' Ratus ' + numberToWords(n % 100);
    if (n < 2000) return 'Seribu ' + numberToWords(n - 1000);
    if (n < 1000000) return numberToWords(Math.floor(n / 1000)) + ' Ribu ' + numberToWords(n % 1000);
    if (n < 1000000000) return numberToWords(Math.floor(n / 1000000)) + ' Juta ' + numberToWords(n % 1000000);
    if (n < 1000000000000) return numberToWords(Math.floor(n / 1000000000)) + ' Milyar ' + numberToWords(n % 1000000000);
    return '';
  }

  const generateAllPDF = () => {
    if (!data.length) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 12;
    const contentW = pageW - 2 * margin;

    const primary = [17, 205, 239];
    const primaryDark = [0, 161, 200];
    const gray = [136, 152, 170];

    // === HEADER ===
    doc.setFillColor(...primary);
    doc.rect(0, 0, pageW, 40, 'F');
    doc.setFillColor(...primaryDark);
    doc.rect(0, 0, pageW, 3, 'F');

    // Left title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('LAPORAN TAGIHAN', margin, 18);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Periode: ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin, 28);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: ${data.length} tagihan`, margin, 36);

    // Right info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('LAYANAN SYNEXA', pageW - margin, 18, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Fleet Management System', pageW - margin, 26, { align: 'right' });
    doc.text('info@layanansynexa.co.id', pageW - margin, 34, { align: 'right' });

    // === SUMMARY STATS ===
    const startY = 50;
    const totalNominal = data.reduce((s, d) => s + (d.nominal_tagihan || 0), 0);
    const lunasCount = data.filter(d => d.status === 'Lunas').length;
    const belumCount = data.filter(d => d.status === 'Belum Dibayar').length;
    // 4 equal stat boxes
    const statCols = 4;
    const statGap = 6;
    const statW = (contentW - (statCols - 1) * statGap) / statCols;

    const statData = [
      { label: 'Total Tagihan', value: data.length.toString(), color: [37, 99, 235], bg: [239, 246, 255] },
      { label: 'Lunas', value: lunasCount.toString(), color: [6, 95, 70], bg: [209, 250, 229] },
      { label: 'Belum Dibayar', value: belumCount.toString(), color: [146, 64, 14], bg: [254, 243, 199] },
      { label: 'Total Nominal', value: `Rp ${totalNominal.toLocaleString('id-ID')}`, color: [23, 43, 77], bg: [248, 249, 252] },
    ];

    statData.forEach((s, idx) => {
      const sx = margin + idx * (statW + statGap);
      doc.setFillColor(...s.bg);
      doc.roundedRect(sx, startY, statW, 20, 2, 2, 'F');
      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text(s.label, sx + statW / 2, startY + 7, { align: 'center' });
      doc.setTextColor(...s.color);
      doc.setFontSize(idx === 3 ? 8 : 12);
      doc.setFont('helvetica', 'bold');
      const val = idx === 3 ? `Rp ${totalNominal.toLocaleString('id-ID')}` : s.value;
      doc.text(val, sx + statW / 2, startY + 17, { align: 'center', maxWidth: statW - 6 });
    });

    // === TABLE ===
    const headers = [['No', 'Invoice', 'No Polisi', 'Klien', 'Pekerjaan', 'Nominal', 'Status', 'Tgl Tagih', 'Jatuh Tempo', 'Tgl Bayar']];
    const rows = data.map((d, i) => [
      (i + 1).toString(),
      d.invoice_no || '-',
      d.no_polisi || '-',
      d.nama_klien || '-',
      d.jenis_pekerjaan || '-',
      `Rp ${(d.nominal_tagihan || 0).toLocaleString('id-ID')}`,
      d.status || '-',
      d.tanggal_tagih?.slice(0, 10) || '-',
      d.tanggal_jatuh_tempo?.slice(0, 10) || '-',
      d.tanggal_bayar?.slice(0, 10) || '-',
    ]);

    // Summary footer row
    const footerRow = ['', '', '', 'TOTAL', '', `Rp ${totalNominal.toLocaleString('id-ID')}`, '', '', '', ''];

    const tableStartY = startY + 28;
    autoTable(doc, {
      startY: tableStartY,
      margin: { left: margin, right: margin },
      head: headers,
      body: [...rows, footerRow],
      styles: { fontSize: 6.5, cellPadding: 2.5, lineColor: [220, 220, 220], lineWidth: 0.4, textColor: [50, 50, 50] },
      headStyles: { fillColor: [17, 205, 239], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.5, halign: 'center' },
      bodyStyles: { fontSize: 6.5 },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        1: { fontStyle: 'bold', cellWidth: 32 },
        2: { halign: 'center', cellWidth: 22 },
        3: { cellWidth: 35 },
        4: { cellWidth: 38 },
        5: { halign: 'right', fontStyle: 'bold', cellWidth: 42 },
        6: { halign: 'center', cellWidth: 22 },
        7: { halign: 'center', cellWidth: 22 },
        8: { halign: 'center', cellWidth: 22 },
        9: { halign: 'center', cellWidth: 22 },
      }, // Total: 10+32+22+35+38+42+22+22+22+22 = 267mm ✓
      footStyles: { fillColor: [248, 249, 252], textColor: [23, 43, 77], fontStyle: 'bold', fontSize: 6.5 },
    });

    // === FOOTER ===
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const h = doc.internal.pageSize.getHeight();
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, h - 12, pageW - margin, h - 12);
      doc.setTextColor(...gray);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'italic');
      const now = new Date();
      const dateStr = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      doc.text(`Dicetak: ${dateStr}`, margin, h - 6);
      doc.text(`Halaman ${i} dari ${pageCount}`, pageW - margin, h - 6, { align: 'right' });
    }

    doc.save('Laporan_Tagihan.pdf');
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Tagihan (Invoice)</h2>
            <p className="page-desc">{data.length} tagihan terdaftar</p>
          </div>
          <div className="page-header-actions">
            <button className="btn btn-secondary" onClick={exportCSV}>
              <Download size={16} /><span>Export CSV</span>
            </button>
            {data.length > 0 && (
              <button className="btn btn-secondary" onClick={generateAllPDF}>
                <Printer size={16} /><span>Export PDF</span>
              </button>
            )}
            <button className="btn btn-primary" onClick={() => { setError(''); setSelectedCM(''); setCmTglMulai(getDefaultTglMulai()); setCmTglSelesai(getDefaultTglSelesai()); setShowGenerateModal(true); }}>
              <FileText size={16} /><span>Buat dari CM</span>
            </button>
            <button className="btn btn-secondary" onClick={openCreate}>
              <Plus size={16} /><span>Buat Pengajuan</span>
            </button>
            <button className="btn btn-secondary" onClick={openCreatePM}>
              <Plus size={16} /><span>Buat Pengajuan PM</span>
            </button>
          </div>
        </div>
      </div>

      {/* Notification Banner */}
      {notifBanner && (
        <div style={{
          padding: '10px 16px',
          background: notifBanner.includes('✅') ? '#d1fae5' : '#fef3c7',
          color: notifBanner.includes('✅') ? '#065f46' : '#92400e',
          borderRadius: 8,
          fontSize: '0.85rem',
          fontWeight: 600,
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span>{notifBanner}</span>
          <button
            onClick={() => setNotifBanner('')}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', opacity: 0.7, padding: 2 }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* ====== STATISTIK TAGIHAN ====== */}
      <div className="dash-stats-row" style={{ gridTemplateColumns: 'repeat(6, 1fr)', marginBottom: 20 }}>
        <div className="dash-stat-col">
          <div className="stat-card">
            <div className="stat-card-body">
              <div className="stat-card-content">
                <span className="stat-card-label">Total Tagihan</span>
                <span className="stat-card-value">{computedStats?.total ?? 0}</span>
              </div>
              <div className="stat-icon-shape" style={{ background: '#eff6ff', color: '#2563eb' }}>
                <FileText size={22} />
              </div>
            </div>
          </div>
        </div>
        <div className="dash-stat-col">
          <div className="stat-card">
            <div className="stat-card-body">
              <div className="stat-card-content">
                <span className="stat-card-label">🟠 Pengajuan</span>
                <span className="stat-card-value" style={{ color: '#c2410c' }}>{computedStats?.pengajuan ?? 0}</span>
              </div>
              <div className="stat-icon-shape" style={{ background: '#fff5ee', color: '#c2410c' }}>
                <FileText size={22} />
              </div>
            </div>
          </div>
        </div>
        <div className="dash-stat-col">
          <div className="stat-card">
            <div className="stat-card-body">
              <div className="stat-card-content">
                <span className="stat-card-label">🔵 Disetujui</span>
                <span className="stat-card-value" style={{ color: '#3730a3' }}>{computedStats?.disetujui ?? 0}</span>
              </div>
              <div className="stat-icon-shape" style={{ background: '#eef2ff', color: '#3730a3' }}>
                <CheckCircle size={22} />
              </div>
            </div>
          </div>
        </div>
        <div className="dash-stat-col">
          <div className="stat-card">
            <div className="stat-card-body">
              <div className="stat-card-content">
                <span className="stat-card-label">🟢 Selesai</span>
                <span className="stat-card-value" style={{ color: '#065f46' }}>{computedStats?.selesai ?? 0}</span>
                <span className="stat-card-sublabel">Lunas: {computedStats?.lunas ?? 0}</span>
              </div>
              <div className="stat-icon-shape" style={{ background: '#d1fae5', color: '#065f46' }}>
                <CheckCircle size={22} />
              </div>
            </div>
          </div>
        </div>
        <div className="dash-stat-col">
          <div className="stat-card">
            <div className="stat-card-body">
              <div className="stat-card-content">
                <span className="stat-card-label">Ditolak</span>
                <span className="stat-card-value" style={{ color: '#991b1b' }}>{computedStats?.ditolak ?? 0}</span>
              </div>
              <div className="stat-icon-shape" style={{ background: '#fef2f2', color: '#991b1b' }}>
                <X size={22} />
              </div>
            </div>
          </div>
        </div>
        <div className="dash-stat-col">
          <div className="stat-card">
            <div className="stat-card-body">
              <div className="stat-card-content">
                <span className="stat-card-label">Total Nominal</span>
                <span className="stat-card-value" style={{ fontSize: '1.1rem', color: '#172b4d' }}>{rupiah(computedStats?.total_nominal ?? 0)}</span>
                <span className="stat-card-sublabel">Belum dibayar: {rupiah(computedStats?.nominal_belum ?? 0)}</span>
              </div>
              <div className="stat-icon-shape" style={{ background: '#f0fdf4', color: '#065f46' }}>
                <DollarSign size={22} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ====== WORKFLOW TAB NAVIGATION ====== */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, overflow: 'auto', paddingBottom: 4 }}>
        {WORKFLOW_TABS.map(tab => {
          const isActive = activeWorkflowTab === tab.key;
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => { setActiveWorkflowTab(tab.key); setFilterStatus(''); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px',
                borderRadius: 10,
                border: 'none',
                background: isActive ? tab.color : '#fff',
                color: isActive ? '#fff' : 'var(--gray-600)',
                fontWeight: isActive ? 700 : 500,
                fontSize: '0.82rem',
                cursor: 'pointer',
                transition: 'all 0.15s',
                boxShadow: isActive ? `0 4px 12px ${tab.color}40` : '0 1px 3px rgba(0,0,0,0.08)',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f5f5f5'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = '#fff'; }}
            >
              <Icon size={16} />
              <span>{tab.label}</span>
              {data.length > 0 && (
                <span style={{
                  background: isActive ? 'rgba(255,255,255,0.25)' : '#e5e7eb',
                  color: isActive ? '#fff' : '#374151',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  padding: '1px 7px',
                  borderRadius: 10,
                  marginLeft: 2,
                }}>
                  {tab.key === 'semua' ? data.length :
 tab.key === 'pengajuan' ? pengajuanData.length :
 tab.key === 'ditolak' ? ditolakData.length :
 tab.key === 'verifikasi' ? disetujuiData.length :
 tab.key === 'selesai' ? selesaiData.length :
 data.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ====== PENGAJUAN TAGIHAN TAB ====== */}
      {activeWorkflowTab === 'pengajuan' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={18} /> Pengajuan Tagihan
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'var(--gray-400)' }}>
                {pengajuanData.length} menunggu persetujuan
              </p>
            </div>
          </div>
          {/* Quick status filter chips */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {['', 'Pengajuan', 'Ditolak'].map(s => (
              <button
                key={s}
                onClick={() => { setFilterStatus(s); load(); }}
                style={{
                  padding: '4px 12px', borderRadius: 20, border: 'none', fontSize: '0.75rem',
                  fontWeight: filterStatus === s ? 700 : 500,
                  background: filterStatus === s
                    ? (s === '' ? '#5e72e4' : s === 'Pengajuan' ? '#fb6340' : '#dc2626')
                    : '#f3f4f6',
                  color: filterStatus === s ? '#fff' : '#374151',
                  cursor: 'pointer', transition: 'all 0.12s',
                  boxShadow: filterStatus === s ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                }}
                onMouseEnter={e => { if (filterStatus !== s) e.currentTarget.style.background = '#e5e7eb'; }}
                onMouseLeave={e => { if (filterStatus !== s) e.currentTarget.style.background = '#f3f4f6'; }}
              >
                {s === '' ? 'Semua' : s}
                {s === '' ? ` (${pengajuanData.length + ditolakData.length})` : ''}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="filters" style={{ flexWrap: 'wrap', gap: 10 }}>
            <div className="search-box" style={{ flex: '0 0 220px' }}>
              <Search size={18} />
              <input type="text" placeholder="Cari tagihan..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 140 }}>
              <option value="">Semua Status</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="text" placeholder="No Polisi" value={filterNoPolisi} onChange={e => setFilterNoPolisi(e.target.value)}
              style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', width: 130 }} />
            <input type="text" placeholder="No Body" value={filterNoBody} onChange={e => setFilterNoBody(e.target.value)}
              style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', width: 130 }} />
            {/* === FILTER KENDARAAN === */}
            <select value={filterJenisKendaraan} onChange={e => setFilterJenisKendaraan(e.target.value)} style={{ minWidth: 120 }}>
              <option value="">Jenis AC/Non AC</option>
              {jenisFilterOptions.map(o => <option key={o} value={o}>{o === 'AC' ? '❄ AC' : o === 'Non AC' ? '🔧 Non AC' : o}</option>)}
            </select>
            <select value={filterNoKontrak} onChange={e => setFilterNoKontrak(e.target.value)} style={{ minWidth: 130 }}>
              <option value="">Semua No Kontrak</option>
              {kontrakFilterOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <MultiSelectFilter
              options={klienFilterOptions}
              selected={filterNamaKlien}
              onChange={setFilterNamaKlien}
              placeholder="Semua Klien"
              searchPlaceholder="Cari klien..."
            />
            <select value={filterTeknisi} onChange={e => setFilterTeknisi(e.target.value)} style={{ minWidth: 140 }}>
              <option value="">Semua Teknisi</option>
              {teknisiFilterOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <input type="date" value={filterTglMulai} onChange={e => setFilterTglMulai(e.target.value)}
              style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem' }} />
            <span style={{ color: 'var(--gray-400)' }}>-</span>
            <input type="date" value={filterTglSelesai} onChange={e => setFilterTglSelesai(e.target.value)}
              style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem' }} />
            <button className="btn btn-secondary" style={{ padding: '8px 14px', fontSize: '0.8rem' }} onClick={load}>
              <Filter size={14} /> Terapkan
            </button>
          </div>

          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Rute</th>
                  <th>No Body</th>
                  <th>No Polisi</th>
                  <th>Klien</th>
                  <th>Teknisi</th>
                  <th>Pekerjaan</th>
                  <th>Nominal</th>
                  <th>Status</th>
                  <th>Tgl Pengerjaan</th>
                  <th>Tgl Pengajuan</th>
                  <th style={{ width: 140 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {pengajuanData.map(d => {
                  const sc = STATUS_COLORS[d.status] || { bg: '#f3f4f6', color: '#374151' };
                  const veh = vehicleMap[d.vehicle_id];
                  const cmData = findCMById(d.cm_id);
                  return (
                    <tr key={d.id}>
                      <td><strong style={{ fontSize: '0.8rem' }}>{d.invoice_no}</strong></td>
                      <td style={{ fontSize: '0.78rem' }}>{veh?.route_code || '-'}</td>
                      <td style={{ fontSize: '0.82rem' }}>{d.no_body || '-'}</td>
                      <td><span className="region-tag">{d.no_polisi || '-'}</span></td>
                      <td style={{ fontSize: '0.8rem' }}>{d.nama_klien || '-'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{d.nama_teknisi || cmData?.nama_teknisi || cmData?.teknisi_handle || '-'}</td>
                      <td style={{ fontSize: '0.78rem' }}>
                        <div>{d.jenis_pekerjaan || '-'}</div>
                        {d.sparepart_digunakan?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginTop: 3 }}>
                            {d.sparepart_digunakan.map((sp, i) => (
                              <span key={i} style={{ fontSize: '0.62rem', padding: '1px 5px', borderRadius: 3, background: '#fef3c7', color: '#92400e', whiteSpace: 'nowrap' }}>
                                🔧 {sp.nama} x{sp.jumlah}
                              </span>
                            ))}
                          </div>
                        )}
                        {d.jasa_digunakan?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginTop: 2 }}>
                            {d.jasa_digunakan.map((j, i) => (
                              <span key={i} style={{ fontSize: '0.62rem', padding: '1px 5px', borderRadius: 3, background: '#e8f4fd', color: '#0369a1', whiteSpace: 'nowrap' }}>
                                ⚙ {j.nama} x{j.jumlah}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{rupiah(d.nominal_tagihan)}</td>
                      <td>
                        <span className="badge" style={{ background: sc.bg, color: sc.color }}>{d.status}</span>
                      </td>
                      <td style={{ fontSize: '0.8rem' }}>{d.tanggal_pengerjaan?.slice(0, 10) || '-'}</td>
                      <td style={{ fontSize: '0.8rem' }}>{d.created_at?.slice(0, 10)}</td>
                      <td>
                        <div className="action-btns">
                  <button className="btn-icon" title="Lihat Detail" onClick={() => setDetailItem(d)} style={{ color: '#11cdef' }}><Eye size={14} /></button>
                  {d.cm_id && (
                    <button className="btn-icon" title="Lihat CM" onClick={() => handleViewCM(d.cm_id)} style={{ color: '#f59e0b' }}><Wrench size={14} /></button>
                  )}
                  {d.status === 'Pengajuan' && (
                    <>
                      <button className="btn-icon" title="Edit" onClick={() => openEdit(d)} style={{ color: '#5e72e4' }}><Edit2 size={14} /></button>
                      <button className="btn-icon" title="Setujui" onClick={async () => { try { await handleStatusChange(d, 'Disetujui'); } catch(e) { alert(e.response?.data?.error || 'Gagal menyetujui tagihan'); }}} style={{ color: '#065f46' }}>
                        <CheckCircle size={14} />
                      </button>
                      <button className="btn-icon" title="Tolak" onClick={async () => { try { await handleStatusChange(d, 'Ditolak'); } catch(e) { alert(e.response?.data?.error || 'Gagal menolak tagihan'); }}} style={{ color: '#dc2626' }}>
                        <X size={14} />
                      </button>
                      {hasMenuAction('tagihan', 'delete') && (
                        <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d)} style={{ color: '#dc2626' }}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </>
                  )}
                          {d.status === 'Ditolak' && (
                            <>
                              <button className="btn-icon" title="Edit" onClick={() => openEdit(d)} style={{ color: '#5e72e4' }}><Edit2 size={14} /></button>
                              <button className="btn-icon" title="Ajukan Ulang" onClick={async () => { try { await updateTagihan(d.id, sanitizeDates({ ...d, cm_id: d.cm_id||null, vehicle_id: d.vehicle_id||null, nominal_tagihan: Number(d.nominal_tagihan)||0, status: 'Pengajuan' })); load(); } catch(e) { alert('Gagal'); }}} style={{ color: '#fb6340' }}>
                                <FileText size={14} />
                              </button>
                              {hasMenuAction('tagihan', 'delete') && (
                                <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d)} style={{ color: '#dc2626' }}>
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {pengajuanData.length === 0 && ditolakData.length === 0 && (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>
                    Belum ada pengajuan tagihan
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ====== DITOLAK TAB ====== */}
      {activeWorkflowTab === 'ditolak' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <X size={18} /> Tagihan Ditolak
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'var(--gray-400)' }}>
                {ditolakData.length} tagihan ditolak
              </p>
            </div>
          </div>

          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Rute</th>
                  <th>No Body</th>
                  <th>No Polisi</th>
                  <th>Klien</th>
                  <th>Teknisi</th>
                  <th>Pekerjaan</th>
                  <th>Nominal</th>
                  <th>Status</th>
                  <th>Tgl Pengerjaan</th>
                  <th>Tgl Pengajuan</th>
                  <th style={{ width: 100 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {ditolakData.length > 0 ? ditolakData.map(d => {
                  const sc = STATUS_COLORS[d.status] || { bg: '#f3f4f6', color: '#374151' };
                  const veh = vehicleMap[d.vehicle_id];
                  const cmData = findCMById(d.cm_id);
                  return (
                    <tr key={d.id}>
                      <td><strong style={{ fontSize: '0.8rem' }}>{d.invoice_no}</strong></td>
                      <td style={{ fontSize: '0.78rem' }}>{veh?.route_code || '-'}</td>
                      <td style={{ fontSize: '0.82rem' }}>{d.no_body || '-'}</td>
                      <td><span className="region-tag">{d.no_polisi || '-'}</span></td>
                      <td style={{ fontSize: '0.8rem' }}>{d.nama_klien || '-'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{d.nama_teknisi || cmData?.nama_teknisi || cmData?.teknisi_handle || '-'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{d.jenis_pekerjaan || '-'}</td>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap', color: '#991b1b' }}>{rupiah(d.nominal_tagihan)}</td>
                      <td><span style={{ backgroundColor: sc.bg, color: sc.color, padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600 }}>{d.status}</span></td>
                      <td style={{ fontSize: '0.8rem' }}>{d.tanggal_pengerjaan?.slice(0, 10) || '-'}</td>
                      <td style={{ fontSize: '0.8rem' }}>{d.tanggal_tagih?.slice(0, 10) || '-'}</td>
                      <td><div className="action-btns">
    {d.cm_id && (
      <button className="btn-icon" title="Lihat CM" onClick={() => handleViewCM(d.cm_id)} style={{ color: '#f59e0b' }}><Wrench size={14} /></button>
    )}
    <button className="btn-icon" title="Export PDF Invoice" onClick={() => generatePDF(d)} style={{ color: '#11cdef' }}><Printer size={14} /></button>
    <button className="btn-icon" title="Ajukan Ulang" onClick={() => handleStatusChange(d, 'Pengajuan')} style={{ color: '#fb6340' }}><FileText size={14} /></button>
</div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>
                    Belum ada tagihan ditolak
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ====== VERIFIKASI TAGIHAN TAB ====== */}
      {activeWorkflowTab === 'verifikasi' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircle size={18} /> Verifikasi Tagihan
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'var(--gray-400)' }}>
                {disetujuiData.length} menunggu pembayaran
              </p>
            </div>
          </div>
          {/* Quick status filter chips */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {['', 'Disetujui'].map(s => (
              <button
                key={s}
                onClick={() => { setFilterStatus(s); load(); }}
                style={{
                  padding: '4px 12px', borderRadius: 20, border: 'none', fontSize: '0.75rem',
                  fontWeight: filterStatus === s ? 700 : 500,
                  background: filterStatus === s ? '#6366f1' : '#f3f4f6',
                  color: filterStatus === s ? '#fff' : '#374151',
                  cursor: 'pointer', transition: 'all 0.12s',
                  boxShadow: filterStatus === s ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                }}
                onMouseEnter={e => { if (filterStatus !== s) e.currentTarget.style.background = '#e5e7eb'; }}
                onMouseLeave={e => { if (filterStatus !== s) e.currentTarget.style.background = '#f3f4f6'; }}
              >
                {s === '' ? 'Semua' : s} {s === '' ? `(${disetujuiData.length})` : ''}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="filters" style={{ flexWrap: 'wrap', gap: 10 }}>
            <div className="search-box" style={{ flex: '0 0 220px' }}>
              <Search size={18} />
              <input type="text" placeholder="Cari tagihan..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 140 }}>
              <option value="">Semua Status</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="text" placeholder="No Polisi" value={filterNoPolisi} onChange={e => setFilterNoPolisi(e.target.value)}
              style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', width: 130 }} />
            <input type="text" placeholder="No Body" value={filterNoBody} onChange={e => setFilterNoBody(e.target.value)}
              style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', width: 130 }} />
            {/* === FILTER KENDARAAN === */}
            <select value={filterJenisKendaraan} onChange={e => setFilterJenisKendaraan(e.target.value)} style={{ minWidth: 120 }}>
              <option value="">Jenis AC/Non AC</option>
              {jenisFilterOptions.map(o => <option key={o} value={o}>{o === 'AC' ? '❄ AC' : o === 'Non AC' ? '🔧 Non AC' : o}</option>)}
            </select>
            <select value={filterNoKontrak} onChange={e => setFilterNoKontrak(e.target.value)} style={{ minWidth: 130 }}>
              <option value="">Semua No Kontrak</option>
              {kontrakFilterOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <MultiSelectFilter
              options={klienFilterOptions}
              selected={filterNamaKlien}
              onChange={setFilterNamaKlien}
              placeholder="Semua Klien"
              searchPlaceholder="Cari klien..."
            />
            <select value={filterTeknisi} onChange={e => setFilterTeknisi(e.target.value)} style={{ minWidth: 140 }}>
              <option value="">Semua Teknisi</option>
              {teknisiFilterOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <input type="date" value={filterTglMulai} onChange={e => setFilterTglMulai(e.target.value)}
              style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem' }} />
            <span style={{ color: 'var(--gray-400)' }}>-</span>
            <input type="date" value={filterTglSelesai} onChange={e => setFilterTglSelesai(e.target.value)}
              style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem' }} />
            <button className="btn btn-secondary" style={{ padding: '8px 14px', fontSize: '0.8rem' }} onClick={load}>
              <Filter size={14} /> Terapkan
            </button>
          </div>

          <div className="table-wrapper">
            <table className="data-table">              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Rute</th>
                  <th>No Body</th>
                  <th>No Polisi</th>
                  <th>Klien</th>
                  <th>Teknisi</th>
                  <th>Pekerjaan</th>
                  <th>Nominal</th>
                  <th>Status</th>
                  <th>Tgl Pengerjaan</th>
                  <th>Tgl Pengajuan</th>
                  <th style={{ width: 140 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {disetujuiData.map(d => {
                  const sc = STATUS_COLORS[d.status] || { bg: '#f3f4f6', color: '#374151' };
                  const veh = vehicleMap[d.vehicle_id];
                  const cmData = findCMById(d.cm_id);
                  return (
                    <tr key={d.id}>
                      <td><strong style={{ fontSize: '0.8rem' }}>{d.invoice_no}</strong></td>
                      <td style={{ fontSize: '0.78rem' }}>{veh?.route_code || '-'}</td>
                      <td style={{ fontSize: '0.82rem' }}>{d.no_body || '-'}</td>
                      <td><span className="region-tag">{d.no_polisi || '-'}</span></td>
                      <td style={{ fontSize: '0.8rem' }}>{d.nama_klien || '-'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{d.nama_teknisi || cmData?.nama_teknisi || cmData?.teknisi_handle || '-'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{d.jenis_pekerjaan || '-'}</td>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{rupiah(d.nominal_tagihan)}</td>
                      <td>
                        <span className="badge" style={{ background: sc.bg, color: sc.color }}>{d.status}</span>
                      </td>
                      <td style={{ fontSize: '0.8rem' }}>{d.tanggal_pengerjaan?.slice(0, 10) || '-'}</td>
                      <td style={{ fontSize: '0.8rem' }}>{d.updated_at?.slice(0, 10)}</td>
                      <td>
                        <div className="action-btns">
                          {d.cm_id && (
                            <button className="btn-icon" title="Lihat CM" onClick={() => handleViewCM(d.cm_id)} style={{ color: '#f59e0b' }}><Wrench size={14} /></button>
                          )}
                          <button className="btn-icon" title="Bayar / Selesaikan" onClick={() => handleBayar(d)} style={{ color: '#065f46' }}>
                            <DollarSign size={14} />
                          </button>
                          <button className="btn-icon" title="Export PDF Invoice" onClick={() => generatePDF(d)} style={{ color: '#11cdef' }}>
                            <Printer size={14} />
                          </button>
                          <button className="btn-icon" title="Tolak Pembayaran" onClick={async () => { try { await updateTagihan(d.id, sanitizeDates({ ...d, cm_id: d.cm_id||null, vehicle_id: d.vehicle_id||null, nominal_tagihan: Number(d.nominal_tagihan)||0, status: 'Ditolak' })); load(); } catch(e) { alert('Gagal'); }}} style={{ color: '#dc2626' }}>
                            <X size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {disetujuiData.length === 0 && (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>
                    Belum ada tagihan yang disetujui
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}


      {/* ====== TAGIHAN SELESAI TAB ====== */}
      {activeWorkflowTab === 'selesai' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircle size={18} /> Tagihan Selesai
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'var(--gray-400)' }}>
                {selesaiData.length} tagihan selesai
              </p>
            </div>
          </div>
          {/* Quick status filter chips */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {['', 'Selesai'].map(s => (
              <button
                key={s}
                onClick={() => { setFilterStatus(s); load(); }}
                style={{
                  padding: '4px 12px', borderRadius: 20, border: 'none', fontSize: '0.75rem',
                  fontWeight: filterStatus === s ? 700 : 500,
                  background: filterStatus === s ? '#059669' : '#f3f4f6',
                  color: filterStatus === s ? '#fff' : '#374151',
                  cursor: 'pointer', transition: 'all 0.12s',
                  boxShadow: filterStatus === s ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                }}
                onMouseEnter={e => { if (filterStatus !== s) e.currentTarget.style.background = '#e5e7eb'; }}
                onMouseLeave={e => { if (filterStatus !== s) e.currentTarget.style.background = '#f3f4f6'; }}
              >
                {s === '' ? 'Semua' : s} {s === '' ? `(${selesaiData.length})` : ''}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="filters" style={{ flexWrap: 'wrap', gap: 10 }}>
            <div className="search-box" style={{ flex: '0 0 220px' }}>
              <Search size={18} />
              <input type="text" placeholder="Cari tagihan..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 140 }}>
              <option value="">Semua Status</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="text" placeholder="No Polisi" value={filterNoPolisi} onChange={e => setFilterNoPolisi(e.target.value)}
              style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', width: 130 }} />
            <input type="text" placeholder="No Body" value={filterNoBody} onChange={e => setFilterNoBody(e.target.value)}
              style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', width: 130 }} />
            {/* === FILTER KENDARAAN === */}
            <select value={filterJenisKendaraan} onChange={e => setFilterJenisKendaraan(e.target.value)} style={{ minWidth: 120 }}>
              <option value="">Jenis AC/Non AC</option>
              {jenisFilterOptions.map(o => <option key={o} value={o}>{o === 'AC' ? '❄ AC' : o === 'Non AC' ? '🔧 Non AC' : o}</option>)}
            </select>
            <select value={filterNoKontrak} onChange={e => setFilterNoKontrak(e.target.value)} style={{ minWidth: 130 }}>
              <option value="">Semua No Kontrak</option>
              {kontrakFilterOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <MultiSelectFilter
              options={klienFilterOptions}
              selected={filterNamaKlien}
              onChange={setFilterNamaKlien}
              placeholder="Semua Klien"
              searchPlaceholder="Cari klien..."
            />
            <select value={filterTeknisi} onChange={e => setFilterTeknisi(e.target.value)} style={{ minWidth: 140 }}>
              <option value="">Semua Teknisi</option>
              {teknisiFilterOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <input type="date" value={filterTglMulai} onChange={e => setFilterTglMulai(e.target.value)}
              style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem' }} />
            <span style={{ color: 'var(--gray-400)' }}>-</span>
            <input type="date" value={filterTglSelesai} onChange={e => setFilterTglSelesai(e.target.value)}
              style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem' }} />
            <button className="btn btn-secondary" style={{ padding: '8px 14px', fontSize: '0.8rem' }} onClick={load}>
              <Filter size={14} /> Terapkan
            </button>
          </div>

          <div className="table-wrapper">
            <table className="data-table">              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Rute</th>
                  <th>No Body</th>
                  <th>No Polisi</th>
                  <th>Klien</th>
                  <th>Teknisi</th>
                  <th>Pekerjaan</th>
                  <th>Nominal</th>
                  <th>Status</th>
                  <th>Tgl Pengerjaan</th>
                  <th>Tgl Pengajuan</th>
                  <th style={{ width: 140 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {selesaiData.map(d => {
                  const veh = vehicleMap[d.vehicle_id];
                  const cmData = findCMById(d.cm_id);
                  return (
                    <tr key={d.id}>
                      <td><strong style={{ fontSize: '0.8rem' }}>{d.invoice_no}</strong></td>
                      <td style={{ fontSize: '0.78rem' }}>{veh?.route_code || '-'}</td>
                      <td style={{ fontSize: '0.82rem' }}>{d.no_body || '-'}</td>
                      <td><span className="region-tag">{d.no_polisi || '-'}</span></td>
                      <td style={{ fontSize: '0.8rem' }}>{d.nama_klien || '-'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{d.nama_teknisi || cmData?.nama_teknisi || cmData?.teknisi_handle || '-'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{d.jenis_pekerjaan || '-'}</td>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap', color: '#065f46' }}>{rupiah(d.nominal_tagihan)}</td>
                      <td style={{ fontSize: '0.8rem' }}>{d.metode_pembayaran || '-'}</td>
                      <td style={{ fontSize: '0.8rem' }}>{d.tanggal_bayar?.slice(0, 10) || '-'}</td>
                      <td style={{ fontSize: '0.8rem' }}>{d.tanggal_tagih?.slice(0, 10) || '-'}</td>
                      <td><div className="action-btns">
    {d.cm_id && (
      <button className="btn-icon" title="Lihat CM" onClick={() => handleViewCM(d.cm_id)} style={{ color: '#f59e0b' }}><Wrench size={14} /></button>
    )}
    <button className="btn-icon" title="Export PDF Invoice" onClick={() => generatePDF(d)} style={{ color: '#11cdef' }}><Printer size={14} /></button>
    <button className="btn-icon" title="Ajukan Ulang" onClick={() => handleStatusChange(d, 'Pengajuan')} style={{ color: '#fb6340' }}><FileText size={14} /></button>
    {hasMenuAction('tagihan', 'delete') && (
      <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d)} style={{ color: '#dc2626' }}>
        <Trash2 size={14} />
      </button>
    )}
</div>
                      </td>
                    </tr>
                  );
                })}
                {selesaiData.length === 0 && (<tr><td colSpan={12} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>
  Belum ada tagihan selesai
</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ====== SEMUA TAGIHAN TAB ====== */}
      {activeWorkflowTab === 'semua' && (<>
        {/* Filters */}
        <div className="filters" style={{ flexWrap: 'wrap', gap: 10 }}>
          <div className="search-box" style={{ flex: '0 0 220px' }}>
            <Search size={18} />
            <input type="text" placeholder="Cari tagihan..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">Semua Status</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <input type="text" placeholder="No Polisi" value={filterNoPolisi} onChange={e => setFilterNoPolisi(e.target.value)}
            style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', width: 130 }} />
          <input type="text" placeholder="No Body" value={filterNoBody} onChange={e => setFilterNoBody(e.target.value)}
            style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', width: 130 }} />

          {/* === FILTER KENDARAAN === */}
          <select value={filterJenisKendaraan} onChange={e => setFilterJenisKendaraan(e.target.value)} style={{ minWidth: 120 }}>
            <option value="">Jenis AC/Non AC</option>
            {jenisFilterOptions.map(o => <option key={o} value={o}>{o === 'AC' ? '❄ AC' : o === 'Non AC' ? '🔧 Non AC' : o}</option>)}
          </select>
          <select value={filterNoKontrak} onChange={e => setFilterNoKontrak(e.target.value)} style={{ minWidth: 130 }}>
            <option value="">Semua No Kontrak</option>
            {kontrakFilterOptions.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <MultiSelectFilter
            options={klienFilterOptions}
            selected={filterNamaKlien}
            onChange={setFilterNamaKlien}
            placeholder="Semua Klien"
            searchPlaceholder="Cari klien..."
          />
          <select value={filterTeknisi} onChange={e => setFilterTeknisi(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">Semua Teknisi</option>
            {teknisiFilterOptions.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <input type="date" value={filterTglMulai} onChange={e => setFilterTglMulai(e.target.value)}
            style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem' }} />
          <span style={{ color: 'var(--gray-400)' }}>-</span>
          <input type="date" value={filterTglSelesai} onChange={e => setFilterTglSelesai(e.target.value)}
            style={{ padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem' }} />
          <button className="btn btn-secondary" style={{ padding: '8px 14px', fontSize: '0.8rem' }} onClick={load}>
            <Filter size={14} /> Terapkan
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="page-loading">Memuat data...</div>
        ) : (
          <div className="table-wrapper">                          <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Rute</th>
                  <th>No Body</th>
                  <th>No Polisi</th>
                  <th>Klien</th>
                  <th>Teknisi</th>
                  <th>Pekerjaan</th>
                  <th>Nominal</th>
                  <th>Status</th>
                  <th>Tgl Pengerjaan</th>
                  <th>Tgl Pengajuan</th>
                  <th style={{ width: 140 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map(d => {
                  const veh = vehicleMap[d.vehicle_id];
                  const cmData = findCMById(d.cm_id);
                  return (
                    <tr key={d.id}>
                      <td><strong style={{ fontSize: '0.8rem' }}>{d.invoice_no}</strong></td>
                      <td style={{ fontSize: '0.78rem' }}>{veh?.route_code || '-'}</td>
                      <td style={{ fontSize: '0.82rem' }}>{d.no_body || '-'}</td>
                      <td><span className="region-tag">{d.no_polisi || '-'}</span></td>
                      <td style={{ fontSize: '0.8rem' }}>{d.nama_klien || '-'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{d.nama_teknisi || cmData?.nama_teknisi || cmData?.teknisi_handle || '-'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{d.jenis_pekerjaan || '-'}</td>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap', color: '#065f46' }}>{rupiah(d.nominal_tagihan)}</td>
                      <td style={{ fontSize: '0.8rem' }}>{d.metode_pembayaran || '-'}</td>
                      <td style={{ fontSize: '0.8rem' }}>{d.tanggal_bayar?.slice(0, 10) || '-'}</td>
                      <td style={{ fontSize: '0.8rem' }}>{d.tanggal_tagih?.slice(0, 10) || '-'}</td>
                      <td><div className="action-btns">
    {d.cm_id && (
      <button className="btn-icon" title="Lihat CM" onClick={() => handleViewCM(d.cm_id)} style={{ color: '#f59e0b' }}><Wrench size={14} /></button>
    )}
    <button className="btn-icon" title="Export PDF Invoice" onClick={() => generatePDF(d)} style={{ color: '#11cdef' }}><Printer size={14} /></button>
    <button className="btn-icon" title="Ajukan Ulang" onClick={() => handleStatusChange(d, 'Pengajuan')} style={{ color: '#fb6340' }}><FileText size={14} /></button>
</div>
                      </td>
                    </tr>
                  );
                })}
                {filteredData.length === 0 && (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>
                    Belum ada tagihan
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </>)}}
      {/* Detail Modal */}
      {detailItem && (
        <div className="modal-overlay" onClick={() => setDetailItem(null)} style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, width: '95%', maxHeight: '80vh', overflow: 'auto', borderRadius: 12 }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #11cdef, #0284c7)', color: '#fff', borderRadius: '12px 12px 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ color: '#fff', margin: 0, fontSize: '1rem' }}>Detail Tagihan — {detailItem.invoice_no || `#${detailItem.id}`}</h3>
              </div>
              <button className="btn-icon" onClick={() => setDetailItem(null)} style={{ color: '#fff' }}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ padding: '16px 24px', background: '#fff', fontSize: '0.85rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr><td style={{ fontWeight: 600, width: 130, padding: '4px 8px' }}>Invoice</td><td>: {detailItem.invoice_no || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>No. Polisi</td><td>: {detailItem.no_polisi || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>No. Body</td><td>: {detailItem.no_body || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Nama Klien</td><td>: {detailItem.nama_klien || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Pekerjaan</td><td>: {detailItem.jenis_pekerjaan || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Status</td><td>: <span className="badge" style={{ background: (STATUS_COLORS[detailItem.status] || {}).bg || '#f3f4f6', color: (STATUS_COLORS[detailItem.status] || {}).color || '#374151', padding: '2px 8px', borderRadius: 10, fontSize: '0.75rem' }}>{detailItem.status}</span></td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Nominal</td><td>: <strong style={{ color: '#065f46' }}>{rupiah(detailItem.nominal_tagihan)}</strong></td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Tgl. Pengerjaan</td><td>: {detailItem.tanggal_pengerjaan?.slice(0,10) || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Tgl. Pengajuan</td><td>: {detailItem.tanggal_tagih?.slice(0,10) || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Metode Bayar</td><td>: {detailItem.metode_pembayaran || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Tgl. Bayar</td><td>: {detailItem.tanggal_bayar?.slice(0,10) || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Catatan</td><td>: {detailItem.catatan || '-'}</td></tr>
                </tbody>
              </table>
              {(detailItem.sparepart_digunakan || []).length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <strong style={{ fontSize: '0.8rem' }}>Sparepart Digunakan:</strong>
                  {(detailItem.sparepart_digunakan || []).map((sp, i) => (
                    <div key={i} style={{ fontSize: '0.78rem', padding: '2px 0' }}>• {sp.nama} x{sp.jumlah} @ Rp {(sp.harga || 0).toLocaleString('id-ID')}</div>
                  ))}
                </div>
              )}
              {(detailItem.jasa_digunakan || []).length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong style={{ fontSize: '0.8rem' }}>Jasa Layanan:</strong>
                  {(detailItem.jasa_digunakan || []).map((j, i) => (
                    <div key={i} style={{ fontSize: '0.78rem', padding: '2px 0' }}>• {j.nama} x{j.jumlah} @ Rp {(j.harga || 0).toLocaleString('id-ID')}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '12px 24px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#fafafa', borderRadius: '0 0 12px 12px' }}>
              <button className="btn btn-secondary" onClick={() => setDetailItem(null)}>Tutup</button>
              <button className="btn btn-primary" onClick={() => { generatePDF(detailItem); setDetailItem(null); }} style={{ background: 'linear-gradient(135deg, #11cdef, #0284c7)', border: 'none' }}>
                <Printer size={14} /> Cetak PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== CM VIEW MODAL ====== */}
      {cmViewItem && (
        <div className="modal-overlay" onClick={() => setCmViewItem(null)} style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1001 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 900, width: '95%', maxHeight: '85vh', overflow: 'auto', borderRadius: 12 }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #2dce89, #1aae6f)', color: '#fff', borderRadius: '12px 12px 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 }}>
              <div>
                <h3 style={{ color: '#fff', margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Wrench size={18} /> Detail CM — {cmViewItem.cm_id || `#${cmViewItem.id}`}
                </h3>
                <small style={{ opacity: 0.85, fontSize: '0.78rem' }}>{cmViewItem.no_polisi} — <span className="badge" style={{ background: cmViewItem.status === 'Selesai' ? '#d1fae5' : '#fff5ee', color: cmViewItem.status === 'Selesai' ? '#065f46' : '#c2410c', fontSize: '0.7rem', fontWeight: 600, padding: '3px 10px', borderRadius: 20 }}>{cmViewItem.status || '-'}</span></small>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-icon" onClick={handleCetakCM} style={{ color: '#fff', background: 'rgba(255,255,255,0.15)' }} title="Cetak">
                  <Printer size={18} />
                </button>
                <button className="btn-icon" onClick={() => setCmViewItem(null)} style={{ color: '#fff' }}><X size={20} /></button>
              </div>
            </div>

            <div className="modal-body" style={{ padding: '16px 24px', background: '#fff' }}>
              {/* INFORMASI UMUM */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: '2px solid #fb6340', paddingBottom: 8, marginBottom: 16, marginTop: 20 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#fb6340', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <FileText size={16} />
                </div>
                <div>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--gray-700)', margin: 0 }}>Informasi Umum</h4>
                  <small style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}>Data kendaraan dan petugas</small>
                </div>
              </div>
              <table className="identity" style={{ width: '100%', fontSize: '0.85rem', marginBottom: 12 }}>
                <tbody>
                  <tr><td style={{ fontWeight: 600, width: 130 }}>Nama Klien</td><td>: {cmViewItem.nama_klien || '-'}</td><td style={{ fontWeight: 600, width: 130 }}>Rute</td><td>: {cmViewItem.rute || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>Nama Teknisi</td><td>: {cmViewItem.nama_teknisi || '-'}</td><td style={{ fontWeight: 600 }}>Lokasi</td><td>: {cmViewItem.lokasi_pengerjaan || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>Detail Pekerjaan</td><td>: {cmViewItem.detail_pekerjaan || '-'}</td><td style={{ fontWeight: 600 }}>Jenis Pengerjaan</td><td>: {cmViewItem.jenis_pengerjaan || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>No. Polisi</td><td>: <strong>{cmViewItem.no_polisi || '-'}</strong></td><td style={{ fontWeight: 600 }}>No. Body</td><td>: {cmViewItem.no_body || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>Pengawas/Pramudi</td><td>: {cmViewItem.pengawas_pramudi || '-'}</td><td style={{ fontWeight: 600 }}>No. HP Pramudi</td><td>: {cmViewItem.no_hp_pramudi || '-'}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>Odometer</td><td>: {cmViewItem.odometer ? cmViewItem.odometer + ' km' : '-'}</td><td style={{ fontWeight: 600 }}>Jenis AC</td><td>: {cmViewItem.jenis_ac || '-'}</td></tr>
                </tbody>
              </table>

              {/* HASIL PEMERIKSAAN */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: '2px solid #11cdef', paddingBottom: 8, marginBottom: 16, marginTop: 20 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#11cdef', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <CheckCircle size={16} />
                </div>
                <div>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--gray-700)', margin: 0 }}>Hasil Pemeriksaan</h4>
                  <small style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}>Detail pemeriksaan dan persetujuan</small>
                </div>
              </div>
              <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: '0.85rem' }}>
                  <div><strong>Detail Pekerjaan:</strong><br/>{cmViewItem.detail_pekerjaan || '-'}</div>
                  <div><strong>Waktu Pemeriksaan:</strong><br/>{cmViewItem.waktu_pemeriksaan ? new Date(cmViewItem.waktu_pemeriksaan).toLocaleString('id-ID', { timeZone: 'Asia/Jakarta' }) : (cmViewItem.created_at ? new Date(cmViewItem.created_at).toLocaleString('id-ID', { timeZone: 'Asia/Jakarta' }) : '-')}</div>
                  <div><strong>Hasil Pemeriksaan:</strong><br/>{cmViewItem.hasil_pemeriksaan || '-'}</div>
                  <div><strong>Disetujui Oleh:</strong><br/>{cmViewItem.disetujui_oleh || '-'}</div>
                  <div style={{ gridColumn: '1 / -1' }}><strong>Keterangan:</strong><br/>{cmViewItem.catatan || '-'}</div>
                </div>
              </div>

              {/* HASIL PEKERJAAN */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: '2px solid #8965e0', paddingBottom: 8, marginBottom: 16, marginTop: 20 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#8965e0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <Wrench size={16} />
                </div>
                <div>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--gray-700)', margin: 0 }}>Hasil Pekerjaan</h4>
                  <small style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}>Sparepart dan jasa yang digunakan</small>
                </div>
              </div>
              {cmViewItem.sparepart_digunakan?.length > 0 && (
                <table className="data-table" style={{ marginBottom: 12, fontSize: '0.82rem' }}>
                  <thead><tr><th>Sparepart</th><th style={{ width: 60 }}>Jumlah</th><th style={{ width: 100 }}>Harga</th><th style={{ width: 100 }}>Subtotal</th><th>Hasil</th></tr></thead>
                  <tbody>
                    {cmViewItem.sparepart_digunakan.map((sp, i) => {
                      const subtotal = (sp.jumlah||0) * (sp.harga||0);
                      return (
                        <tr key={i}><td>{sp.nama}</td><td style={{ textAlign: 'center' }}>{sp.jumlah}</td><td style={{ textAlign: 'right' }}>Rp {(sp.harga || 0).toLocaleString('id-ID')}</td><td style={{ textAlign: 'right' }}>Rp {subtotal.toLocaleString('id-ID')}</td><td>{sp.hasil_pekerjaan || '-'}</td></tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {cmViewItem.jasa_digunakan?.length > 0 && (
                <table className="data-table" style={{ marginBottom: 12, fontSize: '0.82rem' }}>
                  <thead><tr><th>Jasa Layanan</th><th style={{ width: 60 }}>Jml</th><th style={{ width: 100 }}>Harga</th><th style={{ width: 100 }}>Subtotal</th></tr></thead>
                  <tbody>
                    {cmViewItem.jasa_digunakan.map((js, i) => {
                      const subtotal = (js.jumlah||0) * (js.harga||0);
                      return (
                        <tr key={i}><td>{js.nama}</td><td style={{ textAlign: 'center' }}>{js.jumlah}</td><td style={{ textAlign: 'right' }}>Rp {(js.harga || 0).toLocaleString('id-ID')}</td><td style={{ textAlign: 'right' }}>Rp {subtotal.toLocaleString('id-ID')}</td></tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {(() => {
                const totalSP = (cmViewItem.sparepart_digunakan || []).reduce((s, sp) => s + (sp.jumlah||0)*(sp.harga||0), 0);
                const totalJS = (cmViewItem.jasa_digunakan || []).reduce((s, js) => s + (js.jumlah||0)*(js.harga||0), 0);
                const grandTotal = cmViewItem.nominal_tagihan || (totalSP + totalJS);
                return (
                  <div style={{ textAlign: 'right', fontSize: '1rem', fontWeight: 700, color: '#1aae6f', padding: '8px 16px', background: '#e8f8f0', borderRadius: 8, marginBottom: 16 }}>
                    Total: Rp {grandTotal.toLocaleString('id-ID')}
                  </div>
                );
              })()}

              {/* DOKUMENTASI FOTO & VIDEO */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: '2px solid #f59e0b', paddingBottom: 8, marginBottom: 16, marginTop: 20 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <Film size={16} />
                </div>
                <div>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--gray-700)', margin: 0 }}>Dokumentasi Foto & Video</h4>
                  <small style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}>Dokumentasi pekerjaan</small>
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                {(() => {
                  const origin = window.location.origin;
                  const resolveUrl = (u) => u && u.startsWith('data:') ? u : origin + u;
                  const fotoList = cmViewItem.foto_urls || (cmViewItem.foto_url ? [cmViewItem.foto_url] : []);
                  const videoList = cmViewItem.video_urls || (cmViewItem.video_url ? [cmViewItem.video_url] : []);
                  // Beberapa foto mobile-web tersimpan sebagai video di foto_urls — tampilkan sebagai <video>
                  return (
                    <>
                      {fotoList.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>📷 Foto ({fotoList.length})</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {fotoList.map((url, idx) => {
                              const fullUrl = resolveUrl(url);
                              const isVid = isVideoURL(fullUrl);
                              return isVid ? (
                                <div key={idx} style={{ width: 140, height: 90, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--gray-200)', cursor: 'pointer', background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
                                  onClick={() => window.open(fullUrl, '_blank')}>
                                  <video src={fullUrl} controls muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, fontSize: '0.55rem', color: '#fff', textAlign: 'center', background: 'rgba(0,0,0,0.6)', padding: '1px 4px' }}>
                                    Video {idx+1}
                                  </div>
                                </div>
                              ) : (
                                <div key={idx} style={{ width: 100, height: 80, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--gray-200)', cursor: 'pointer' }}
                                  onClick={() => window.open(fullUrl, '_blank')}>
                                  <img src={fullUrl} alt={'Foto ' + (idx+1)} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => { e.target.style.display = 'none'; }} />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {videoList.length > 0 && (
                        <div>
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>🎬 Video ({videoList.length})</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {videoList.map((url, idx) => {
                              const fullUrl = resolveUrl(url);
                              return (
                                <div key={idx} style={{ width: 120, height: 80, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--gray-200)', background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative' }}
                                  onClick={() => window.open(fullUrl, '_blank')}>
                                  <Film size={24} color="#fff" />
                                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, fontSize: '0.55rem', color: '#fff', textAlign: 'center', background: 'rgba(0,0,0,0.6)', padding: '1px 4px' }}>
                                    Video {idx+1}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {fotoList.length === 0 && videoList.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--gray-400)', fontSize: '0.82rem', fontStyle: 'italic' }}>
                          Belum ada dokumentasi foto/video
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '12px 24px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#fafafa', borderRadius: '0 0 12px 12px' }}>
              <button className="btn btn-secondary" onClick={() => setCmViewItem(null)}>Tutup</button>
            </div>
          </div>
        </div>
      )}


      {/* Payment Modal */}
      {showPaymentModal && paymentItem && (
        <div className="modal-overlay" onClick={() => { setShowPaymentModal(false); setPaymentItem(null); setSelectedPaymentMethod(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>💳 Pembayaran</h3>
              <button className="btn-icon" onClick={() => { setShowPaymentModal(false); setPaymentItem(null); setSelectedPaymentMethod(''); }}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {/* Invoice info */}
              <div style={{ padding: '12px 16px', background: '#f8f9fa', borderRadius: 10, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>Invoice</div>
                    <div style={{ fontSize: '1rem', fontWeight: 700 }}>{paymentItem.invoice_no}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>Total Tagihan</div>
                    <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#065f46' }}>
                      {rupiah(paymentItem.nominal_tagihan)}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--gray-500)' }}>
                  {paymentItem.nama_klien} — {paymentItem.no_polisi}
                </div>
              </div>

              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: 12 }}>
                Pilih Metode Pembayaran
              </label>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {METODE_PEMBAYARAN.map(m => (
                  <button
                    key={m.value}
                    onClick={() => setSelectedPaymentMethod(m.value)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 16px',
                      border: selectedPaymentMethod === m.value ? `2px solid var(--primary)` : '1px solid var(--gray-200)',
                      borderRadius: 12,
                      background: selectedPaymentMethod === m.value ? '#f0f9ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      textAlign: 'left',
                      width: '100%',
                    }}
                    onMouseEnter={e => { if (selectedPaymentMethod !== m.value) e.currentTarget.style.background = '#f8f9fa'; }}
                    onMouseLeave={e => { if (selectedPaymentMethod !== m.value) e.currentTarget.style.background = '#fff'; }}
                  >
                    <span style={{ fontSize: '1.5rem' }}>{m.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{m.label}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{m.description}</div>
                    </div>
                    {selectedPaymentMethod === m.value && (
                      <span style={{ color: 'var(--primary)' }}>
                        <CheckCircle size={20} />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowPaymentModal(false); setPaymentItem(null); setSelectedPaymentMethod(''); }}>
                Batal
              </button>
              <button
                className="btn btn-primary"
                onClick={handleKonfirmasiBayar}
                disabled={!selectedPaymentMethod}
                style={{ background: !selectedPaymentMethod ? 'var(--gray-300)' : '#065f46' }}
              >
                Konfirmasi Pembayaran
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate from CM Modal */}
      {showGenerateModal && (
        <div className="modal-overlay" onClick={() => { setShowGenerateModal(false); setCmSearchText(''); setSelectedCM(''); setCmTglMulai(''); setCmTglSelesai(''); setError(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h3>Buat Tagihan dari CM</h3>
              <button className="btn-icon" onClick={() => { setShowGenerateModal(false); setCmSearchText(''); setSelectedCM(''); setCmTglMulai(''); setCmTglSelesai(''); setError(''); }}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}

              {/* Filter bar */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="search-box" style={{ flex: 1 }}>
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Cari CM-ID, No Body, No Polisi..."
                    value={cmSearchText}
                    onChange={e => setCmSearchText(e.target.value)}
                  />
                  {cmSearchText && (
                    <button className="btn-icon" onClick={() => setCmSearchText('')} style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--gray-400)' }}>
                      <X size={14} />
                    </button>
                  )}
                </div>
                <select
                  value={cmFilterBy}
                  onChange={e => setCmFilterBy(e.target.value)}
                  style={{ padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.82rem', background: '#fff' }}
                >
                  <option value="all">Semua Field</option>
                  <option value="cm_id">CM-ID</option>
                  <option value="no_body">No Body / No Polisi</option>
                  <option value="teknisi">Teknisi</option>
                </select>
              </div>

              {/* Date range filter */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>Tanggal:</span>
                <input
                  type="date"
                  value={cmTglMulai}
                  onChange={e => setCmTglMulai(e.target.value)}
                  style={{ padding: '6px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.78rem', background: '#fff' }}
                />
                <span style={{ color: 'var(--gray-400)', fontSize: '0.78rem' }}>—</span>
                <input
                  type="date"
                  value={cmTglSelesai}
                  onChange={e => setCmTglSelesai(e.target.value)}
                  style={{ padding: '6px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.78rem', background: '#fff' }}
                />
                {(cmTglMulai || cmTglSelesai) && (
                  <button
                    className="btn-icon"
                    onClick={() => { setCmTglMulai(''); setCmTglSelesai(''); }}
                    style={{ color: '#f5365c', border: 'none', background: 'none', cursor: 'pointer', padding: 4 }}
                    title="Reset tanggal"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* CM List */}
              {filteredCM.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--gray-400)' }}>
                  <FileText size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                  <p style={{ fontSize: '0.9rem', fontWeight: 500 }}>Tidak ada CM siap tagih</p>
                  <p style={{ fontSize: '0.78rem' }}>Semua CM dengan status "Selesai Pengerjaan" sudah memiliki tagihan</p>
                </div>
              ) : (
                <div style={{ maxHeight: 340, overflow: 'auto', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
                  <table className="data-table" style={{ margin: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}></th>
                        <th>CM-ID</th>
                        <th>No Polisi</th>
                        <th>No Body</th>
                        <th>Teknisi</th>
                        <th>Pekerjaan</th>
                        <th>Nominal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCM.map(c => (
                        <tr
                          key={c.id}
                          onClick={() => setSelectedCM(String(c.id))}
                          style={{
                            cursor: 'pointer',
                            background: selectedCM === String(c.id) ? '#eef2ff' : '#fff',
                            fontWeight: selectedCM === String(c.id) ? 600 : 'normal',
                            transition: 'background 0.1s',
                          }}
                          onMouseEnter={e => { if (selectedCM !== String(c.id)) e.currentTarget.style.background = '#f9fafb'; }}
                          onMouseLeave={e => { if (selectedCM !== String(c.id)) e.currentTarget.style.background = '#fff'; }}
                        >
                          <td style={{ textAlign: 'center' }}>
                            <div style={{
                              width: 18, height: 18, borderRadius: '50%',
                              border: selectedCM === String(c.id) ? '5px solid var(--primary)' : '2px solid var(--gray-300)',
                              transition: 'all 0.15s',
                            }} />
                          </td>
                          <td><strong style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{c.cm_id}</strong></td>
                          <td><span className="region-tag">{c.no_polisi || '-'}</span></td>
                          <td style={{ fontSize: '0.82rem' }}>{c.no_body || '-'}</td>
                          <td style={{ fontSize: '0.8rem' }}>{c.nama_teknisi || c.teknisi_handle || '-'}</td>
                          <td style={{ fontSize: '0.8rem' }}>{c.detail_pekerjaan || c.jenis_pekerjaan || 'Perbaikan'}</td>
                          <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            Rp {Number(c.nominal_tagihan).toLocaleString('id-ID')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedCM && (
                <div style={{ marginTop: 12, padding: '8px 14px', background: '#f0fdf4', borderRadius: 8, color: '#065f46', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle size={16} />
                  <span>CM terpilih: <strong>{filteredCM.find(c => String(c.id) === selectedCM)?.cm_id}</strong></span>
                </div>
              )}

              {generateInfo && (
                <div style={{ marginTop: 12, padding: 12, background: '#d1fae5', borderRadius: 'var(--radius)', color: '#065f46', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle size={18} />
                  <span>✅ Tagihan <strong>{generateInfo.invoice_no}</strong> berhasil dibuat!</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowGenerateModal(false); setCmSearchText(''); setSelectedCM(''); setCmTglMulai(''); setCmTglSelesai(''); setError(''); }}>Tutup</button>
              <button className="btn btn-primary" onClick={handleGenerate} disabled={saving || !selectedCM}>
                {saving ? 'Memproses...' : 'Generate Tagihan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setIsPM(false); }}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #5e72e4, #825ee4)', color: '#fff', borderRadius: '12px 12px 0 0', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ color: '#fff', margin: 0, fontSize: '1rem', fontWeight: 600 }}>{editing ? 'Edit Tagihan' : (isPM ? 'Buat Pengajuan PM' : 'Buat Pengajuan Tagihan')}</h3>
              <button className="btn-icon" onClick={() => { setShowModal(false); setIsPM(false); }} style={{ color: '#fff', border: 'none', background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}

              {/* === INFORMASI KENDARAAN — 4 BARIS KOMPAK === */}
              <div style={{ marginBottom: 12, background: '#f8fafc', borderRadius: 10, padding: '10px 12px 8px', border: '1px solid #eef2ff' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#5e72e4', textTransform: 'uppercase', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 3, height: 14, background: '#5e72e4', borderRadius: 2 }} /> INFORMASI KENDARAAN
                </div>

                {/* BARIS 1: No Invoice | Tanggal | Tgl Jatuh Tempo */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.55fr 0.55fr', gap: '4px', marginBottom: 2 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>No Invoice</label>
                    <input type="text" value={form.invoice_no}
                      onChange={e => setForm({...form, invoice_no: e.target.value})}
                      placeholder="Auto"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem', fontFamily: 'monospace', fontWeight: 600 }} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>Tanggal</label>
                    <input type="date" value={form.tanggal_pengerjaan}
                      onChange={e => setForm({...form, tanggal_pengerjaan: e.target.value})}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem' }} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>Tgl Jatuh Tempo</label>
                    <input type="date" value={form.tanggal_jatuh_tempo}
                      onChange={e => setForm({...form, tanggal_jatuh_tempo: e.target.value})}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem' }} />
                  </div>
                </div>

                {/* BARIS 2: Kendaraan | No Body | No Polisi */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.6fr 0.6fr', gap: '4px', marginBottom: 2 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>Kendaraan</label>
                    {isPM ? (
                      <input type="text" value={form.no_polisi}
                        onChange={e => setForm({...form, no_polisi: e.target.value})}
                        placeholder="Masukkan No Polisi"
                        style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem' }} />
                    ) : (
                      <div ref={vehicleSearchRef} style={{ position: 'relative' }}>
                        <div style={{ position: 'relative' }}>
                          <input type="text"
                            value={showVehicleDropdown ? vehicleSearchQuery : (form.no_polisi ? `${form.no_polisi} — ${form.no_body || ''}` : '')}
                            onChange={handleVehicleSearchChange}
                            onFocus={() => { setVehicleSearchQuery(''); setShowVehicleDropdown(true); }}
                            placeholder="Cari kendaraan..."
                            style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem' }}
                          />
                          {form.vehicle_id && !showVehicleDropdown && (
                            <span style={{ position: 'absolute', right: 6, top: 6, fontSize: '0.6rem', color: '#2dce89', fontWeight: 600, pointerEvents: 'none' }}>✓</span>
                          )}
                          {showVehicleDropdown && (
                            <div style={{
                              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                              maxHeight: 180, overflowY: 'auto',
                              background: '#fff', border: '1px solid #d1d5db', borderTop: 'none',
                              borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                            }}>
                              {filteredVehicles.length === 0 ? (
                                <div style={{ padding: '6px 10px', color: '#9ca3af', fontSize: '0.76rem', fontStyle: 'italic' }}>
                                  {vehicleMasterList.length === 0 ? 'Memuat data...' : 'Tidak ditemukan'}
                                </div>
                              ) : filteredVehicles.map(v => (
                                <div key={v.id}
                                  onClick={() => selectVehicleFromMaster(v)}
                                  style={{
                                    padding: '6px 10px', cursor: 'pointer', fontSize: '0.78rem',
                                    borderBottom: '1px solid #f0f0f0',
                                    background: Number(form.vehicle_id) === v.id ? '#fff5ee' : '#fff',
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                                  onMouseLeave={e => e.currentTarget.style.background = Number(form.vehicle_id) === v.id ? '#fff5ee' : '#fff'}
                                >
                                  <strong>{v.body_no || '-'}</strong>
                                  <span style={{ color: '#666', marginLeft: 4 }}>{v.plate_no || '-'}</span>
                                  <span style={{ color: '#999', marginLeft: 4, fontSize: '0.7rem' }}>{v.operator || v.owner_name || ''}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {form.vehicle_id && (
                          <button
                            type="button"
                            onClick={() => { setForm(f => ({ ...f, vehicle_id: '', no_body: '', no_polisi: '', nama_klien: '', no_kontrak: '', lokasi: '', ketua_koperasi: '' })); setVehicleSearchQuery(''); }}
                            style={{ fontSize: '0.65rem', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', marginTop: 1, padding: 0 }}
                          >
                            ✕ Hapus
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>No Body</label>
                    <input type="text" value={form.no_body} onChange={e => setForm({...form, no_body: e.target.value})} placeholder="Auto"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem', background: form.vehicle_id ? '#f0fdf4' : '#fff' }} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>No Polisi</label>
                    <input type="text" value={form.no_polisi} onChange={e => setForm({...form, no_polisi: e.target.value})} placeholder="Auto"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem', background: form.vehicle_id ? '#f0fdf4' : '#fff' }} />
                  </div>
                </div>

                {/* BARIS 3: Nama Klien | Rute | Kontrak | Jenis AC/Non AC */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 0.8fr 0.9fr', gap: '4px', marginBottom: 2 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>Nama Klien</label>
                    <input type="text" value={form.nama_klien} onChange={e => setForm({...form, nama_klien: e.target.value})} placeholder="Nama klien"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem', background: form.vehicle_id ? '#f0fdf4' : '#fff' }} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>Rute</label>
                    <input type="text" value={form.lokasi} onChange={e => setForm({...form, lokasi: e.target.value})} placeholder="Auto"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem', background: form.vehicle_id ? '#f0fdf4' : '#fff' }} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>Kontrak</label>
                    <input type="text" value={form.no_kontrak} onChange={e => setForm({...form, no_kontrak: e.target.value})} placeholder="Auto"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem', background: form.vehicle_id ? '#f0fdf4' : '#fff' }} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>Jenis AC/Non AC</label>
                    <select value={form.jenis_kendaraan} onChange={e => setForm({...form, jenis_kendaraan: e.target.value})}
                      style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem', background: '#fff' }}>
                      <option value="">--</option>
                      <option value="AC">AC</option>
                      <option value="Non AC">Non AC</option>
                    </select>
                  </div>
                </div>

                {/* BARIS 3b: Ketua Koperasi */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4px', marginBottom: 2, marginTop: 4 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>Ketua Koperasi</label>
                    <input type="text" value={form.ketua_koperasi || ''} onChange={e => setForm({...form, ketua_koperasi: e.target.value})} placeholder="Ketua koperasi (muncul di cetak invoice)"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem', background: form.vehicle_id ? '#f0fdf4' : '#fff' }} />
                  </div>
                </div>

                {/* BARIS 4: Jenis Pekerjaan — full width */}
                <div style={{ display: 'grid', gap: '4px' }}>
                  {form.cm_id ? (
                    <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                      <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>Detail / Uraian Pekerjaan</label>
                      <textarea value={form.detail_pekerjaan} onChange={e => setForm({...form, detail_pekerjaan: e.target.value, jenis_pekerjaan: e.target.value})}
                        placeholder="Uraikan detail pekerjaan..." rows={3}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem', resize: 'vertical', fontFamily: 'inherit' }} />
                    </div>
                  ) : (
                    <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                      <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>Jenis Pekerjaan</label>
                      <input value={form.jenis_pekerjaan} onChange={e => setForm({...form, jenis_pekerjaan: e.target.value})}
                        placeholder="Jenis pekerjaan..."
                        style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem' }} />
                    </div>
                  )}
                  <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.7rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>Catatan</label>
                    <textarea value={form.catatan} onChange={e => setForm({...form, catatan: e.target.value})}
                      placeholder="Catatan pekerjaan..."
                      rows={2}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: '0.82rem', resize: 'vertical', fontFamily: 'inherit' }} />
                  </div>
                </div>
              </div>

              {/* === SPAREPART DAN JASA === */}
              <div style={{ marginBottom: 12, background: '#fff', borderRadius: 10, padding: '10px 12px 8px', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 3, height: 14, background: '#374151', borderRadius: 2 }} /> SPAREPART DAN JASA
                </div>

                {/* UNIFIED TABLE */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead>
                      <tr style={{ background: '#f3f4f6' }}>
                        <th style={{ padding: '4px 6px', textAlign: 'left', borderBottom: '2px solid #374151', color: '#374151', fontWeight: 600, fontSize: '0.7rem' }}>Nama Item</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', borderBottom: '2px solid #374151', color: '#374151', fontWeight: 600, fontSize: '0.7rem', width: 50 }}>Qty</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '2px solid #374151', color: '#374151', fontWeight: 600, fontSize: '0.7rem', width: 80 }}>Harga</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', borderBottom: '2px solid #374151', width: 28 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr key={`item-${idx}`}>
                          <td style={{ padding: '2px 4px' }}>
                            {isPM ? (
                              <input type="text" value={item.nama} onChange={e => {
                                const newItems = [...items];
                                newItems[idx].nama = e.target.value;
                                setItems(newItems);
                              }} placeholder="Nama item"
                                style={{ width: '100%', padding: '4px 6px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: '0.78rem' }} />
                            ) : (
                              <div ref={itemSearchRef} style={{ position: 'relative' }}>
                                <div
                                  onClick={(e) => {
                                    const r = e.currentTarget.getBoundingClientRect();
                                    const spaceBelow = window.innerHeight - r.bottom - 10;
                                    const dropH = Math.min(220, Math.max(150, spaceBelow));
                                    const topPos = spaceBelow > 150 ? r.bottom + 2 : r.top - dropH;
                                    setItemDropdownPos({ top: topPos, left: r.left, width: r.width, maxH: dropH });
                                    setSelectedItemIdx(idx);
                                    setShowItemDropdown(!showItemDropdown || selectedItemIdx !== idx);
                                    setItemSearchQuery('');
                                  }}
                                  style={{
                                    display: 'flex', alignItems: 'center',
                                    border: '1px solid #e5e7eb', borderRadius: 4,
                                    padding: '4px 6px', fontSize: '0.78rem',
                                    cursor: 'pointer', background: '#fff',
                                    minHeight: 26, boxSizing: 'border-box', width: '100%'
                                  }}>
                                  <span style={{ flex: 1, color: item.nama ? '#1f2937' : '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
                                    {item.nama
                                      ? `${item.tipe === 'jasa' ? '🔧' : '🛠'} ${item.nama}`
                                      : '-- Pilih item --'}
                                  </span>
                                  {item.nama && (
                                    <span onClick={e => {
                                      e.stopPropagation();
                                      const newItems = [...items];
                                      newItems[idx].nama = '';
                                      newItems[idx].harga = 0;
                                      setItems(newItems);
                                    }} style={{ display: 'flex', alignItems: 'center', color: '#9ca3af', cursor: 'pointer', padding: '1px', borderRadius: 3, marginLeft: 2 }}>
                                      <X size={11} />
                                    </span>
                                  )}
                                  <span style={{ fontSize: '0.55rem', color: '#9ca3af', marginLeft: 3 }}>▼</span>
                                </div>
                                {showItemDropdown && selectedItemIdx === idx && (
                                  <div style={{
                                    position: 'fixed', top: itemDropdownPos.top, left: itemDropdownPos.left, width: itemDropdownPos.width, zIndex: 9999,
                                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                                    maxHeight: itemDropdownPos.maxH || 220, overflowY: 'auto',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                  }}>
                                    <div style={{ padding: '5px', borderBottom: '1px solid #f3f4f6', position: 'sticky', top: 0, background: '#fff' }}>
                                      <input type="text" value={itemSearchQuery} onChange={handleItemSearchChange}
                                        onClick={e => e.stopPropagation()} placeholder="Cari sparepart / jasa..." autoFocus
                                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: '0.76rem', boxSizing: 'border-box', outline: 'none' }} />
                                    </div>
                                    {/* Sparepart section */}
                                    {combinedMasterList.filter(x => x.tipe === 'sparepart').filter(x => {
                                      if (!itemSearchQuery) return true;
                                      const q = itemSearchQuery.toLowerCase();
                                      return (x.nama || '').toLowerCase().includes(q);
                                    }).length > 0 && (
                                      <div style={{ padding: '3px 8px', fontSize: '0.65rem', fontWeight: 600, color: '#6b7280', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>⚙ SPAREPART</div>
                                    )}
                                    {combinedMasterList.filter(x => x.tipe === 'sparepart').filter(x => {
                                      if (!itemSearchQuery) return true;
                                      const q = itemSearchQuery.toLowerCase();
                                      return (x.nama || '').toLowerCase().includes(q);
                                    }).map(x => (
                                      <div key={`sp-${x.id}`}
                                        onClick={() => {
                                          const newItems = [...items];
                                          newItems[idx].kode = x.kode || '';
                                          newItems[idx].nama = x.nama || '';
                                          newItems[idx].harga = Number(x.harga || 0);
                                          newItems[idx].unit = x.unit || '';
                                          newItems[idx].tipe = 'sparepart';
                                          setItems(newItems);
                                          setItemSearchQuery('');
                                          setShowItemDropdown(false);
                                        }}
                                        style={{ padding: '5px 10px', cursor: 'pointer', fontSize: '0.76rem', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', background: x.nama === item.nama ? '#f0fdf4' : 'transparent' }}
                                        onMouseEnter={e => { if (x.nama !== item.nama) e.target.style.background = '#f8fafc'; }}
                                        onMouseLeave={e => e.target.style.background = (x.nama === item.nama ? '#f0fdf4' : 'transparent')}
                                      >
                                        <span>🛠 {x.nama}</span>
                                        <span style={{ color: '#6b7280', fontSize: '0.7rem' }}>{x.kode ? `#${x.kode}` : ''}</span>
                                      </div>
                                    ))}
                                    {/* Jasa section */}
                                    {combinedMasterList.filter(x => x.tipe === 'jasa').filter(x => {
                                      if (!itemSearchQuery) return true;
                                      const q = itemSearchQuery.toLowerCase();
                                      return (x.nama || '').toLowerCase().includes(q);
                                    }).length > 0 && (
                                      <div style={{ padding: '3px 8px', fontSize: '0.65rem', fontWeight: 600, color: '#6b7280', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>🔧 JASA LAYANAN</div>
                                    )}
                                    {combinedMasterList.filter(x => x.tipe === 'jasa').filter(x => {
                                      if (!itemSearchQuery) return true;
                                      const q = itemSearchQuery.toLowerCase();
                                      return (x.nama || '').toLowerCase().includes(q);
                                    }).map(x => (
                                      <div key={`js-${x.id}`}
                                        onClick={() => {
                                          const newItems = [...items];
                                          newItems[idx].kode = x.kode || '';
                                          newItems[idx].nama = x.nama || '';
                                          newItems[idx].harga = Number(x.harga || 0);
                                          newItems[idx].unit = x.unit || '';
                                          newItems[idx].tipe = 'jasa';
                                          setItems(newItems);
                                          setItemSearchQuery('');
                                          setShowItemDropdown(false);
                                        }}
                                        style={{ padding: '5px 10px', cursor: 'pointer', fontSize: '0.76rem', borderBottom: '1px solid #f3f4f6', background: x.nama === item.nama ? '#f0fdf4' : 'transparent' }}
                                        onMouseEnter={e => { if (x.nama !== item.nama) e.target.style.background = '#f8fafc'; }}
                                        onMouseLeave={e => e.target.style.background = (x.nama === item.nama ? '#f0fdf4' : 'transparent')}
                                      >
                                        <span>🔧 {x.kode ? x.kode + ' - ' : ''}{x.nama}</span>
                                      </div>
                                    ))}
                                    {combinedMasterList.filter(x => {
                                      if (!itemSearchQuery) return true;
                                      const q = itemSearchQuery.toLowerCase();
                                      return (x.nama || '').toLowerCase().includes(q);
                                    }).length === 0 && (
                                      <div style={{ padding: '6px 10px', color: '#9ca3af', fontSize: '0.76rem' }}>Tidak ditemukan</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '2px 4px' }}>
                            <input type="number" min="1" value={item.jumlah} onChange={e => {
                              const newItems = [...items];
                              newItems[idx].jumlah = Number(e.target.value) || 1;
                              setItems(newItems);
                            }} style={{ width: '100%', padding: '4px 6px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: '0.78rem', textAlign: 'center' }} />
                          </td>
                          <td style={{ padding: '2px 4px' }}>
                            <input type="number" min="0" value={item.harga} onChange={e => {
                              const newItems = [...items];
                              newItems[idx].harga = Number(e.target.value) || 0;
                              setItems(newItems);
                            }} placeholder="0"
                              style={{ width: '100%', padding: '4px 6px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: '0.78rem', textAlign: 'right' }} />
                          </td>
                          <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                            <button className="btn-icon" onClick={() => {
                              if (items.length > 1) {
                                setItems(items.filter((_, i) => i !== idx));
                              }
                            }} style={{ color: items.length > 1 ? '#dc2626' : '#d1d5db', cursor: items.length > 1 ? 'pointer' : 'not-allowed', padding: 1 }}>
                              <X size={12} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* TAMBAH ITEM BUTTON */}
                <div style={{ marginTop: 4 }}>
                  <button className="btn btn-secondary" style={{ padding: '3px 8px', fontSize: '0.72rem' }}
                    onClick={() => setItems([...items, { nama: '', jumlah: 1, harga: 0, tipe: 'sparepart' }])}>
                    <Plus size={11} /> Tambah Item
                  </button>
                </div>

                {/* RINGKASAN — di dalam container yang sama */}
                <div style={{ marginTop: 8, borderTop: '1px dashed #d1d5db', paddingTop: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '3px 6px', textAlign: 'right', color: '#6b7280', fontWeight: 500, borderBottom: '1px solid #f3f4f6', width: '60%' }}>Total Sparepart</td>
                        <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #f3f4f6', width: '40%' }}>Rp {totalSparepart.toLocaleString('id-ID')}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '3px 6px', textAlign: 'right', color: '#6b7280', fontWeight: 500, borderBottom: '1px solid #f3f4f6' }}>Total Jasa</td>
                        <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #f3f4f6' }}>Rp {totalJasa.toLocaleString('id-ID')}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '3px 6px', textAlign: 'right', color: '#6b7280', fontWeight: 500, borderBottom: '1px solid #f3f4f6' }}>Biaya Lain-lain</td>
                        <td style={{ padding: '3px 6px', textAlign: 'right', borderBottom: '1px solid #f3f4f6' }}>
                          <input type="number" min="0" value={form.biaya_lain || 0} onChange={e => setForm({...form, biaya_lain: Number(e.target.value) || 0})}
                            placeholder="0"
                            style={{ width: '100%', maxWidth: 140, padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.78rem', textAlign: 'right' }} />
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '3px 6px', textAlign: 'right', color: '#6b7280', fontWeight: 500, borderBottom: '1px solid #f3f4f6' }}>PPN</td>
                        <td style={{ padding: '3px 6px', textAlign: 'right', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                          <input type="number" min="0" max="100" value={form.ppn || 0} onChange={e => setForm({...form, ppn: Number(e.target.value) || 0})}
                            placeholder="0"
                            style={{ width: 50, padding: '3px 4px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.78rem', textAlign: 'right' }} />
                          <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>%</span>
                          <span style={{ color: '#374151', fontWeight: 600, fontSize: '0.78rem' }}>
                            = Rp {ppnAmount.toLocaleString('id-ID')}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 6px', textAlign: 'right', color: '#065f46', fontWeight: 700, fontSize: '0.85rem', borderTop: '2px solid #065f46' }}>Grand Total</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 800, fontSize: '0.85rem', color: '#065f46', borderTop: '2px solid #065f46' }}>
                          Rp {nominalTagihan.toLocaleString('id-ID')}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* === TTD DIGITAL === */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, marginBottom: 2, padding: '8px 0' }}>
                <label style={{ fontWeight: 600, fontSize: '0.72rem', color: '#4b5563', marginBottom: 2, display: 'block' }}>
                  TTD Digital {form.signature_url && <span style={{ color: '#2dce89', fontSize: '0.65rem' }}>✓</span>}
                </label>
                <div style={{
                  border: '2px dashed #d1d5db',
                  borderRadius: 6, padding: 6,
                  background: form.signature_url ? '#f0fdf4' : '#fafafa',
                  textAlign: 'center',
                  minHeight: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  maxWidth: 260,
                }}>
                  {form.signature_url ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img src={form.signature_url} alt="TTD" style={{ maxWidth: 200, maxHeight: 40, borderRadius: 4 }} />
                      <button type="button" onClick={() => setForm({...form, signature_url: ''})}
                        style={{
                          position: 'absolute', top: -6, right: -6,
                          width: 16, height: 16, borderRadius: '50%',
                          background: '#f5365c', color: '#fff', border: 'none',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }}>
                        <X size={7} />
                      </button>
                    </div>
                  ) : (
                    <label style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                      background: '#1a1a2e', color: '#fff', fontSize: '0.72rem',
                    }}>
                      <Upload size={11} /> Upload
                      <input type="file" hidden accept="image/*"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setForm({...form, signature_url: ev.target?.result || ''});
                          };
                          reader.readAsDataURL(file);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#6b7280', fontStyle: 'italic' }}>
                  ( {user?.nama || 'nama user'} )
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowModal(false); setIsPM(false); }}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan Pengajuan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
